import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate as useRouterNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { api, NODE_TYPE_COLORS, NODE_TYPES, MOD_KEY } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useBg } from "../hooks/useBg";
import { useSettings } from "../context/SettingsContext";
import { useClasses } from "../context/ClassesContext";
import { useFolders } from "../context/FoldersContext";
import { useTemplates } from "../context/TemplatesContext";
import { useShorthandExpansion } from "../hooks/useShorthandExpansion";
import { confirmDelete } from "./ConfirmDialog";
import TimeBlockingPanel from './TimeBlockingPanel';
import { WorkspaceSettingsButton, applyWorkspaceBgVars } from "./WorkspaceSettings";
import { ThemedSelect } from "./ThemedSelect";
import CalendarImportModal from "./CalendarImportModal";
import { useDocuments } from "../context/DocumentsContext";
import { useEntitlements } from "../context/EntitlementsContext";
import { useTags } from "../context/TagsContext";
import { useKeyPairs } from "../context/KeyPairsContext";
import { useGoals } from "../context/GoalsContext";
import {
  format, startOfWeek, addDays, startOfMonth, endOfMonth, startOfDay,
  addHours, addMinutes, differenceInMinutes, isSameDay, parseISO,
  addWeeks, subWeeks, addMonths, subMonths,
} from "date-fns";
import {
  ChevronLeft, ChevronRight, StickyNote, X, X as XIcon,
  CheckSquare, Repeat, Zap, Bell, PlayCircle,
  Copy, Scissors, Clipboard, Copy as CopyIcon, Trash2, FileEdit, ChevronDown,
  Plus, Undo2, Redo2, Pin, PinOff, Focus, History, FilePlus, Cloud, RefreshCw,
  Lock, Unlock, FileText, Check, Palette, ArrowRight, Circle, Link as LinkIcon,
  Globe, Share2, FolderPlus, Folder as FolderIcon, ChevronUp, Sparkles,
  Download, AlertTriangle, Info, Tag,
} from "lucide-react";
import ConditionIcon from "./ConditionIcon";
import SequenceIcon from "./SequenceIcon";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "./ui/dropdown-menu";
import { toast } from "sonner";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_HEIGHT = 56;
const TYPE_ICONS = { task: CheckSquare, routine: Repeat, action: Zap, reminder: Bell, trigger: PlayCircle, condition: ConditionIcon, sequence: SequenceIcon, note: StickyNote, resource: FileText };
const PRESET_COLORS = ["#0055FF", "#FF3B30", "#FFCC00", "#00C781", "#FF8A00", "#A855F7", "#14B8A6"];
const minutesToY = (m) => (m / 60) * HOUR_HEIGHT;
// Unified timeline model — every node type is a timed block filling the full
// column width. (The old floating-layer for trigger/action/condition/note is
// gone per user request.) Zero-duration types render as slim ribbons.

// Lazy-loaded Documents panel for split view
const SplitDocumentsView = React.lazy
  ? React.lazy(() => import("./DocumentsView"))
  : null;

const ZERO_DURATION_TYPES = new Set(["trigger", "action", "condition", "note", "reminder", "resource"]);
const isFloatingType = (t) => ZERO_DURATION_TYPES.has(t);
const isZeroDurationType = (t) => ZERO_DURATION_TYPES.has(t);

// Type-icon map shared by the EventDialog initialization picker and a few
// other places (mirrors the sidebar). Keep here so we don't re-import.
const TYPE_ICONS_LOCAL = {
  task: CheckSquare,
  routine: Repeat,
  action: Zap,
  reminder: Bell,
  trigger: PlayCircle,
  condition: ConditionIcon,
  sequence: SequenceIcon,
  note: StickyNote,
  resource: FileText,
};

// "Just Now" / "Nm ago" / "h:mm PM" relative save-time formatter.
// Updates on minute marks (see minute-tick effect in CalendarView) so the
// display changes predictably instead of jumping each second.
function formatRelativeSave(d) {
  if (!d) return "";
  // Accept either a Date or an ISO string — savedAt is initialized as an
  // ISO string for SSR-safety but updated to a Date elsewhere.
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const diff = Math.floor((Date.now() - dt.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m} minute${m === 1 ? "" : "s"} ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  return format(dt, "p");
}

// Timezone display helpers — used so the user can pick a "view" timezone
// from the calendar header and have node times shift visually (incl.
// pushing across midnight boundaries). Backend storage stays UTC; only
// the visual layout / labels are adjusted.
const LOCAL_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; }
})();
function tzOffsetMsAt(date, tz) {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
    const asUTC = Date.UTC(
      parseInt(parts.year), parseInt(parts.month) - 1, parseInt(parts.day),
      parseInt(parts.hour) % 24, parseInt(parts.minute), parseInt(parts.second),
    );
    return asUTC - date.getTime();
  } catch { return 0; }
}
function shiftIsoToTZ(iso, tz) {
  if (!iso || !tz || tz === LOCAL_TZ) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const targetOffset = tzOffsetMsAt(d, tz);
  const localOffset = -d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() + targetOffset - localOffset).toISOString();
}

// Urgency: return a color interpolated from the node's base color toward RED
// as end approaches. Honours the new before-start / before-end toggles in
// addition to the legacy single sensitivity field (user request).
function urgencyColor(baseColor, ev) {
  if (!ev) return baseColor;
  const now = Date.now();
  const startMs = new Date(ev.start).getTime();
  const endMs = new Date(ev.end).getTime();
  // Before-end (legacy + new toggle).
  let endSens = ev.urgency_before_end_enabled ? (ev.urgency_before_end_minutes || ev.urgency_sensitivity_minutes || null) : (ev.urgency_sensitivity_minutes || null);
  if (endSens && endMs) {
    if (endMs < now) return "#FF3B30";
    const diffMin = (endMs - now) / 60000;
    if (diffMin <= endSens) {
      const t = 1 - diffMin / endSens;
      return mixHex(baseColor, "#FF3B30", t);
    }
  }
  // Before-start (new toggle only).
  if (ev.urgency_before_start_enabled && ev.urgency_before_start_minutes && startMs) {
    const diffMin = (startMs - now) / 60000;
    if (diffMin > 0 && diffMin <= ev.urgency_before_start_minutes) {
      const t = 1 - diffMin / ev.urgency_before_start_minutes;
      return mixHex(baseColor, "#FF3B30", t * 0.85); // slightly less aggressive than overdue
    }
  }
  return baseColor;
}
function mixHex(a, b, t) {
  const parse = (h) => { const s = h.replace("#",""); return [0,2,4].map((i) => parseInt(s.slice(i, i + 2), 16)); };
  const [ar, ag, ab] = parse(a); const [br, bg, bb] = parse(b);
  const to = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${to(ar + (br - ar) * t)}${to(ag + (bg - ag) * t)}${to(ab + (ab * 0) + (bb - ab) * t)}`;
}

function layoutEvents(events) {
  const sorted = [...events].sort((a, b) => new Date(a.start) - new Date(b.start));
  const groups = [];
  // Grouping uses VISUAL bounds (>=25 min floor) — this matches the
  // minimum rendered block height (see EventBlock line: durationMin =
  // Math.max(25, …)). User request: "when nodes overlap vertically they
  // need to collapse" even if their time-ranges don't actually overlap.
  const MIN_VIS_MIN = 25;
  const visBounds = (ev) => {
    const s = new Date(ev.start).getTime();
    const e = new Date(ev.end).getTime();
    return { s, e: Math.max(e, s + MIN_VIS_MIN * 60000) };
  };
  sorted.forEach((ev) => {
    const { s, e } = visBounds(ev);
    let placed = false;
    for (const g of groups) {
      if (g.end > s) { g.items.push(ev); g.end = Math.max(g.end, e); placed = true; break; }
    }
    if (!placed) groups.push({ items: [ev], end: e });
  });
  const layout = new Map();
  groups.forEach((g, gIdx) => {
    const cols = [];
    g.items.forEach((ev) => {
      const { s, e } = visBounds(ev);
      let col = 0;
      while (cols[col] && cols[col] > s) col++;
      cols[col] = e;
      layout.set(ev.event_id, {
        col,
        total: 0,
        groupId: gIdx,
        start: s,
        end: e,
      });
    });
    g.items.forEach((ev) => { layout.get(ev.event_id).total = cols.length; });
  });
  // ---- Column-resize: if any event renders taller than its time slot
  // (sequence nodes carry their children inline so the rendered height can
  // exceed the time-allocated height), push subsequent events down so
  // they don't overlap (user request: "when a node is expanded vertically
  // it should move down the rest of the column underneath it"). ----
  // We approximate the rendered height as time-height + (sequence-children
  // * 32px). The number is intentionally conservative — we'd rather have a
  // little gap than overlap.
  const PX_PER_MIN = HOUR_HEIGHT / 60;
  const childPx = (ev) => {
    if (ev.type !== "sequence") return 0;
    const kids = (ev.children_event_ids || []).length;
    // Each child: ~28px row. Drop zone: ~32px. Padding: ~24px.
    return kids * 28 + 32 + 24;
  };
  // Resolve overflow per event_id and accumulate downward shifts within
  // each visual column.
  sorted.forEach((ev) => {
    const meta = layout.get(ev.event_id);
    if (!meta) return;
    const startMin = (new Date(ev.start).getTime() - new Date(ev.start).setHours(0, 0, 0, 0)) / 60000;
    const endMin = (new Date(ev.end).getTime() - new Date(ev.start).setHours(0, 0, 0, 0)) / 60000;
    const allocPx = Math.max(25 * PX_PER_MIN, (endMin - startMin) * PX_PER_MIN);
    const renderedPx = (endMin - startMin) * PX_PER_MIN + childPx(ev);
    const overflowPx = Math.max(0, renderedPx - allocPx);
    meta.overflowPx = overflowPx;
  });
  // Apply cumulative shifts: for every event A in column C, look at all
  // events B in the same column whose start is before A's start; sum their
  // overflowPx and store as A.shiftPx.
  layout.forEach((meta, evId) => {
    let shift = 0;
    layout.forEach((other, otherId) => {
      if (otherId === evId) return;
      if (other.col !== meta.col) return;
      if (other.groupId !== meta.groupId) return;
      // 'before' = ends before this one starts in time.
      if (other.end <= meta.start) shift += other.overflowPx || 0;
    });
    meta.shiftPx = shift;
  });
  return layout;
}

function EventBlock({ event, layout, onOpen, onStartConnect, onAddNote, onDeleteNote, onAddToSequence, onRemoveFromSequence, onCreateChildInSequence, onReorderSequenceChild, onToggleComplete, notes, setRef, timeFormat, onReschedule, onResize, selected, onSelect, classColor, classBg, isDimmed, onContextMenu, snapToGrid, snapMinutes = 15, hasOutgoingEdge, resolveChild, expandedId, setExpandedId, isNodeCollapsed, toggleNodeCollapse, isUnused, onHoverChange }) {
  const start = new Date(event.start);
  const end = new Date(event.end);
  const startMin = differenceInMinutes(start, startOfDay(start));
  const rawDurationMin = Math.max(25, differenceInMinutes(end, start));
  const shiftPx = (layout.get(event.event_id) || {}).shiftPx || 0;
  const top = minutesToY(startMin) + shiftPx;
  const height = minutesToY(rawDurationMin);
  const { col = 0, total = 1, groupId } = layout.get(event.event_id) || {};
  // Expand / collapse model. `expandedId` lives on the Calendar; when this
  // block is the expanded one it takes the FULL column width and all others
  // in the same overlap cluster collapse to a thin ribbon (user req).
  const isExpanded = expandedId === event.event_id;
  const expandedMeta = expandedId ? layout.get(expandedId) : null;
  const someoneElseExpanded = !!(expandedMeta && expandedId !== event.event_id && expandedMeta.groupId === groupId);
  const isCollapsed = someoneElseExpanded;

  const colFrac = isExpanded ? 1 : (isCollapsed ? 0.1 : 1 / total);
  const colLeftPct = isExpanded ? 0 : (isCollapsed ? 0 : col * 100 * (1 / total));
  const colWidthPct = 100 * colFrac;
  const isCollapsedOverlap = isCollapsed;
  // Density-based collapse: compact → collapsed by default, expanded → open
  const isDensityCollapsed = isNodeCollapsed ? isNodeCollapsed(event.event_id) : false;
  const onToggleDensityCollapse = (e) => { e.stopPropagation(); toggleNodeCollapse?.(event.event_id); };
  // Top-bar color: custom_color overrides only if no class; else class color; else type color
  const hasClass = !!event.class_name;
  // Brand-blue fallback (user request: stop color-coding by type; defer to
  // the user's class / custom_color choice and fall back to brand blue).
  // Per-type default colors (user request: restore type defaults on blocks,
  // keep dropdown menus brand-blue). User's chosen class/custom color still
  // wins over the type default when set.
  const TYPE_DEFAULTS = {
    task: "#EF4444",        // richer red
    routine: "#FFCC00",
    action: "#0055FF",
    reminder: "#00C781",
    trigger: "#FF8A00",
    condition: "#A855F7",
    sequence: "#14B8A6",
    note: "#A1A1AA",
    resource: "#6366F1",
  };
  const typeDefault = "#5A90F5"; // brand blue — only class color overrides
  const baseColor = hasClass ? (classColor || "#5A90F5") : "#5A90F5";
  // Apply urgency shift if sensitivity set
  const color = event.urgency_sensitivity_minutes
    ? urgencyColor(baseColor, event)
    : baseColor;
  const Icon = TYPE_ICONS[event.type] || CheckSquare;
  const [notesOpen, setNotesOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const actionRef = useRef(null);
  const lastTapRef = useRef(0);

  const addNote = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!newNote.trim()) return;
    await onAddNote(event.event_id, newNote.trim());
    setNewNote("");
    setNotesOpen(false);
  };

  // Live drag offset — the block visually slides with the cursor while the
  // user drags. On pointer-up we snap to the grid and commit via onReschedule.
  const [dragDy, setDragDy] = useState(0);
  const [liveResizeMin, setLiveResizeMin] = useState(null); // live duration during resize drag
  const durationMin = liveResizeMin != null ? Math.max(25, liveResizeMin) : rawDurationMin;
  // Live-projected start time while dragging — shown inside the block's
  // header so the user sees the new time immediately, not only on drop.
  const dragDeltaMin = (() => {
    if (!dragDy) return 0;
    const pxPerMinute = HOUR_HEIGHT / 60;
    const raw = dragDy / pxPerMinute;
    return snapToGrid ? Math.round(raw / snapMinutes) * snapMinutes : Math.round(raw);
  })();
  const liveStart = dragDy ? addMinutes(start, dragDeltaMin) : start;
  const liveEnd = dragDy ? addMinutes(end, dragDeltaMin) : liveResizeMin ? addMinutes(start, liveResizeMin) : end;

  const onCardPointerDown = (e) => {
    if (event.locked) return; // locked nodes cannot be moved
    if (e.target.closest("[data-conn-handle]") || e.target.closest("[data-nopropagate]") || e.target.closest("[data-resize]")) return;
    if (e.button === 2) return; // right-click handled separately
    // For action nodes we use native HTML5 drag (nesting into sequences), so
    // skip the pointer-based reschedule entirely. The native drag path is on
    // the outer element's onDragStart below.
    if (event.type === "action") return;
    actionRef.current = { startY: e.clientY, startX: e.clientX, moved: false };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onCardPointerMove = (e) => {
    if (!actionRef.current) return;
    const dy = e.clientY - actionRef.current.startY;
    const dx = e.clientX - actionRef.current.startX;
    if (Math.hypot(dx, dy) > 4) {
      actionRef.current.moved = true;
      setDragDy(dy);
    }
  };
  const onCardPointerUp = (e) => {
    const st = actionRef.current;
    actionRef.current = null;
    setDragDy(0);
    if (!st) return;


    // Detect cross-day move: temporarily disable pointer-events on this element
    // so elementFromPoint sees the day column beneath it, not the event block itself
    const dragEl = e.currentTarget;
    const prevPE = dragEl.style.pointerEvents;
    dragEl.style.pointerEvents = "none";
    const elUnder = document.elementFromPoint(e.clientX, e.clientY);
    dragEl.style.pointerEvents = prevPE;
    const colUnder = elUnder?.closest("[data-day-col]");
    const targetDay = colUnder?.dataset?.dayCol;
    const sourceDay = event.start ? event.start.substring(0, 10) : "";
    if (st.moved && targetDay && targetDay !== sourceDay) {
      // Cross-day pointer move — calculate time from pointer position in target column
      const colRect = colUnder.getBoundingClientRect();
      const y = e.clientY - colRect.top;
      const rawMin = (y / HOUR_HEIGHT) * 60;
      const snapMin = snapMinutes || 15;
      const minutes = snapToGrid ? Math.round(rawMin / snapMin) * snapMin : Math.round(rawMin);
      const targetDate = new Date(targetDay + "T00:00:00");
      const newStart = new Date(targetDate.getTime() + minutes * 60000);
      onReschedule?.(event.event_id, 0, newStart); // signal cross-day with absolute newStart
      return;
    }

    const dy = e.clientY - st.startY;
    const pxPerMinute = HOUR_HEIGHT / 60;
    const raw = dy / pxPerMinute;
    const deltaMin = snapToGrid ? Math.round(raw / snapMinutes) * snapMinutes : Math.round(raw);
    const minMove = snapToGrid ? snapMinutes : 5;
    if (st.moved && Math.abs(deltaMin) >= minMove) {
      onReschedule(event.event_id, deltaMin);
    } else if (!st.moved) {
      // click = select (with shift/meta: additive)
      onSelect(event.event_id, { additive: e.shiftKey || e.metaKey || e.ctrlKey });
      // Click toggles expand/collapse when this node overlaps others — no
      // separate caret button needed (user request). Non-overlapping nodes
      // are unaffected.
      if (total > 1 && !(e.shiftKey || e.metaKey || e.ctrlKey)) {
        setExpandedId?.(isExpanded ? null : event.event_id);
      }
      // detect double-click manually
      const now = Date.now();
      if (now - lastTapRef.current < 350 && !e.shiftKey) {
        onOpen(event);
      }
      lastTapRef.current = now;
    }

  };

  // Resize
  const resizeRef = useRef(null);
  const onResizePointerDown = (e) => {
    e.stopPropagation(); e.preventDefault();
    const origDuration = Math.max(25, differenceInMinutes(new Date(event.end), new Date(event.start)));
    resizeRef.current = { startY: e.clientY, startDuration: origDuration };
    const onMove = (ev) => {
      if (!resizeRef.current) return;
      const dy = ev.clientY - resizeRef.current.startY;
      const pxPerMinute = HOUR_HEIGHT / 60;
      const rawMin = dy / pxPerMinute;
      const deltaMin = snapToGrid ? Math.round(rawMin / snapMinutes) * snapMinutes : Math.round(rawMin);
      const newDuration = Math.max(15, resizeRef.current.startDuration + deltaMin);
      // Update visually in real-time (no API call during drag)
      setLiveResizeMin(newDuration);
    };
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (!resizeRef.current) return;
      const dy = ev.clientY - resizeRef.current.startY;
      const pxPerMinute = HOUR_HEIGHT / 60;
      const rawMin = dy / pxPerMinute;
      const deltaMin = snapToGrid ? Math.round(rawMin / snapMinutes) * snapMinutes : Math.round(rawMin);
      const newDuration = Math.max(15, resizeRef.current.startDuration + deltaMin);
      const newEnd = addMinutes(new Date(event.start), newDuration);
      setLiveResizeMin(null); // clear live preview
      onResize(event.event_id, newEnd.toISOString()); // commit to server
      resizeRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // Accept drops: note-type node creates a subnote; for sequence containers,
  // also accept existing-event drags to nest them as children.
  const [dropPicker, setDropPicker] = useState(null); // { x, y, nodeType, existingId }

  const onEventDragOver = (e) => {
    const isNew = e.dataTransfer?.types?.includes("application/x-crescendo-node-type");
    const isExisting = e.dataTransfer?.types?.includes("application/x-crescendo-event-id");
    if (isNew || isExisting) {
      e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy";
    }
  };
  const onEventDrop = async (e) => {
    const nodeType = e.dataTransfer.getData("application/x-crescendo-node-type");
    const existingId = e.dataTransfer.getData("application/x-crescendo-event-id");

    // Sequence containers: existing event drop becomes a child
    if (event.type === "sequence" && existingId && existingId !== event.event_id) {
      e.preventDefault(); e.stopPropagation();
      onAddToSequence?.(event.event_id, existingId);
      return;
    }

    // New-node drop OR existing-node drop on any non-sequence node → show picker
    // BUT: if the dragged event is from a different day, let it bubble to DayColumn (cross-day move)
    if (existingId && existingId !== event.event_id) {
      const draggedDay = e.dataTransfer.getData("application/x-crescendo-event-day");
      const targetDay = event.start ? event.start.substring(0, 10) : "";
      if (draggedDay && targetDay && draggedDay !== targetDay) {
        // Cross-day: don't intercept — bubble to DayColumn's onDrop handler
        return;
      }
    }
    if (nodeType || (existingId && existingId !== event.event_id)) {
      e.preventDefault(); e.stopPropagation();
      setDropPicker({ x: e.clientX, y: e.clientY, nodeType, existingId });
    }
  };

  // Overlap stacking — when `total > 1`, divide the column width into equal
  // sub-columns so every overlapping block remains visible simultaneously
  // (user request: tasks overlapping should "stack next to each other and size
  // down a bit"). Gaps shrink as overlap increases.
  const isContinuation = !!event._continuation;
  // Check if this event is happening right now
  const nowMs = Date.now();
  const isHappening = !event.completed_at && !event.unscheduled &&
    Date.parse(event.start) <= nowMs && Date.parse(event.end || event.start) >= nowMs;

  const classes = `event-block event-node absolute rounded-md overflow-visible group hover:brightness-110 ${event.type === "sequence" ? "sequence-block" : ""} ${selected ? "event-selected" : ""} ${classBg ? "class-match" : ""} ${isContinuation ? "event-continuation" : ""} ${isUnused ? "ring-1 ring-[#EAB308]" : ""}`;
  const styleObj = {
    top: top,
    minHeight: (isCollapsed || isDensityCollapsed) ? 28 : height,
    height: (isCollapsed || isDensityCollapsed) ? 28 : undefined,
    left: `calc(${colLeftPct}% + 3px)`,
    width: `calc(${colWidthPct}% - 6px)`,
    background: isDone
      ? `${color}40`
      : isHappening
        ? color
        : color,
    borderRadius: 10,
    opacity: isDimmed ? 0.35 : 1,
    zIndex: (isExpanded ? 40 : 10) + col + (dragDy ? 50 : 0),
    transform: dragDy ? `translateY(${dragDy}px)` : undefined,
    boxShadow: isHappening
      ? `0 0 0 2px white, 0 4px 16px ${color}60`
      : dragDy ? "0 12px 32px rgba(0,0,0,0.5)" : undefined,
    cursor: dragDy ? "grabbing" : (event.locked ? "not-allowed" : "grab"),
    transition: "left 0.18s ease, width 0.18s ease",
    "--event-color": color,
  };

  const condensed = false;
  const isDone = !!event.completed_at;
  return (
    <div
      ref={(el) => setRef(event.event_id, el)}
      className={`${classes} ${isDone ? "event-completed" : ""}`}
      style={styleObj}
      data-collapsed={isCollapsedOverlap ? "1" : "0"}
      onPointerDown={onCardPointerDown}
      onPointerMove={onCardPointerMove}
      onPointerUp={onCardPointerUp}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onContextMenu?.(e, event); }}
      onDragOver={onEventDragOver}
      onDrop={onEventDrop}
      draggable={!(("ontouchstart" in window) || isFloatingType(event.type))}
      onDragStart={(e) => {
        // Always set event-id so drop zones work
        e.dataTransfer.setData("application/x-crescendo-event-id", event.event_id);
        e.dataTransfer.setData("application/x-crescendo-event-day", event.start ? event.start.substring(0, 10) : "");
        e.dataTransfer.effectAllowed = "move";
        // Pointer drag was initiated — cancel HTML5 drag so pointer path handles reschedule.
        // This ensures same-day time-change via pointer events works properly.
        if (actionRef.current) {
          e.preventDefault();
          return;
        }
      }}
      data-event-id={event.event_id}
      data-testid={`event-${event.event_id}`}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
    >
      {(isCollapsedOverlap || isDensityCollapsed) ? (
        /* Minimized pill when stacked behind another overlapping node.
           Shows ONLY title, color bar, and complete checkbox for tasks
           (user request). Clicking anywhere on the pill expands it. */
        <div
          className="collapsed-pill px-2 h-full flex items-center gap-2 cursor-pointer"
          onClick={isDensityCollapsed && !isCollapsedOverlap ? onToggleDensityCollapse : undefined}
          data-testid={`collapsed-pill-${event.event_id}`}
          data-nopropagate
          onClick={(e) => { e.stopPropagation(); setExpandedId?.(event.event_id); }}
        >
          <span className="inline-block w-1 h-3 rounded-sm flex-shrink-0" style={{ background: color }} />
          {event.type === "task" && (
            <button
              data-nopropagate
              onClick={(e) => { e.stopPropagation(); onToggleComplete?.(event); }}
              className={`w-3 h-3 rounded-sm border flex items-center justify-center flex-shrink-0 ${isDone ? "bg-[#0A0A0A] border-[#5A90F5]" : "border-[#3A3A3A] hover:border-[#5A90F5] bg-[#0A0A0A]"}`}
              title={isDone ? "Mark incomplete" : "Mark complete"}
              data-testid={`task-complete-${event.event_id}`}
            >
              {isDone && <Check className="w-2 h-2 text-[#FFFFFF]" strokeWidth={3} />}
            </button>
          )}
          <span className={`truncate text-[11px] ${isDone ? "line-through text-[#6A6A6A]" : "text-[#EDEDED]"}`}>{event.title}</span>
          {event.locked && <Lock className="w-3 h-3 text-[#5A90F5] ml-auto flex-shrink-0" />}
        </div>
      ) : (
      <div className="px-2 pt-1.5 pb-1">
        {!condensed && (
          <>
            <div className="flex items-center gap-1 mb-0.5">
              {/* Task-only inline complete checkbox — click to toggle without
                  opening the right-click menu. (P0 backlog item.) */}
              {event.type === "task" && (
                <button
                  data-nopropagate
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleComplete?.(event);
                  }}
                  className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 ${event.completed_at ? "bg-[#0A0A0A] border-[#5A90F5]" : "border-[#3A3A3A] hover:border-[#5A90F5] bg-[#0A0A0A]"}`}
                  title={event.completed_at ? "Mark incomplete" : "Mark complete"}
                  data-testid={`task-complete-${event.event_id}`}
                >
                  {event.completed_at && <Check className="w-2.5 h-2.5 text-[#FFFFFF]" strokeWidth={3} />}
                </button>
              )}
              <Icon className="w-3 h-3 text-white/70" />
              <span className="text-[9px] uppercase tracking-[0.1em] font-medium text-white/60">
                {event.type}{event.class_name ? ` · ${event.class_name}` : ""}
              </span>
            </div>
            <div className={`event-title text-[13px] font-semibold truncate leading-tight ${event.completed_at ? "line-through opacity-50" : "text-white"}`}>
              {event.title}
              {Array.isArray(event.subtasks) && event.subtasks.length > 0 && (
                <span className="ml-1.5 text-[10px] text-[#A1A1AA]" data-testid={`subtasks-count-${event.event_id}`}>
                  {event.subtasks.find(s => s.active && !s.done)
                    ? `▸ ${event.subtasks.find(s => s.active && !s.done).text.slice(0, 20)}`
                    : `+${event.subtasks.length} subtask${event.subtasks.length === 1 ? "" : "s"}`}
                </span>
              )}
              {event.type === "sequence" && (
                <span className="ml-1.5 text-[10px] text-[#A1A1AA] inline-flex items-center gap-1" data-testid={`sequence-meta-${event.event_id}`}>
                  {/* Live count — recomputed from `children_event_ids` so it
                      always reflects current state (user request). */}
                  <span data-testid={`sequence-step-count-${event.event_id}`}>
                    {(event.children_event_ids?.length || 0)} {(event.children_event_ids?.length || 0) === 1 ? "step" : "steps"}
                  </span>
                  {/* S = Sequential, Q = Parallel/Queue (user request:
                      indicate sequence vs parallel firing). */}
                  <span
                    className="px-1 rounded text-[9px] font-mono uppercase tracking-tight"
                    style={{
                      background: (event.sequence_mode || "sequential") === "parallel" ? "rgba(168,85,247,0.18)" : "rgba(20,184,166,0.18)",
                      color: (event.sequence_mode || "sequential") === "parallel" ? "#A855F7" : "#14B8A6",
                    }}
                    title={(event.sequence_mode || "sequential") === "parallel" ? "Parallel — fires simultaneously" : "Sequential — fires one after another"}
                    data-testid={`sequence-mode-badge-${event.event_id}`}
                  >
                    {(event.sequence_mode || "sequential") === "parallel" ? "Q" : "S"}
                  </span>
                </span>
              )}
            </div>
            <div className="text-[10px] text-white/70 flex items-center gap-1">
              {event.type === "reminder" || event.type === "trigger" || event.type === "action" || event.type === "condition" || event.type === "note"
                ? format(liveStart, timeFormat)
                : `${format(liveStart, timeFormat)} – ${format(liveEnd, timeFormat)}`}
              {durationMin >= 60 && !isFloatingType(event.type) && (
                <span className="text-[#52525B] ml-0.5">
                  {durationMin >= 60 ? `${Math.floor(durationMin/60)}h${durationMin%60 ? `${durationMin%60}m` : ""}` : `${durationMin}m`}
                </span>
              )}
            </div>
            {event.type === "sequence" && (
              <div
                className="mt-2 rounded-md border border-dashed border-[#14B8A6]/50 bg-[#0A0A0A]/50 p-1.5 pb-3 space-y-1"
                data-testid={`sequence-children-${event.event_id}`}
                onDragOver={(e) => {
                  const t = e.dataTransfer?.types;
                  if (t?.includes("application/x-crescendo-node-type") || t?.includes("application/x-crescendo-event-id")) {
                    e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy";
                    e.currentTarget.classList.add("seq-drop-active");
                  }
                }}
                onDragLeave={(e) => e.currentTarget.classList.remove("seq-drop-active")}
                onDrop={async (e) => {
                  e.currentTarget.classList.remove("seq-drop-active");
                  const nodeType = e.dataTransfer.getData("application/x-crescendo-node-type");
                  const existingId = e.dataTransfer.getData("application/x-crescendo-event-id");
                  // ANY new-node drop into a sequence becomes an action —
                  // sequences only accept actions, so we should never make
                  // the user pick a type (user request: "When dragging a
                  // new node into a sequence, it should automatically
                  // bring up the new action screen, it should NOT let the
                  // user choose what kind of node they want to add").
                  if (nodeType) {
                    e.preventDefault(); e.stopPropagation();
                    onCreateChildInSequence?.(event.event_id);
                    return;
                  }
                  if (existingId && existingId !== event.event_id) {
                    e.preventDefault(); e.stopPropagation();
                    onAddToSequence?.(event.event_id, existingId);
                  }
                }}
              >
                {(event.children_event_ids || []).map((cid, idx) => {
                  const child = resolveChild?.(cid);
                  // Skip silently if the underlying event was deleted —
                  // user request: don't permanently show "(missing)".
                  if (!child) return null;
                  const childColor = child.custom_color || NODE_TYPE_COLORS[child.type] || "#0055FF";
                  // Render each child as a real mini-NODE: registered in
                  // eventRefs (so edges work), draggable output handle, click
                  // to open, right-click for context menu, X to detach.
                  // Since sequences only accept actions, we drop the type
                  // label + icon (user request).
                  return (
                    <div
                      key={cid}
                      ref={(el) => setRef(child.event_id, el)}
                      draggable
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.setData("application/x-crescendo-seq-child-id", cid);
                        e.dataTransfer.setData("application/x-crescendo-seq-parent-id", event.event_id);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragOver={(e) => {
                        const types = e.dataTransfer?.types;
                        if (types?.includes("application/x-crescendo-seq-child-id")) {
                          e.preventDefault(); e.stopPropagation();
                          e.dataTransfer.dropEffect = "move";
                          // Visual insertion indicator — top half = before,
                          // bottom half = after.
                          const rect = e.currentTarget.getBoundingClientRect();
                          const before = (e.clientY - rect.top) < rect.height / 2;
                          e.currentTarget.classList.toggle("seq-reorder-top", before);
                          e.currentTarget.classList.toggle("seq-reorder-bottom", !before);
                        }
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove("seq-reorder-top", "seq-reorder-bottom");
                      }}
                      onDrop={(e) => {
                        const movedId = e.dataTransfer.getData("application/x-crescendo-seq-child-id");
                        const fromSeq = e.dataTransfer.getData("application/x-crescendo-seq-parent-id");
                        if (!movedId || fromSeq !== event.event_id || movedId === cid) return;
                        e.preventDefault(); e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        const before = (e.clientY - rect.top) < rect.height / 2;
                        e.currentTarget.classList.remove("seq-reorder-top", "seq-reorder-bottom");
                        onReorderSequenceChild?.(event.event_id, movedId, cid, before);
                      }}
                      className="event-block sequence-child-node group relative rounded-md bg-[#141414] hover:bg-[#1a1a1a] cursor-pointer overflow-visible"
                      style={{ borderTop: `2px solid ${childColor}` }}
                      data-event-id={child.event_id}
                      data-testid={`sequence-child-${event.event_id}-${cid}`}
                      data-nopropagate
                      onClick={(e) => { e.stopPropagation(); onOpen?.(child); }}
                      onContextMenu={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        onContextMenu?.(e, child);
                      }}
                    >
                      <div className="px-2 py-1 flex items-center gap-1.5">
                        <span className="flex-1 truncate text-[11px] text-[#EDEDED] leading-tight">
                          {child.title || <span className="italic text-[#A1A1AA]">untitled</span>}
                        </span>
                        {child.action_config?.type && (
                          <span className="text-[9px] uppercase tracking-wider text-[#A1A1AA] flex-shrink-0 mr-4">
                            {child.action_config.type}
                          </span>
                        )}
                        <button
                          data-nopropagate
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveFromSequence?.(event.event_id, cid);
                          }}
                          className="opacity-0 group-hover:opacity-60 hover:!opacity-100 p-0.5 text-[#FF3B30] flex-shrink-0"
                          title="Remove from sequence"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      {/* Right-side output handle so users can start edges
                          directly from a nested child (user request). */}
                      <div
                        data-conn-handle="right"
                        className="conn-handle right"
                        style={{ background: childColor }}
                        onPointerDown={(e) => {
                          e.stopPropagation(); e.preventDefault();
                          onStartConnect?.(child, "right", e);
                        }}
                        data-testid={`handle-right-${child.event_id}`}
                      />
                    </div>
                  );
                })}
                {/* Drop zone — only renders when the sequence is selected/
                    focused (user request) so the calendar isn't cluttered
                    with empty plus boxes. Always reachable: clicking the
                    sequence selects it, then the + appears. */}
                {selected && (
                  <div
                    className="seq-drop-zone h-7 rounded-md border border-dashed border-[#2A2A2A] flex items-center justify-center text-[#5A5A5A] hover:border-[#6A6A6A] hover:text-[#A1A1AA] cursor-pointer"
                    data-testid={`seq-drop-zone-${event.event_id}`}
                    data-nopropagate
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateChildInSequence?.(event.event_id);
                    }}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {condensed && (
          <div className="text-[11px] font-medium text-[#EDEDED] truncate leading-tight">{event.title}</div>
        )}
      </div>
      )}

      {/* Handles, resize, and notes are hidden on collapsed back-stack pills. */}
      {!isCollapsedOverlap && !isDensityCollapsed && (
        <>
      <button
        data-nopropagate
        onClick={(e) => { e.stopPropagation(); setNotesOpen(!notesOpen); }}
        className="absolute top-1 right-1 p-0.5 rounded bg-[#0A0A0A]/70 hover:bg-[#0A0A0A] flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`notes-dropdown-${event.event_id}`}
      >
        <StickyNote className="w-3 h-3 text-[#A1A1AA]" />
        {notes.length > 0 && <span className="text-[9px] text-[#A1A1AA]">{notes.length}</span>}
      </button>

      {/* Output handles. Condition nodes have ONLY true/false branches —
          no right-middle handle (issue #10). */}
      {event.type !== "condition" && (
        <div data-conn-handle="right" className={`conn-handle right `} style={{ background: color }}
          onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onStartConnect(event, "right", e); }}
          data-testid={`handle-right-${event.event_id}`} />
      )}

      {/* Condition nodes: two output handles. Green = true (top), Red = false (bottom). */}
      {event.type === "condition" && (
        <>
          <div data-conn-handle="true" className="cond-handle top" title="If True"
            onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onStartConnect(event, "true", e); }}
            data-testid={`cond-true-handle-${event.event_id}`} />
          <div data-conn-handle="false" className="cond-handle bottom" title="If False"
            onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onStartConnect(event, "false", e); }}
            data-testid={`cond-false-handle-${event.event_id}`} />
        </>
      )}

      {/* Subtask progress bar — shown when task has subtasks */}
      {Array.isArray(event.subtasks) && event.subtasks.length > 0 && (() => {
        const done = event.subtasks.filter(s => s.done).length;
        const total = event.subtasks.length;
        const pct = Math.round((done / total) * 100);
        return (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black/20 rounded-b" data-nopropagate>
            <div className="h-full rounded-b transition-all" style={{ width: `${pct}%`, background: pct === 100 ? "#00C781" : color }} />
          </div>
        );
      })()}
      <div data-resize className="resize-handle" onPointerDown={onResizePointerDown} />
      {/* Nest handle removed — actions are natively draggable. See the
          draggable + onDragStart on the outer .event-block below. */}

      {notesOpen && (
        <div className="border-t border-[#2A2A2A] bg-[#0f0f0f] p-1.5 space-y-1" data-nopropagate onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} data-testid={`notes-panel-${event.event_id}`}>
          {notes.map((n) => (
            <div key={n.note_id} className="flex items-start gap-1 group/n">
              <div className="text-[10px] text-[#A1A1AA] flex-1 whitespace-pre-wrap break-words">{n.content}</div>
              <button onClick={() => onDeleteNote(event.event_id, n.note_id)} className="opacity-0 group-hover/n:opacity-100">
                <X className="w-3 h-3 text-[#FF3B30]" />
              </button>
            </div>
          ))}
          <form onSubmit={addNote} className="flex gap-1">
            <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="add note..."
              onBlur={(e) => { if (!e.currentTarget.value.trim()) setNotesOpen(false); }}
              className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[10px] outline-none focus:border-[#555]" />
            <button type="submit" className="text-[10px] px-1.5 bg-[#EDEDED] text-[#0A0A0A] rounded font-medium">+</button>
          </form>
        </div>
      )}
        </>
      )}
    </div>
  );
}

// ConditionBuilder — dropdown-based logic rule editor (no free-typing).
// Persists the logic as a JSON array of rows into `condition_logic` so the
// backend can evaluate it programmatically once the evaluator ships.
function ConditionBuilder({ logic, setLogic, outputs, setOutputs }) {
  const { pairs } = useKeyPairs?.() || { pairs: [] };
  const [mode, setMode] = useState("builder"); // 'builder' | 'json'
  const [shape, setShape] = useState("if_else"); // 'true_false' | 'if_else' | 'while' | 'while_not'
  const [jsonDraft, setJsonDraft] = useState("");
  // Phase state — same pattern as ResourceEditor (user request: same
  // "click button → pick type → confirm" flow for conditions).
  const initiallyConfigured = (logic && logic !== "[]") || (Array.isArray(outputs) && outputs.length > 0);
  const [phase, setPhase] = useState(initiallyConfigured ? "configured" : "idle");
  const rows = (() => {
    if (!logic) return [];
    try { const v = JSON.parse(logic); return Array.isArray(v) ? v : []; } catch { return []; }
  })();
  const setRows = (next) => setLogic(JSON.stringify(next));
  const addRow = () => setRows([...rows, { op: "AND", not: false, key: pairs[0]?.key || "", cmp: "==", value: "" }]);
  const updateRow = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const human = rows.map((r, i) => `${i > 0 ? (r.op || "AND") + " " : ""}${r.not ? "NOT " : ""}${r.key || "?"} ${r.cmp} ${r.value || '""'}`).join(" ");

  useEffect(() => {
    if (mode === "json") {
      try { setJsonDraft(JSON.stringify(JSON.parse(logic || "[]"), null, 2)); }
      catch { setJsonDraft(logic || "[]"); }
    }
  }, [mode, logic]);

  // Apply a shape preset to `outputs` and enter the builder phase.
  const applyShape = (s) => {
    setShape(s);
    if (s === "true_false") setOutputs([{ label: "true", value: "" }, { label: "false", value: "" }]);
    else if (s === "if_else") setOutputs([{ label: "if", value: "" }, { label: "else", value: "" }]);
    else if (s === "while") setOutputs([{ label: "while true", value: "" }, { label: "on exit", value: "" }]);
    else if (s === "while_not") setOutputs([{ label: "while not", value: "" }, { label: "on exit", value: "" }]);
    setPhase("configured");
  };

  // ---- Phase: idle — single dashed full-width button (matches the resource
  // initialization pattern; user request: "Logic builder should look like
  // the resource initialization menu"). ----
  if (phase === "idle") {
    return (
      <div className="border border-[#2A2A2A] bg-[#0A0A0A] rounded-md p-3" data-testid="condition-builder">
        <div className="meta-label mb-2">CONDITION</div>
        <button
          type="button"
          onClick={() => setPhase("pick")}
          className="w-full px-3 py-3 rounded-md border-2 border-dashed border-[#3A3A3A] hover:border-[#6A6A6A] hover:bg-[#A1A1AA]/10 text-[#A1A1AA] text-xs uppercase tracking-wider flex items-center justify-center gap-2"
          data-testid="condition-add-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add condition
        </button>
      </div>
    );
  }

  // ---- Phase: pick shape ----
  if (phase === "pick") {
    const shapes = [
      { key: "true_false", label: "True / False" },
      { key: "if_else", label: "If / Else" },
      { key: "while", label: "While" },
      { key: "while_not", label: "While Not" },
    ];
    return (
      <div className="border border-[#2A2A2A] bg-[#0A0A0A] rounded-md p-3 space-y-2" data-testid="condition-builder">
        <div className="meta-label">PICK A SHAPE</div>
        <div className="grid grid-cols-2 gap-1.5" data-testid="condition-shape-picker">
          {shapes.map((s) => (
            <button
              key={s.key}
              onClick={() => applyShape(s.key)}
              className="text-left px-3 py-2 rounded-md border border-[#2A2A2A] hover:border-[#6A6A6A] hover:bg-[#A1A1AA]/10 text-xs text-[#EDEDED]"
              data-testid={`condition-shape-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex justify-end pt-1">
          <button onClick={() => setPhase("idle")} className="px-3 py-1 rounded text-[11px] border border-[#2A2A2A] hover:bg-[#1a1a1a]" data-testid="condition-cancel">Cancel</button>
        </div>
      </div>
    );
  }

  // ---- Phase: configured — full builder (existing UX, restyled to match
  // resource builder neutrality) ----
  return (
    <div className="border border-[#2A2A2A] bg-[#0A0A0A] rounded-md p-3 space-y-3" data-testid="condition-builder">
      <div className="flex items-center justify-between">
        <div className="meta-label">CONDITION — logic + outputs</div>
        <div className="flex items-center gap-1">
          <select value={shape} onChange={(e) => applyShape(e.target.value)}
            className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1 text-[10px] uppercase tracking-wider text-[#A1A1AA]"
            data-testid="condition-shape-select"
            title="Flow shape"
          >
            <option value="true_false">True / False</option>
            <option value="if_else">If / Else</option>
            <option value="while">While</option>
            <option value="while_not">While Not</option>
          </select>
          <select value={mode} onChange={(e) => setMode(e.target.value)}
            className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1 text-[10px] uppercase tracking-wider text-[#A1A1AA]"
            data-testid="condition-mode-select"
          >
            <option value="builder">Builder</option>
            <option value="json">JSON Editor</option>
          </select>
        </div>
      </div>
      {mode === "json" ? (
        <div>
          <textarea
            value={jsonDraft}
            onChange={(e) => setJsonDraft(e.target.value)}
            onBlur={() => {
              try { JSON.parse(jsonDraft); setLogic(jsonDraft); }
              catch { toast.error("Invalid JSON — not saved"); }
            }}
            rows={10}
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs font-mono"
            spellCheck={false}
            data-testid="condition-json-editor"
          />
          <div className="text-[10px] text-[#A1A1AA] mt-1">Structured rules as JSON: array of {"{op, not, key, cmp, value}"} objects.</div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="meta-label">RULES</span>
            <button type="button" onClick={addRow}
              className="text-[10px] text-[#A855F7] hover:underline flex items-center gap-1"
              data-testid="condition-add-rule"
            >
              <Plus className="w-3 h-3" /> Add Rule
            </button>
          </div>
          {rows.length === 0 && (
            <div className="text-[11px] text-[#A1A1AA] italic">No rules — condition will evaluate true by default.</div>
          )}
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-1 mb-1" data-testid={`condition-rule-${i}`}>
              {i > 0 && (
                <select value={r.op || "AND"} onChange={(e) => updateRow(i, { op: e.target.value })}
                  className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-1.5 py-1 text-[10px] uppercase">
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
              )}
              <button type="button" onClick={() => updateRow(i, { not: !r.not })}
                className={`text-[10px] uppercase tracking-wider px-1.5 py-1 rounded-md border ${r.not ? "border-[#A855F7] bg-[#A855F7]/20 text-[#A855F7]" : "border-[#2A2A2A] text-[#A1A1AA]"}`}
                title="Toggle NOT">NOT</button>
              <select value={r.key || ""} onChange={(e) => updateRow(i, { key: e.target.value })}
                className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-1.5 py-1 text-xs font-mono">
                <option value="">(key)</option>
                {pairs.map((p) => (<option key={p.kp_id} value={p.key}>{p.key}</option>))}
              </select>
              <select value={r.cmp || "=="} onChange={(e) => updateRow(i, { cmp: e.target.value })}
                className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-1.5 py-1 text-xs">
                <option value="==">=</option>
                <option value="!=">≠</option>
                <option value=">">&gt;</option>
                <option value=">=">≥</option>
                <option value="<">&lt;</option>
                <option value="<=">≤</option>
                <option value="contains">contains</option>
              </select>
              <input value={r.value || ""} onChange={(e) => updateRow(i, { value: e.target.value })}
                placeholder="value"
                className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-1.5 py-1 text-xs font-mono" />
              <button type="button" onClick={() => removeRow(i)}
                className="p-1 rounded hover:bg-[#2a2a2a] text-[#FF3B30]"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {rows.length > 0 && (
            <div className="mt-1 px-2 py-1 rounded bg-[#0A0A0A] border border-[#2A2A2A] text-[11px] text-[#A1A1AA] font-mono" data-testid="condition-preview">{human}</div>
          )}
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="meta-label">OUTPUTS</span>
          <button type="button"
            onClick={() => setOutputs([...outputs, { label: `Branch ${outputs.length + 1}`, value: "" }])}
            className="text-[10px] text-[#A855F7] hover:underline flex items-center gap-1"
            data-testid="condition-add-output"
          >
            <Plus className="w-3 h-3" /> Add Output
          </button>
        </div>
        {outputs.length === 0 && (
          <div className="text-[11px] text-[#A1A1AA] italic">Using default true / false branches.</div>
        )}
        {outputs.map((o, idx) => (
          <div key={idx} className="flex items-center gap-2 mb-1" data-testid={`condition-output-row-${idx}`}>
            <input value={o.label} onChange={(e) => setOutputs(outputs.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
              placeholder="label" className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1 text-xs" />
            <input value={o.value} onChange={(e) => setOutputs(outputs.map((x, i) => i === idx ? { ...x, value: e.target.value } : x))}
              placeholder="match value" className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1 text-xs font-mono" />
            <button type="button" onClick={() => setOutputs(outputs.filter((_, i) => i !== idx))}
              className="p-1 rounded hover:bg-[#2a2a2a] text-[#FF3B30]"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <input type="hidden" value={logic} readOnly data-testid="condition-logic-input" />
    </div>
  );
}

// KeyPairTagRow — shows the user's key pairs as chips; clicking one inserts a
// `{{key}}` reference into the action's message field.
function KeyPairTagRow({ onInsert }) {
  const { pairs } = useKeyPairs?.() || { pairs: [] };
  if (!pairs || pairs.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid="action-keypair-tags">
      <span className="text-[9px] uppercase tracking-wider text-[#A1A1AA] self-center mr-1">insert:</span>
      {pairs.map((p) => (
        <button key={p.kp_id}
          type="button"
          onClick={() => onInsert(p.key)}
          className="px-1.5 py-0.5 rounded-md border border-[#2A2A2A] bg-[#0A0A0A] text-[10px] font-mono text-[#5A90F5] hover:bg-[#141414]"
          data-testid={`action-kp-chip-${p.key}`}
          title={`Inserts {{${p.key}}}`}
        >
          {"{{"}{p.key}{"}}"}
        </button>
      ))}
    </div>
  );
}

// ResourceEditor — dropdown + kind-specific input; supports linking to an
// existing Document, pasting a URL, or uploading a small file which is stored
// as a new Document and referenced by doc_id.
function ResourceEditor({ resourceKind, setResourceKind, resourceUrl, setResourceUrl }) {
  const { docs, create: createDoc } = useDocuments();
  // Phases:
  //  - "idle" : nothing configured — show single full-width "+ Add resource" button
  //  - "pick" : pick a kind
  //  - "configure" : draft kind+value, with Confirm to commit
  //  - "configured" : kind+url already set (existing resource); render summary + edit
  const [phase, setPhase] = useState(() => (resourceUrl ? "configured" : "idle"));
  const [draftKind, setDraftKind] = useState(resourceKind || "link");
  const [draftValue, setDraftValue] = useState(resourceUrl || "");

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text().catch(() => "");
    try {
      const doc = await createDoc({ name: file.name, kind: "text", body: text });
      setDraftKind("doc"); setDraftValue(doc.doc_id);
      setResourceKind("doc"); setResourceUrl(doc.doc_id);
      setPhase("configured");
      toast.success(`Attached ${file.name}`);
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const confirm = () => {
    if (!draftValue && draftKind !== "text") { toast.error("Add a value"); return; }
    setResourceKind(draftKind); setResourceUrl(draftValue);
    setPhase("configured");
  };

  return (
    <div className="border border-[#6366F1]/40 bg-[#6366F1]/5 rounded-md p-3 space-y-3">
      <div className="meta-label" style={{ color: "#6366F1" }}>RESOURCE</div>

      {/* Phase 1 — idle: single full-width dashed button (user request:
          initialization config should NOT show the whole drag-drop menu — just
          a button, then a menu, then handle with confirmation). */}
      {phase === "idle" && (
        <button
          type="button"
          onClick={() => setPhase("pick")}
          className="w-full px-3 py-3 rounded-md border-2 border-dashed border-[#3A3A3A] hover:border-[#6A6A6A] hover:bg-[#A1A1AA]/10 text-[#A1A1AA] text-xs uppercase tracking-wider flex items-center justify-center gap-2"
          data-testid="resource-add-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add resource
        </button>
      )}

      {/* Phase 2 — pick a kind. */}
      {phase === "pick" && (
        <div className="grid grid-cols-2 gap-1.5" data-testid="resource-kind-picker">
          {[
            { key: "link", label: "Link / URL", Icon: ArrowRight },
            { key: "github", label: "GitHub Repo", Icon: LinkIcon },
            { key: "doc", label: "Document", Icon: FileText },
            { key: "text", label: "Plain Text", Icon: FileText },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => { setDraftKind(key); setPhase("configure"); }}
              className="text-left px-3 py-2 rounded-md border border-[#2A2A2A] hover:border-[#6366F1]/50 hover:bg-[#6366F1]/10 text-xs flex items-center gap-2"
              data-testid={`resource-menu-${key}`}
            >
              <Icon className="w-3 h-3 text-[#6366F1]" /> {label}
            </button>
          ))}
          <label
            className="text-left px-3 py-2 rounded-md border border-[#2A2A2A] hover:border-[#6366F1]/50 hover:bg-[#6366F1]/10 text-xs flex items-center gap-2 cursor-pointer"
            data-testid="resource-menu-upload"
          >
            <FileText className="w-3 h-3 text-[#6366F1]" /> Upload File…
            <input type="file" onChange={onUpload} className="hidden" />
          </label>
        </div>
      )}

      {/* Phase 3 — configure: input + Confirm/Cancel. */}
      {phase === "configure" && (
        <div className="space-y-2" data-testid="resource-configure">
          <label className="meta-label block mb-1">{draftKind === "link" ? "URL" : draftKind === "github" ? "GITHUB REPO URL" : draftKind === "doc" ? "DOCUMENT" : "CONTENT"}</label>
          {draftKind === "doc" ? (
            <select
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs"
              data-testid="resource-doc-select"
            >
              <option value="">(select a document)</option>
              {docs.map((d) => <option key={d.doc_id} value={d.doc_id}>{d.name}</option>)}
            </select>
          ) : (
            <input
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              placeholder={draftKind === "link" ? "https://…" : draftKind === "github" ? "https://github.com/owner/repo" : "plain text"}
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs font-mono"
              data-testid="resource-url-input"
            />
          )}
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => setPhase("idle")} className="px-3 py-1 rounded text-[11px] border border-[#2A2A2A] hover:bg-[#1a1a1a]" data-testid="resource-cancel">Cancel</button>
            <button onClick={confirm} className="px-3 py-1 rounded text-[11px] bg-[#6366F1] text-white font-semibold" data-testid="resource-confirm">Confirm</button>
          </div>
        </div>
      )}

      {/* Phase 4 — configured: summary + edit/clear. */}
      {phase === "configured" && (
        <div className="space-y-2" data-testid="resource-configured">
          <div className="flex items-center gap-2 text-xs">
            <span className="meta-label" style={{ color: "#6366F1" }}>{(resourceKind || draftKind || "link").toUpperCase()}</span>
            <span className="flex-1 truncate font-mono text-[#EDEDED]">{resourceKind === "doc" ? (docs.find((d) => d.doc_id === resourceUrl)?.name || resourceUrl) : (resourceUrl || "—")}</span>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setPhase("pick")} className="px-3 py-1 rounded text-[11px] border border-[#2A2A2A] hover:bg-[#1a1a1a]" data-testid="resource-edit">Change</button>
            <button onClick={() => { setResourceKind("link"); setResourceUrl(""); setDraftValue(""); setPhase("idle"); }} className="px-3 py-1 rounded text-[11px] border border-[#2A2A2A] hover:bg-[#FF3B30]/10 text-[#FF3B30]" data-testid="resource-clear">Clear</button>
          </div>
        </div>
      )}
      {/* Hidden kind select retained for test backwards-compat. */}
      <select value={resourceKind || ""} onChange={(e) => setResourceKind(e.target.value)} className="hidden" data-testid="resource-kind-select">
        <option value="link">Link (URL)</option>
        <option value="github">GitHub</option>
        <option value="doc">Document ref</option>
        <option value="text">Plain text</option>
      </select>
      {/* Drop picker — teleported to body via portal */}
      {dropPicker && createPortal(
        <>
          <div className="fixed inset-0 z-[9990]" onClick={() => setDropPicker(null)} />
          <div
            className="fixed z-[9991] bg-[#141414] border border-[#2A2A2A] rounded-lg shadow-2xl p-1 w-44"
            style={{ left: Math.min(dropPicker.x, window.innerWidth - 180), top: Math.min(dropPicker.y, window.innerHeight - 110) }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[9px] text-[#52525B] uppercase tracking-wider px-2 py-1">Add to this node</div>
            <button
              onClick={async () => { await onAddNote?.(event.event_id, ""); setNotesOpen(true); setDropPicker(null); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#1a1a1a] text-[#EDEDED]"
            >
              <StickyNote className="w-3.5 h-3.5 text-[#A1A1AA]" />
              <span className="text-[12px]">Add Note</span>
            </button>
            <button
              onClick={() => { onOpen?.(event); setDropPicker(null); setTimeout(() => window.dispatchEvent(new CustomEvent("crescendo:focus-resource", { detail: { event_id: event.event_id } })), 100); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#1a1a1a] text-[#EDEDED]"
            >
              <LinkIcon className="w-3.5 h-3.5 text-[#A1A1AA]" />
              <span className="text-[12px]">Add Resource</span>
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

function FloatingNode({ event, setRef, classColor, classBg, isDimmed, selected, onSelect, onOpen, onStartConnect, onContextMenu, onMoveCommit, onDragFrame, hasOutgoingEdge, columnRef, defaultY, onAddNote, notes, onDeleteNote, isUnused }) {
  const hasClass = !!event.class_name;
  // Brand-blue fallback (user request: stop color-coding by type; defer to
  // the user's class / custom_color choice and fall back to brand blue).
  // Per-type default colors (user request: restore type defaults on blocks,
  // keep dropdown menus brand-blue). User's chosen class/custom color still
  // wins over the type default when set.
  const TYPE_DEFAULTS = {
    task: "#EF4444",        // richer red
    routine: "#FFCC00",
    action: "#0055FF",
    reminder: "#00C781",
    trigger: "#FF8A00",
    condition: "#A855F7",
    sequence: "#14B8A6",
    note: "#A1A1AA",
    resource: "#6366F1",
  };
  const typeDefault = "#5A90F5";
  const baseColor = hasClass ? (classColor || "#5A90F5") : "#5A90F5";
  const color = event.custom_color || (hasClass ? classColor : null) || TYPE_DEFAULTS[event.type] || typeDefault;
  const Icon = TYPE_ICONS[event.type] || CheckSquare;
  const nodeRef = useRef(null);
  const dragState = useRef(null);
  const [localPos, setLocalPos] = useState(null); // transient while dragging

  // Effective position: stored position_x/y or default anchored to start-time slot
  const x = localPos?.x ?? (event.position_x ?? 12);
  const y = localPos?.y ?? (event.position_y ?? defaultY);

  const onPointerDown = (e) => {
    if (e.target.closest("[data-conn-handle]") || e.target.closest("[data-nopropagate]")) return;
    if (e.button !== 0) return;
    const rect = nodeRef.current?.getBoundingClientRect();
    const colRect = columnRef.current?.getBoundingClientRect();
    if (!rect || !colRect) return;
    dragState.current = {
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
      colRect,
      moved: false,
      start: { x: e.clientX, y: e.clientY },
    };
    nodeRef.current.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const st = dragState.current;
    if (!st) return;
    const moved = Math.hypot(e.clientX - st.start.x, e.clientY - st.start.y) > 4;
    if (moved) st.moved = true;
    // Keep the node inside its day column (issue #19: no overflow into the
    // next day). The 96px margin matches the .floating-node min-width.
    const nx = Math.max(0, Math.min(st.colRect.width - 96, e.clientX - st.colRect.left - st.dx));
    const ny = Math.max(0, e.clientY - st.colRect.top - st.dy);
    setLocalPos({ x: nx, y: ny });
    if (moved) onDragFrame?.();
  };
  const onPointerUp = (e) => {
    const st = dragState.current;
    dragState.current = null;
    try { nodeRef.current?.releasePointerCapture(e.pointerId); } catch {}
    if (!st) return;
    if (!st.moved) {
      // click → select, double-click → open
      onSelect(event.event_id, { additive: e.shiftKey || e.metaKey || e.ctrlKey });
      if (nodeRef.current._lastTap && Date.now() - nodeRef.current._lastTap < 350) {
        onOpen(event);
      }
      nodeRef.current._lastTap = Date.now();
      setLocalPos(null);
      return;
    }
    if (localPos) {
      onMoveCommit(event.event_id, localPos.x, localPos.y);
      setLocalPos(null);
    }
  };

  return (
    <div
      ref={(el) => { nodeRef.current = el; setRef(event.event_id, el); }}
      className={`floating-node ${selected ? "event-selected" : ""} ${classBg ? "class-match" : ""} ${isUnused ? "ring-1 ring-[#EAB308]" : ""}`}
      style={{
        top: y,
        borderLeft: `3px solid ${color}`,
        opacity: isDimmed ? 0.35 : 1,
        boxShadow: classBg ? `0 0 0 2px ${classBg}, 0 0 10px ${classBg}44` : undefined,
        "--class-color": classBg || "transparent",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onContextMenu?.(e, event); }}
      onDragOver={(e) => {
        if (e.dataTransfer?.types?.includes("application/x-crescendo-node-type")) {
          e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy";
        }
      }}
      onDrop={async (e) => {
        const t = e.dataTransfer.getData("application/x-crescendo-node-type");
        if (t === "note" && onAddNote) {
          e.preventDefault(); e.stopPropagation();
          await onAddNote(event.event_id, "new note");
        }
      }}
      data-event-id={event.event_id}
      data-testid={`floating-${event.event_id}`}
    >
      {/* Left color accent already via borderLeft — minimal one-line layout */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 min-h-[28px]">
        <Icon className="w-3 h-3 flex-shrink-0" style={{ color }} />
        <span className="text-[12px] font-medium text-[#EDEDED] truncate flex-1 leading-none">
          {event.title || event.type}
        </span>
        {event.pinned && <Pin className="w-2.5 h-2.5 text-[#52525B] flex-shrink-0" />}
        {/* Output handle — right edge */}
        {event.type !== "condition" && (
          <div
            data-conn-handle="right"
            className="conn-handle right flex-shrink-0"
            style={{ background: color, position: "relative", right: "unset", top: "unset", transform: "none", width: 8, height: 8, borderRadius: "50%", cursor: "crosshair", marginLeft: 2 }}
            onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onStartConnect(event, "right", e); }}
            data-testid={`handle-right-${event.event_id}`}
          />
        )}
        {event.type === "condition" && (
          <div className="flex flex-col gap-0.5 flex-shrink-0 ml-1">
            <div data-conn-handle="true" className="cond-handle top" title="If True"
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onStartConnect(event, "true", e); }}
              data-testid={`cond-true-handle-${event.event_id}`} />
            <div data-conn-handle="false" className="cond-handle bottom" title="If False"
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onStartConnect(event, "false", e); }}
              data-testid={`cond-false-handle-${event.event_id}`} />
          </div>
        )}
      </div>
    </div>
  );
}

const NODE_CARD_H = 34;
const NODE_CARD_W = 112;
const NODE_GAP = 8;
const PLACEHOLDER_W = 50;
const PLUS_W = 20;
const DOT_SIZE = 8;
const ATTACH_ALLOWED = new Set(["trigger", "action", "sequence", "condition"]);

function EventNodeArea({ nodes, parentEvent, top, left, onAttachNode, onOpen, onContextMenu, onStartConnect, setRef, selectedIds, unusedNodeSet, visible, isConnecting }) {
  const [dragOver, setDragOver] = useState(false);
  const [insertIdx, setInsertIdx] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selfHover, setSelfHover] = useState(false);
  const rowRef = useRef(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const h = e => {
      if (!pickerRef.current?.contains(e.target)) {
        setPickerOpen(false);
        setSelfHover(false);
      }
    };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [pickerOpen]);

  const SLOT_W = NODE_CARD_W + NODE_GAP;

  const calcInsert = clientX => {
    const rect = rowRef.current?.getBoundingClientRect();
    if (!rect) return nodes.length;
    return Math.max(0, Math.min(nodes.length, Math.round((clientX - rect.left) / SLOT_W)));
  };

  const handleDragOver = e => {
    if (!e.dataTransfer.types.includes("application/x-crescendo-node-type")) return;
    e.preventDefault(); e.stopPropagation();
    setDragOver(true);
    setInsertIdx(calcInsert(e.clientX));
  };

  const handleDrop = e => {
    e.preventDefault(); e.stopPropagation();
    if (isConnecting) { setDragOver(false); return; } // connection in progress — ignore drop
    const type = e.dataTransfer.getData("application/x-crescendo-node-type");
    if (!type) { setDragOver(false); return; }
    const real = type === "blank" ? "action" : type;
    if (!ATTACH_ALLOWED.has(real)) { setDragOver(false); return; }
    onAttachNode(parentEvent, real, insertIdx);
    setDragOver(false);
  };

  const PICKER_TYPES = [
    { type: "trigger",   color: "#FF8A00", label: "Trigger" },
    { type: "action",    color: "#0055FF", label: "Action" },
    { type: "condition", color: "#A855F7", label: "Condition" },
    { type: "sequence",  color: "#14B8A6", label: "Sequence" },
  ];

  const TYPE_LABELS = {
    trigger: "Trigger Type", action: "Action Type", condition: "# Rules",
    sequence: "Sequence", reminder: "Reminder", note: "Note", resource: "Resource",
  };

  const showPlus = visible || selfHover || dragOver || pickerOpen;

  const items = [];
  nodes.forEach((n, i) => {
    if (dragOver && insertIdx === i) items.push({ kind: "ph", key: "ph-" + i });
    items.push({ kind: "node", node: n, key: n.event_id });
  });
  if (dragOver && insertIdx >= nodes.length) items.push({ kind: "ph", key: "ph-end" });

  return (
    <div
      ref={rowRef}
      className="absolute flex items-center"
      style={{
        top,
        left: left || 2,
        height: NODE_CARD_H,
        gap: NODE_GAP,
        zIndex: 26,
        pointerEvents: (nodes.length > 0 || showPlus) ? "auto" : "none",
        overflow: "visible",
      }}
      onDragOver={handleDragOver}
      onDragLeave={e => { if (!rowRef.current?.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={handleDrop}
      onMouseEnter={() => setSelfHover(true)}
      onMouseLeave={() => { if (!pickerOpen) setSelfHover(false); }}
      onPointerLeave={() => { if (!pickerOpen) setSelfHover(false); }}
    >
      {items.map(item => {
        if (item.kind === "ph") {
          return (
            <div key={item.key} className="flex-shrink-0 rounded-sm" style={{
              width: PLACEHOLDER_W, height: NODE_CARD_H,
              background: "#000", border: "1px solid #2A2A2A",
            }}/>
          );
        }
        const n = item.node;
        const color = NODE_TYPE_COLORS[n.type] || "#5A90F5";
        return (
          <div key={n.event_id}
            ref={el => setRef?.(n.event_id, el)}
            className="flex-shrink-0 flex items-center cursor-pointer hover:brightness-110 transition-all relative"
            style={{ width: NODE_CARD_W, height: NODE_CARD_H, background: "#111111", padding: "0 8px", overflow: "visible", borderRadius: "0 0 4px 4px" }}
            onClick={e => { e.stopPropagation(); onOpen?.(n); }}
            onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onContextMenu?.(e, n); }}
            data-event-id={n.event_id}
          >
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold text-white truncate leading-tight">{n.title || n.type}</div>
              <div className="text-[9px] text-[#52525B] truncate leading-tight mt-0.5">{TYPE_LABELS[n.type] || n.type}</div>
            </div>
            {/* Connector dot — outer right edge, same as event handles */}
            <div
              data-conn-handle="right"
              style={{
                position: "absolute",
                right: -(DOT_SIZE / 2),
                top: "50%",
                transform: "translateY(-50%)",
                width: DOT_SIZE,
                height: DOT_SIZE,
                borderRadius: "50%",
                background: color,
                cursor: "crosshair",
                zIndex: 27,
                boxShadow: `0 0 0 1.5px #111111`,
              }}
              onPointerDown={e => {
                e.stopPropagation();
                e.preventDefault();
                e.currentTarget.setPointerCapture(e.pointerId);
                onStartConnect?.(n, "right", e);
              }}
            />
          </div>
        );
      })}

      {showPlus && (
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button
            style={{
              width: PLUS_W, height: NODE_CARD_H,
              background: "#111111",
              borderTop: "none",
              borderRight: "1px solid #2A2A2A",
              borderBottom: "1px solid #2A2A2A",
              borderLeft: nodes.length > 0 ? "none" : "1px solid #2A2A2A",
              borderRadius: "0 0 4px 4px", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
            onPointerDown={e => { e.stopPropagation(); if (isConnecting) e.preventDefault(); }}
            onClick={e => { e.stopPropagation(); if (!isConnecting) setPickerOpen(v => !v); }}
          >
            <svg width="7" height="7" viewBox="0 0 7 7" fill="none">
              <path d="M3.5 1v5M1 3.5h5" stroke="#3A3A3A" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
          {pickerOpen && (
            <div ref={pickerRef} style={{
              position: "absolute", left: 0, bottom: "100%", marginBottom: 4,
              zIndex: 50, background: "#141414", border: "1px solid #2A2A2A",
              borderRadius: 8, boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              overflow: "hidden", padding: "4px 0", width: 128,
            }}>
              {PICKER_TYPES.map(pt => (
                <button key={pt.type}
                  onPointerDown={e => { e.stopPropagation(); e.preventDefault(); onAttachNode(parentEvent, pt.type, nodes.length); setPickerOpen(false); }}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", fontSize: 11, color: "#A1A1AA", background: "none", border: "none", cursor: "pointer", touchAction: "manipulation" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#1a1a1a"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: pt.color, display: "inline-block", flexShrink: 0 }}/>
                  {pt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DayColumn(props) {
  const { isNodeCollapsed, toggleNodeCollapse } = props;
  const [hoveredEventId, setHoveredEventId] = useState(null);
  const { date, events, isToday, showLabel, isSelectedDay, onSelectDay, weekdayTint, overlayEvents = [], colZoom = 1, onEventOpen } = props;
  const columnRef = useRef(null);
  const blankClickTimerRef = useRef(null);
  const [ghost, setGhost] = useState(null);
  const { structStyle } = useBg();

  // Per-day-of-week availability (0=Sun…6=Sat), stored in localStorage
  const dowKey = `crescendo:avail:${date.getDay()}`;
  const [avail, setAvail] = useState(() => { try { return localStorage.getItem(dowKey) || "available"; } catch { return "available"; } });
  const toggleAvail = (e) => {
    e.stopPropagation();
    const next = avail === "available" ? "busy" : "available";
    setAvail(next);
    try { localStorage.setItem(dowKey, next); } catch {}
  };
  const timedEvents = events.filter((e) => !isFloatingType(e.type) && !e.all_day);
  const allDayEvents = events.filter((e) => e.all_day);
  const floatingEvents = events.filter((e) => isFloatingType(e.type));
  // Completion stats for this day
  const taskEvents = events.filter(e => e.type === "task" || e.type === "routine");
  const completedToday = taskEvents.filter(e => !!e.completed_at).length;
  const completionPct = taskEvents.length > 0 ? Math.round((completedToday / taskEvents.length) * 100) : null;
  const layout = useMemo(() => layoutEvents(timedEvents), [timedEvents]);
  // Read-only overlay ribbons (from other workspaces). Placed on the right
  // edge of the column so they don't fight with normal blocks for space.
  // Iter35 fix: zooming below 150% appeared to do nothing because
  // `flex-1` lets the columns expand to fill the parent, ignoring
  // `minWidth` when there's available width. The visual zoom only
  // kicked in once total min-width (7 × 120 × zoom) exceeded the
  // Columns always use flex-1 so they fill the available width equally.
  // The number of visible days (from the parent's visibleDaysCount) determines
  // effective width — more days = narrower columns, fewer = wider.
  const colWidthPx = Math.round(120 * Math.max(colZoom, 1));
  const colSizingClass = "flex-1";
  const colSizingStyle = { minWidth: "80px" };
  const overlayRibbons = overlayEvents.filter((ev) => isSameDay(parseISO(ev.start), date));
  const selectedClass = isSelectedDay ? "selected-col" : "";
  return (
    <div className={`relative border-r border-[#2A2A2A] last:border-r-0 day-col-bg ${colSizingClass} ${selectedClass}`}
      data-weekday-tint={weekdayTint ? "1" : undefined}
      style={{ ...colSizingStyle, ...(weekdayTint ? { "--weekday-tint": weekdayTint } : {}),
               ...(isSelectedDay ? { "--selected-col-bg": "rgba(90,144,245,0.055)" } : {}) }}
      data-testid={`day-col-${format(date, "yyyy-MM-dd")}`}
    >
      {showLabel && (
        <>
        <div
          onClick={(e) => { e.stopPropagation(); onSelectDay?.(date); }}
          className={`sticky top-0 z-40 h-16 flex flex-col items-center justify-center border-b cursor-pointer ${isToday ? "today-header" : ""}`}
          onDoubleClick={(e) => { e.stopPropagation(); props.onDayHeaderDoubleClick?.(date); }}
          data-cresc-col-header
          data-testid={`day-header-${format(date, "yyyy-MM-dd")}`} data-fmt-key={props.dayFmtKey}
          style={{ ...structStyle, background: "var(--cresc-bg, #0F1020)", borderBottomColor: "var(--cresc-border-sub, #1E1E22)" }}
        >
          {(() => {
            const dayStr = format(date, "EEE");
            return (
              <>
                <div style={{ fontSize: 11, color: isToday ? "var(--cresc-accent, #7C6CF8)" : "var(--cresc-text-3, #56565E)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>{dayStr}</div>
                <div style={{ fontSize: 22, color: isToday ? "var(--cresc-text, #F0F0F0)" : "var(--cresc-text-2, #888892)", fontWeight: 700, lineHeight: 1 }}>{format(date, "d")}</div>
              </>
            );
          })()}
        </div>
        {/* Completion progress bar — shown when tasks exist for this day */}
        {completionPct !== null && (
          <div className="h-0.5 w-full bg-[#1a1a1a]" title={`${completedToday}/${taskEvents.length} tasks done (${completionPct}%)`}>
            <div className="h-full transition-all duration-300" style={{ width: `${completionPct}%`, background: completionPct === 100 ? "#00C781" : completionPct > 50 ? "#5A90F5" : "#FF8A00" }} />
          </div>
        )}
        </>
      )}
      {/* All-day strip — events with all_day: true pinned above time grid */}
      {allDayEvents.length > 0 && (
        <div className="border-b border-[#2A2A2A] bg-[#0C0C0C] px-1 py-1 flex flex-col gap-0.5" data-testid="all-day-strip" style={structStyle}>
          {allDayEvents.map(ev => (
            <div
              key={ev.event_id}
              onClick={() => onEventOpen?.(ev)}
              className="text-[10px] font-medium px-2 py-0.5 rounded cursor-pointer truncate hover:opacity-80 transition-opacity"
              style={{ background: ev.color || "#5A90F5", color: "#fff" }}
              title={ev.title}
            >
              {ev.title}
            </div>
          ))}
        </div>
      )}
      <div ref={columnRef} className="relative" style={{ height: 24 * HOUR_HEIGHT }}
        data-day-col={date.toISOString().substring(0,10)}
        onDragOver={(e) => {
          const types = e.dataTransfer?.types;
          const nodeType = types?.includes("application/x-crescendo-node-type");
          const tplType = types?.includes("application/x-crescendo-template-id");
          const seqChildType = types?.includes("application/x-crescendo-seq-child-id");
          const unschedType = types?.includes("application/x-crescendo-unscheduled-id");
          const existingNode = types?.includes("application/x-crescendo-event-id");
          if (!nodeType && !tplType && !seqChildType && !unschedType && !existingNode) return;
          e.preventDefault(); e.dataTransfer.dropEffect = seqChildType ? "move" : "copy";
          e.currentTarget.classList.add("drop-target");
          // Live drop ghost preview position. Respect snapToGrid: when off,
          // honour the user's pixel-perfect cursor position (1-min resolution).
          const rect = e.currentTarget.getBoundingClientRect();
          const y = e.clientY - rect.top;
          const snapMin = props.snapMinutes || 15;
          const rawMinutes = (y / HOUR_HEIGHT) * 60;
          const snapMinutes = props.snapToGrid ? Math.round(rawMinutes / snapMin) * snapMin : Math.round(rawMinutes);
          const top = (snapMinutes / 60) * HOUR_HEIGHT;
          const draggingType = nodeType ? (props._dragType || "task") : "template";
          const isFloat = draggingType === "trigger" || draggingType === "action" || draggingType === "condition" || draggingType === "note";
          const height = isFloat ? 28 : HOUR_HEIGHT; // 1-hour default for timed
          const hh = Math.floor(snapMinutes / 60); const mm = snapMinutes % 60;
          const label = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
          const color = draggingType === "template" ? "#A1A1AA" : (NODE_TYPE_COLORS[draggingType] || "#5A90F5");
          setGhost({ top, height, label, color, type: draggingType });
        }}
        onDragLeave={(e) => { e.currentTarget.classList.remove("drop-target"); setGhost(null); }}
        onDrop={(e) => {
          e.currentTarget.classList.remove("drop-target");
          setGhost(null);
          // ── Cross-day drag: existing event dropped onto a different day ──
          const droppedEventId = e.dataTransfer.getData("application/x-crescendo-event-id");
          if (droppedEventId && !e.dataTransfer.getData("application/x-crescendo-seq-child-id")) {
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const snapMin = props.snapMinutes || 15;
            const minutes = props.snapToGrid ? Math.round((y / HOUR_HEIGHT) * 60 / snapMin) * snapMin : Math.round((y / HOUR_HEIGHT) * 60);
            const newStart = addMinutes(startOfDay(date), minutes);
            props.onCrossDayDrop?.(droppedEventId, newStart);
            return;
          }
          // Drag-out-of-sequence — if a sequence child was dragged onto a
          // day slot, detach it from its parent and reposition (user request).
          const seqChildId = e.dataTransfer.getData("application/x-crescendo-seq-child-id");
          const seqParentId = e.dataTransfer.getData("application/x-crescendo-seq-parent-id");
          if (seqChildId && seqParentId) {
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const snapMin = props.snapMinutes || 15;
            const minutes = props.snapToGrid ? Math.round((y / HOUR_HEIGHT) * 60 / snapMin) * snapMin : Math.round((y / HOUR_HEIGHT) * 60);
            const dt = addMinutes(startOfDay(date), minutes);
            props.onDetachFromSequence?.(seqParentId, seqChildId, dt);
            return;
          }
          const tplId = e.dataTransfer.getData("application/x-crescendo-template-id");
          if (tplId) {
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const snapMin = props.snapMinutes || 15;
            const minutes = props.snapToGrid ? Math.round((y / HOUR_HEIGHT) * 60 / snapMin) * snapMin : Math.round((y / HOUR_HEIGHT) * 60);
            const dt = addMinutes(startOfDay(date), minutes);
            window.dispatchEvent(new CustomEvent("crescendo:instantiate-template-at", { detail: { templateId: tplId, date: dt.toISOString() } }));
            return;
          }
          const unschedId = e.dataTransfer.getData("application/x-crescendo-unscheduled-id");
          if (unschedId) {
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const snapMin = props.snapMinutes || 15;
            const minutes = props.snapToGrid ? Math.round((y / HOUR_HEIGHT) * 60 / snapMin) * snapMin : Math.round((y / HOUR_HEIGHT) * 60);
            const dt = addMinutes(startOfDay(date), minutes);
            const endDt = addMinutes(dt, 60);
            window.dispatchEvent(new CustomEvent("crescendo:schedule-unscheduled", {
              detail: { eventId: unschedId, start: dt.toISOString(), end: endDt.toISOString() }
            }));
            return;
          }
          const t = e.dataTransfer.getData("application/x-crescendo-node-type");
          if (!t) return;
          e.preventDefault();
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const snapMin = props.snapMinutes || 15;
          const minutes = props.snapToGrid ? Math.round((y / HOUR_HEIGHT) * 60 / snapMin) * snapMin : Math.round((y / HOUR_HEIGHT) * 60);
          const dt = addMinutes(startOfDay(date), minutes);
          // Float types drop with pixel coords; timed types snap to time slot only.
          if (isFloatingType(t)) props.onNodeDrop(t, dt, { x: Math.max(0, x - 70), y: Math.max(0, y - 14) });
          else props.onNodeDrop(t, dt);
        }}
      >
        {/* Ghost preview block during drag. */}
        {ghost && (
          <div
            className="absolute left-1 right-1 rounded-md pointer-events-none z-30 border-2 border-dashed"
            style={{
              top: ghost.top,
              height: ghost.height,
              borderColor: ghost.color,
              background: `${ghost.color}22`,
            }}
            data-testid="drag-ghost"
          >
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-medium flex items-center gap-1.5" style={{ color: ghost.color }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: ghost.color }} />
              {ghost.type} · {ghost.label}
            </div>
          </div>
        )}
        {/* Busy-day availability overlay */}
        {avail === "busy" && <div className="absolute inset-0 pointer-events-none z-0" style={{ background: "repeating-linear-gradient(135deg, rgba(239,68,68,0.03) 0px, rgba(239,68,68,0.03) 2px, transparent 2px, transparent 10px)", borderLeft: "2px solid rgba(239,68,68,0.15)" }} />}
        {HOURS.map((h) => (
          <div key={h}
            onClick={(e) => {
              // Single click on empty slot — highlights the column. Double-click
              // still creates an event (handled below), so we don't intercept
              // dblclick here. A tiny delay keeps dblclick working smoothly.
              if (e.detail === 1 && props.onSelectDay) {
                // Defer so a second click (dblclick) cancels this.
                if (blankClickTimerRef.current) window.clearTimeout(blankClickTimerRef.current);
                blankClickTimerRef.current = window.setTimeout(() => {
                  props.onSelectDay?.(date);
                }, 180);
              }
            }}
            onDoubleClick={() => {
              if (blankClickTimerRef.current) window.clearTimeout(blankClickTimerRef.current);
              props.onSlotDoubleClick(addHours(startOfDay(date), h));
            }}
            onContextMenu={(e) => props.onSlotContextMenu?.(e, addHours(startOfDay(date), h).toISOString())}
            className="absolute left-0 right-0 keep-dark-hover cursor-default"
            style={{ top: h * HOUR_HEIGHT, height: HOUR_HEIGHT }}
            data-testid={`slot-${format(date, "yyyy-MM-dd")}-${h}`}
          />
        ))}
          {timedEvents.map((e) => {
            const evStart = new Date(e.start);
            const evEnd = new Date(e.end || e.start);
            const startMin = differenceInMinutes(evStart, startOfDay(evStart));
            const dur = Math.max(25, differenceInMinutes(evEnd, evStart));
            const evTop = (startMin / 60) * HOUR_HEIGHT;
            const evHeight = (dur / 60) * HOUR_HEIGHT;
            const attached = props.attachedByEvent?.get(e.event_id) || [];
            const { col = 0, total = 1 } = layout.get(e.event_id) || {};
            const nodeLeft = `calc(${col * 100 * (1/total)}% + 2px)`;
            return (
              <React.Fragment key={e.event_id}>
                <EventBlock
                  event={e} layout={layout}
                  onOpen={props.onEventOpen} onStartConnect={props.onStartConnect}
                  onAddNote={props.onAddNote} onDeleteNote={props.onDeleteNote}
                  onAddToSequence={props.onAddToSequence}
                  onRemoveFromSequence={props.onRemoveFromSequence}
                  onCreateChildInSequence={props.onCreateChildInSequence}
                  onReorderSequenceChild={props.onReorderSequenceChild}
                  onToggleComplete={props.onToggleComplete}
                  notes={props.notesByEvent.get(e.event_id) || []}
                  setRef={props.setRef} timeFormat={props.timeFormat}
                  onReschedule={props.onReschedule}
                  onResize={props.onResize}
                  selected={props.selectedIds.has(e.event_id)}
                  onSelect={props.onSelect}
                  classColor={props.classColorMap.get(e.event_id)}
                  classBg={props.classBgMap.get(e.event_id)}
                  isDimmed={props.dimmedMap.get(e.event_id)}
                  onContextMenu={props.onContextMenu}
                  snapToGrid={props.snapToGrid}
                  snapMinutes={props.snapMinutes}
                  resolveChild={props.resolveChild}
                  hasOutgoingEdge={props.outgoingMap.get(e.event_id)}
                  isUnused={props.unusedNodeSet?.has(e.event_id)}
                  expandedId={props.expandedId}
                  setExpandedId={props.setExpandedId}
                  isNodeCollapsed={isNodeCollapsed}
                  toggleNodeCollapse={toggleNodeCollapse}
                  onHoverChange={h => setHoveredEventId(h ? e.event_id : null)}
                />
                {!isFloatingType(e.type) && (
                  <EventNodeArea
                    nodes={attached}
                    parentEvent={e}
                    top={evTop + evHeight}
                    left={nodeLeft}
                    onAttachNode={props.onAttachNode}
                    onOpen={props.onEventOpen}
                    onContextMenu={props.onContextMenu}
                    onStartConnect={props.onStartConnect}
                    setRef={props.setRef}
                    selectedIds={props.selectedIds}
                    unusedNodeSet={props.unusedNodeSet}
                    visible={hoveredEventId === e.event_id}
                    isConnecting={props.isConnecting}
                  />
                )}
              </React.Fragment>
            );
          })}
          {/* Overlay ribbons — read-only events from other workspaces shown
              as 4px-wide left-edge stripes at their start time. Hover to see
              workspace + title. Right-aligned so they don't fight with the
              real event blocks. */}
          {overlayRibbons.map((ev) => {
            const start = new Date(ev.start);
            const end = new Date(ev.end || start);
            const startMin = (start.getHours() * 60) + start.getMinutes();
            const dur = Math.max(15, ((end - start) / 60000) || 30);
            const top = (startMin / 60) * HOUR_HEIGHT;
            const h = Math.max(20, (dur / 60) * HOUR_HEIGHT);
            // Render as a minimal chip: name + time, light gray
            return (
              <div
                key={`overlay-${ev._ws_id}-${ev.event_id}`}
                className="absolute left-0 right-1 z-10 rounded pointer-events-none overflow-hidden"
                style={{
                  top, height: h,
                  borderLeft: `2px solid #71717A`,
                  background: "rgba(113,113,122,0.12)",
                  opacity: 0.85,
                }}
                title={`${ev._ws_name} · ${ev.title || ev.type}`}
                data-testid={`overlay-chip-${ev.event_id}`}
              >
                <div className="px-2 py-1 flex items-center gap-1 min-h-0">
                  <div className="text-[10px] font-medium truncate text-[#A1A1AA]">
                    {ev.title || ev.type}
                  </div>
                  {h > 28 && (
                    <div className="text-[9px] text-[#71717A] flex-shrink-0 ml-auto">
                      {start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}–{end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {/* Floating nodes layer — only unattached nodes float freely */}
          {floatingEvents.filter(e => !e.attached_to).map((e) => {
            const startMin = differenceInMinutes(new Date(e.start), startOfDay(date));
            return (
              <FloatingNode
                key={e.event_id} event={e} columnRef={columnRef}
                setRef={props.setRef}
                classColor={props.classColorMap.get(e.event_id)}
                classBg={props.classBgMap.get(e.event_id)}
                isDimmed={props.dimmedMap.get(e.event_id)}
                selected={props.selectedIds.has(e.event_id)}
                onSelect={props.onSelect}
                onOpen={props.onEventOpen}
                onStartConnect={props.onStartConnect}
                onContextMenu={props.onContextMenu}
                onMoveCommit={props.onMoveCommit}
                onDragFrame={props.onDragFrame}
                onAddNote={props.onAddNote}
                notes={props.notesByEvent.get(e.event_id) || []}
                hasOutgoingEdge={props.outgoingMap.get(e.event_id)}
                defaultY={minutesToY(startMin)}
              />
            );
          })}
      </div>
    </div>
  );
}

function TimeGutter({ timeFormat }) {
  const { structStyle } = useBg();
  // Match the day column headers — structStyle gives translucent bg when wallpaper is set,
  // otherwise falls back to the standard near-black.
  const bg = structStyle?.background
    ? structStyle
    : { background: "#0A0A0A" };
  return (
    <div className="w-20 flex-shrink-0 border-r border-[#2A2A2A]" style={bg}>
      {/* Sticky placeholder that sits under the day-column headers */}
      <div className="sticky top-0 z-40 h-12 border-b border-[#2A2A2A]" style={bg} />
      <div className="relative" style={{ height: 24 * HOUR_HEIGHT }}>
        {HOURS.map((h) => {
          const d = new Date(); d.setHours(h, 0, 0, 0);
          return (
            <div key={h} className="absolute left-0 right-0 flex items-start px-2"
              style={{ top: h * HOUR_HEIGHT, height: HOUR_HEIGHT, ...bg }}>
              <span className="text-[10px] text-[#3F3F46] mt-1 flex-1 leading-none select-none">{format(d, timeFormat)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Workflow Canvas — free-form node layout (GHL-style) ──────────────────────
function WorkflowCanvas({ events, edges, current, onOpen, onStartConnect, onContextMenu, onAddNode }) {
  const canvasRef = useRef(null);
  const [pan, setPan] = useState({ x: 40, y: 40 });
  const [scale, setScale] = useState(0.85);
  const [dragging, setDragging] = useState(null);
  const [positions, setPositions] = useState({});
  const panStart = useRef(null);

  useEffect(() => {
    const pos = {};
    const groups = {};
    events.forEach(ev => { if (!groups[ev.type]) groups[ev.type] = []; groups[ev.type].push(ev); });
    const typeOrder = ["trigger","condition","action","task","routine","note","reminder","resource"];
    let colX = 60;
    typeOrder.concat(Object.keys(groups).filter(t => !typeOrder.includes(t))).forEach(type => {
      if (!groups[type]) return;
      groups[type].forEach((ev, i) => { pos[ev.event_id] = { x: ev.position_x ?? colX, y: ev.position_y ?? 60 + i * 110 }; });
      colX += 220;
    });
    setPositions(pos);
  }, [events.map(e => e.event_id).join(",")]);

  const toCanvas = (cx, cy) => {
    const r = canvasRef.current?.getBoundingClientRect() || { left:0, top:0 };
    return { x: (cx - r.left - pan.x) / scale, y: (cy - r.top - pan.y) / scale };
  };
  const onCanvasPointerDown = e => {
    if (!e.target.closest("[data-conn-port]") && !e.target.closest(".absolute.rounded-xl")) {
      panStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };
  const onCanvasPointerMove = e => {
    if (panStart.current) setPan({ x: panStart.current.px + e.clientX - panStart.current.mx, y: panStart.current.py + e.clientY - panStart.current.my });
    if (dragging) { const {x,y} = toCanvas(e.clientX, e.clientY); setPositions(p => ({...p, [dragging.id]: {x:x-dragging.ox, y:y-dragging.oy}})); }
  };
  const onCanvasPointerUp = async e => {
    if (dragging) {
      const pos = positions[dragging.id];
      if (pos && current?.workspace_id) try { await api.put(`/workspaces/${current.workspace_id}/events/${dragging.id}`, { position_x: Math.round(pos.x), position_y: Math.round(pos.y) }); } catch {}
      setDragging(null);
    }
    panStart.current = null;
  };
  const onWheel = e => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      const r = canvasRef.current?.getBoundingClientRect();
      const mx = (e.clientX - r.left - pan.x) / scale, my = (e.clientY - r.top - pan.y) / scale;
      const ns = Math.min(3, Math.max(0.2, scale * (e.deltaY > 0 ? 0.92 : 1.08)));
      setPan({ x: e.clientX - r.left - mx * ns, y: e.clientY - r.top - my * ns }); setScale(ns);
    } else { setPan(p => ({x: p.x - e.deltaX, y: p.y - e.deltaY})); }
  };

  const NW = 180, NH = 64;
  return (
    <div ref={canvasRef} className="flex-1 relative overflow-hidden select-none"
      style={{ background:"#080808", backgroundImage:"radial-gradient(circle, #222 1px, transparent 1px)", backgroundSize:`${28*scale}px ${28*scale}px`, backgroundPosition:`${pan.x%(28*scale)}px ${pan.y%(28*scale)}px` }}
      onPointerDown={onCanvasPointerDown} onPointerMove={onCanvasPointerMove} onPointerUp={onCanvasPointerUp} onWheel={onWheel}>
      <svg className="absolute inset-0 pointer-events-none" style={{width:"100%",height:"100%",overflow:"visible"}}>
        <g transform={`translate(${pan.x},${pan.y}) scale(${scale})`}>
          {edges.map(edge => {
            const sp = positions[edge.source], tp = positions[edge.target];
            if (!sp || !tp) return null;
            const sx=sp.x+NW, sy=sp.y+NH/2, tx=tp.x, ty=tp.y+NH/2, cx=(sx+tx)/2;
            const srcEv = events.find(e=>e.event_id===edge.source);
            const color = NODE_TYPE_COLORS[srcEv?.type]||"#5A90F5";
            return (<g key={edge.edge_id}>
              <path d={`M${sx},${sy} C${cx},${sy} ${cx},${ty} ${tx},${ty}`} fill="none" stroke={color} strokeWidth={2} strokeOpacity={0.5} strokeLinecap="round"/>
              <circle cx={tx} cy={ty} r={4} fill={color} opacity={0.8}/>
              {edge.trigger_label && <text x={cx} y={(sy+ty)/2-6} textAnchor="middle" fontSize={9} fill="#52525B" fontFamily="monospace">{edge.trigger_label}</text>}
            </g>);
          })}
        </g>
      </svg>
      <div style={{transform:`translate(${pan.x}px,${pan.y}px) scale(${scale})`,transformOrigin:"0 0",position:"absolute",inset:0,pointerEvents:"none"}}>
        {events.map(ev => {
          const pos = positions[ev.event_id]; if (!pos) return null;
          const color = NODE_TYPE_COLORS[ev.type]||"#5A90F5";
          return (
            <div key={ev.event_id} className="absolute rounded-xl border"
              style={{left:pos.x,top:pos.y,width:NW,height:NH,background:"#111",borderColor:color+"44",borderWidth:1.5,pointerEvents:"auto"}}
              onPointerDown={e=>{if(e.target.closest("[data-conn-port]"))return;e.stopPropagation();const{x,y}=toCanvas(e.clientX,e.clientY);setDragging({id:ev.event_id,ox:x-pos.x,oy:y-pos.y});e.currentTarget.setPointerCapture(e.pointerId);}}
              onDoubleClick={()=>onOpen(ev)} onContextMenu={e=>{e.stopPropagation();onContextMenu(e,ev);}}>
              <div className="h-1 w-full rounded-t-xl" style={{background:color}}/>
              <div className="px-3 py-1.5 flex flex-col justify-center" style={{height:"calc(100% - 4px)"}}>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{background:color}}/>
                  <div className="text-[11px] font-semibold text-[#EDEDED] truncate">{ev.title||ev.type}</div>
                </div>
                <div className="text-[9px] text-[#52525B] capitalize mt-0.5 ml-3">{ev.type}</div>
              </div>
              <div data-conn-port className="absolute cursor-crosshair"
                style={{right:-8,top:"50%",transform:"translateY(-50%)",width:16,height:16,borderRadius:"50%",background:color,border:"2px solid #111",zIndex:10}}
                onPointerDown={e=>{e.stopPropagation();e.preventDefault();e.currentTarget.setPointerCapture(e.pointerId);onStartConnect(ev,"right",e);}}/>
            </div>
          );
        })}
      </div>
      <div className="absolute bottom-4 left-4 flex gap-1.5">
        <button onClick={()=>setScale(s=>Math.min(3,s*1.2))} className="w-7 h-7 rounded bg-[#141414] border border-[#2A2A2A] text-[#A1A1AA] hover:text-white text-sm flex items-center justify-center">+</button>
        <button onClick={()=>setScale(s=>Math.max(0.2,s*0.8))} className="w-7 h-7 rounded bg-[#141414] border border-[#2A2A2A] text-[#A1A1AA] hover:text-white text-sm flex items-center justify-center">−</button>
        <button onClick={()=>{setPan({x:40,y:40});setScale(0.85);}} className="px-2 h-7 rounded bg-[#141414] border border-[#2A2A2A] text-[10px] text-[#52525B] hover:text-[#A1A1AA]">Reset</button>
        <span className="px-2 h-7 flex items-center text-[10px] text-[#3A3A3A]">{Math.round(scale*100)}%</span>
      </div>
      <div className="absolute bottom-4 right-4 flex flex-col gap-1.5">
        {[["trigger","#FF8A00"],["action","#0055FF"],["condition","#A855F7"],["task","#EF4444"],["note","#A1A1AA"]].map(([type,color])=>(
          <button key={type} onClick={()=>{const cw=canvasRef.current?.clientWidth||800,ch=canvasRef.current?.clientHeight||600;onAddNode(type,(cw/2-pan.x)/scale,(ch/2-pan.y)/scale);}}
            className="px-3 py-1 rounded-lg text-[10px] font-semibold border capitalize"
            style={{background:color+"20",borderColor:color+"50",color}}>+ {type}</button>
        ))}
      </div>
      {events.length===0&&<div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="text-center"><p className="text-[#3A3A3A] text-sm mb-1">Workflow canvas</p><p className="text-[#2A2A2A] text-xs">Add nodes with the buttons →</p></div></div>}
    </div>
  );
}

function MonthView({ date, events, onDayClick, classHighlightMap, weekStartsOn = 1 }) {
  const { structStyle, transparentStyle } = useBg();
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const gridStart = startOfWeek(monthStart, { weekStartsOn });
  const days = [];
  let d = gridStart;
  for (let i = 0; i < 42; i++) { days.push(d); d = addDays(d, 1); }
  // Day-name header order rotates with weekStartsOn so headers always match
  // the columns. Index 0 = first column.
  const DAY_NAMES_FULL = ["SUNDAY","MONDAY","TUESDAY","WEDNESDAY","THURSDAY","FRIDAY","SATURDAY"];
  const headerNames = Array.from({ length: 7 }, (_, i) => DAY_NAMES_FULL[(weekStartsOn + i) % 7]);
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="grid grid-cols-7 border-b border-[#2A2A2A] keep-dark bg-[#0A0A0A]" style={structStyle}>
        {headerNames.map((dn) => (
          <div key={dn} className="meta-label text-center py-3 border-r border-[#2A2A2A] last:border-r-0 keep-dark" style={transparentStyle}>{dn}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 grid-rows-6 flex-1">
        {days.map((day, i) => {
          const inMonth = day >= monthStart && day <= monthEnd;
          const dayEvents = events.filter((e) => isSameDay(parseISO(e.start), day));
          const isToday = isSameDay(day, new Date());
          return (
            <button key={i} onClick={() => onDayClick(day)}
              className={`border-r border-b border-[#2A2A2A] text-left p-2 transition-colors relative overflow-hidden ${!inMonth ? "opacity-40" : ""} ${isToday ? "today-col" : ""} month-day-cell`}
              style={{ background: "rgba(5,5,5,0.97)" }}
              data-testid={`month-day-${format(day, "yyyy-MM-dd")}`}
            >
              <div className={`font-display text-sm mb-1 ${isToday ? "text-[#EDEDED] font-semibold" : "text-[#A1A1AA]"}`}>{format(day, "d")}</div>
              <div className="space-y-0.5">
                {dayEvents.slice(0, 3).map((ev) => {
                  const borderColor = classHighlightMap.get(ev.event_id) || ev.custom_color || NODE_TYPE_COLORS[ev.type];
                  return (
                    <div key={ev.event_id} className="text-[10px] truncate px-1 rounded"
                      style={{
                        borderTop: `2px solid ${borderColor}`,
                        background: "#141414", color: "#EDEDED",
                      }}>
                      {ev.title}
                    </div>
                  );
                })}
                {dayEvents.length > 3 && (<div className="text-[10px] text-[#A1A1AA]">+{dayEvents.length - 3}</div>)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EventDialog({ open, onClose, initial, onSave, onDelete, classes, events = [] }) {
  const { pairs: keyPairs } = useKeyPairs?.() || { pairs: [] };
  const { current: currentWorkspace } = useWorkspace?.() || {};
  const { onKeyDown: shorthandKeyDown } = useShorthandExpansion();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("task");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [customColor, setCustomColor] = useState("");
  const [className, setClassName] = useState("");
  const [triggerAt, setTriggerAt] = useState("");
  const [triggerKind, setTriggerKind] = useState("manual");
  const [triggerCron, setTriggerCron] = useState("");
  const [triggerTarget, setTriggerTarget] = useState("");
  const [triggerKey, setTriggerKey] = useState("");
  const [remindAt, setRemindAt] = useState("");
  const [actionNodeType, setActionNodeType] = useState("task");
  const [actionTitle, setActionTitle] = useState("");
  const [actionCondition, setActionCondition] = useState("always");
  const [routineFreq, setRoutineFreq] = useState("daily");
  const [urgencySens, setUrgencySens] = useState(null);
  // Granular urgency — separate before-start and before-end with custom
  // minutes (user request).
  const [urgencyBeforeStartEnabled, setUrgencyBeforeStartEnabled] = useState(false);
  const [urgencyBeforeStartMinutes, setUrgencyBeforeStartMinutes] = useState(15);
  const [urgencyBeforeEndEnabled, setUrgencyBeforeEndEnabled] = useState(false);
  const [urgencyBeforeEndMinutes, setUrgencyBeforeEndMinutes] = useState(60);
  // Priority dropdown for tasks/routines (user request).
  const [priority, setPriority] = useState(null);
  const [status, setStatus] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState("");
  // Sequence-node fields (children + execution mode).
  const [sequenceMode, setSequenceMode] = useState("sequential");
  // Locked flag — guards this node against accidental delete / drag.
  const [locked, setLocked] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [dependsOn, setDependsOn] = useState([]);
  const [linkedGoalId, setLinkedGoalId] = useState(null);
  const [estimatedMinutes, setEstimatedMinutes] = useState("");
  const [wsMembers, setWsMembers] = useState([]);
  const [smartSuggestion, setSmartSuggestion] = useState(null);
  const [smartLoading, setSmartLoading] = useState(false);
  // Unscheduled — task has no time slot yet, lives in the sidebar panel.
  const [isUnscheduled, setIsUnscheduled] = useState(false);
  // Color/class picker popover state for the chip inside the name box.
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  // Condition logic (user-written expression) + labeled outputs beyond true/false.
  const [conditionLogic, setConditionLogic] = useState("");
  const [conditionOutputs, setConditionOutputs] = useState([]); // [{label, value}]
  // Resource node: URL + kind.
  const [resourceUrl, setResourceUrl] = useState("");
  const [resourceKind, setResourceKind] = useState("link");
  const [focusOnStart, setFocusOnStart] = useState(false);
  const resourceEditorRef = useRef(null);
  // When the drop-picker "Add Resource" is selected, focus the resource section
  useEffect(() => {
    if (!initial?.event_id) return;
    const on = (e) => {
      if (e.detail?.event_id === initial.event_id && resourceEditorRef.current) {
        resourceEditorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        resourceEditorRef.current.querySelector("input,button")?.focus();
      }
    };
    window.addEventListener("crescendo:focus-resource", on);
    return () => window.removeEventListener("crescendo:focus-resource", on);
  }, [initial?.event_id]);
  // Iter35 user request: "We are not able to do anything with tags …
  // lets fix this." Tags can now be assigned to events via this picker
  // in the EventDialog. Backend supports tag_ids on Event since iter32.
  const [tagIds, setTagIds] = useState([]);
  const { tags = [] } = useTags?.() || { tags: [] };
  const { goals = [] } = useGoals?.() || { goals: [] };

  // Action-config form state — seven curated action types (issue: trigger→action engine).
  const [actionKind, setActionKind] = useState("notify");
  const [actionMessage, setActionMessage] = useState("");
  const [actionTarget, setActionTarget] = useState(""); // event_id of target for target-taking actions
  const [actionWebhook, setActionWebhook] = useState("");
  const [actionOffsetMinutes, setActionOffsetMinutes] = useState(60);
  // update_keypair: mutate a specific key-pair when the trigger fires.
  const [actionKeypairKey, setActionKeypairKey] = useState("");
  const [actionKeypairValue, setActionKeypairValue] = useState("");

  const needsTimeRange = type === "task" || type === "routine" || type === "sequence";

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || "");
    setDescription(initial?.description || "");
    // User request: skip the standalone "Pick a type" picker — start with a
    // sensible default and let the user change it via the existing type
    // dropdown inside the form.
    setType(initial?.type || "task");
    setStart(initial?.start ? format(parseISO(initial.start), "yyyy-MM-dd'T'HH:mm") : "");
    setEnd(initial?.end ? format(parseISO(initial.end), "yyyy-MM-dd'T'HH:mm") : "");
    setCustomColor(initial?.custom_color || "");
    setClassName(initial?.class_name || "");
    setTriggerAt(initial?.trigger_at ? format(parseISO(initial.trigger_at), "yyyy-MM-dd'T'HH:mm") : "");
    setRemindAt(initial?.start && initial?.type === "reminder" ? format(parseISO(initial.start), "yyyy-MM-dd'T'HH:mm") : "");
    const cfg = initial?.action_config || {};
    setActionKind(cfg.type || "notify");
    setActionMessage(cfg.message || "");
    setActionTarget(cfg.target_event_id || "");
    setActionWebhook(cfg.webhook || "");
    setActionOffsetMinutes(cfg.offset_minutes || 60);
    setActionKeypairKey(cfg.key || "");
    setActionKeypairValue(cfg.value || "");
    // Legacy (kept for backward compatibility with older saved action nodes).
    setActionNodeType(cfg.node?.type || "task");
    setActionTitle(cfg.node?.title || "");
    setActionCondition(cfg.condition || "always");
    setRoutineFreq(initial?.routine_frequency || "daily");
    setUrgencySens(initial?.urgency_sensitivity_minutes || null);
    setUrgencyBeforeStartEnabled(!!initial?.urgency_before_start_enabled);
    setUrgencyBeforeStartMinutes(initial?.urgency_before_start_minutes ?? 15);
    setUrgencyBeforeEndEnabled(!!initial?.urgency_before_end_enabled);
    setUrgencyBeforeEndMinutes(initial?.urgency_before_end_minutes ?? 60);
    setPriority(initial?.priority || null);
    setStatus(initial?.status || null);
    setSubtasks(Array.isArray(initial?.subtasks) ? initial.subtasks : []);
    setNewSubtask("");
    setSequenceMode(initial?.sequence_mode || "sequential");
    setIsUnscheduled(!!initial?.unscheduled);
    setLocked(!!initial?.locked);
    setDueDate(initial?.due_date ? initial.due_date.slice(0, 10) : "");
    setAssignedTo(initial?.assigned_to || "");
    setDependsOn(Array.isArray(initial?.depends_on) ? initial.depends_on : []);
    // Pre-select the goal this event is linked to (if any)
    const existingGoal = goals.find(g => g.linked_event_ids?.includes(initial?.event_id));
    setLinkedGoalId(existingGoal?.goal_id || null);
    setEstimatedMinutes(initial?.estimated_minutes ? String(initial.estimated_minutes) : "");
    setSmartSuggestion(null);
    if (currentWorkspace?.workspace_id) {
      api.get(`/workspaces/${currentWorkspace.workspace_id}/members`)
        .then(({ data }) => setWsMembers(data || [])).catch(() => {});
    }
    setConditionLogic(initial?.condition_logic || "");
    setConditionOutputs(Array.isArray(initial?.condition_outputs) ? initial.condition_outputs : []);
    setResourceUrl(initial?.resource_url || "");
    setResourceKind(initial?.resource_kind || "link");
    setFocusOnStart(!!initial?.focus_on_start);
    setTagIds(Array.isArray(initial?.tag_ids) ? initial.tag_ids : []);
  }, [open, initial]);

  const submit = () => {
    // Per-type validation — ensure required fields are set.
    if (type !== "note" && !title.trim()) return toast.error("Title required");
    // Iter35 user request: "We need to allow tasks to allow to not have a
    // set end time." Tasks now require start ONLY; end is optional and
    // defaults to start + 30min if blank. Routines + Sequences still
    // require both since they're inherently time-blocked.
    if (type === "task" && !start) return toast.error("Fill start time");
    if ((type === "routine" || type === "sequence") && (!start || !end)) return toast.error("Fill start and end times");
    if (type === "reminder" && !remindAt) return toast.error("Pick a time to occur");
    if (type === "resource" && !resourceUrl.trim()) return toast.error("Resource URL or content required");
    if (type === "action" && (actionKind === "notify" || actionKind === "journal_log" || actionKind === "slack_post") && !actionMessage.trim()) return toast.error("Action message required");
    if (type === "action" && actionKind === "slack_post" && !actionWebhook.trim()) return toast.error("Slack webhook URL required");
    if (type === "action" && actionKind === "update_keypair" && !actionKeypairKey.trim()) return toast.error("Key required for update keypair");
    if (type === "action" && (actionKind === "complete_task" || actionKind === "toggle_pin" || actionKind === "duplicate_tomorrow" || actionKind === "reschedule") && !actionTarget) return toast.error("Target node required");
    if (type === "condition") {
      try {
        const parsed = JSON.parse(conditionLogic || "[]");
        if (Array.isArray(parsed)) {
          for (const r of parsed) { if (!r.key) return toast.error("Each condition rule needs a key"); }
        }
      } catch { return toast.error("Condition logic is not valid JSON"); }
    }
    let startISO, endISO;
    if (isUnscheduled) {
      // Unscheduled tasks use current time as placeholder — the real
      // scheduling happens when the user drags to a time slot.
      const now = new Date().toISOString();
      startISO = now; endISO = now;
    } else if (type === "task") {
      // Default end to start + 30min when user leaves end blank.
      const s = new Date(start);
      const e = end ? new Date(end) : new Date(s.getTime() + 30 * 60 * 1000);
      startISO = s.toISOString();
      endISO = e.toISOString();
    } else if (type === "routine" || type === "sequence") {
      startISO = new Date(start).toISOString();
      endISO = new Date(end).toISOString();
    } else if (type === "reminder") {
      const d = new Date(remindAt);
      startISO = d.toISOString(); endISO = d.toISOString();
    } else if (type === "trigger") {
      const d = triggerAt ? new Date(triggerAt) : new Date();
      startISO = d.toISOString(); endISO = d.toISOString();
    } else {
      const d = initial?.start ? new Date(initial.start) : new Date();
      startISO = d.toISOString(); endISO = d.toISOString();
    }
    const body = {
      title, description, type,
      start: startISO, end: endISO,
      custom_color: className ? null : (customColor || null),
      class_name: className || null,
      urgency_sensitivity_minutes: urgencySens || null,
      urgency_before_start_enabled: urgencyBeforeStartEnabled,
      urgency_before_start_minutes: urgencyBeforeStartEnabled ? (urgencyBeforeStartMinutes || 15) : null,
      urgency_before_end_enabled: urgencyBeforeEndEnabled,
      urgency_before_end_minutes: urgencyBeforeEndEnabled ? (urgencyBeforeEndMinutes || 60) : null,
      priority: priority,
      status: status,
      subtasks: subtasks,
      locked: locked,
      tag_ids: tagIds,
      unscheduled: isUnscheduled,
    };
    if (type === "trigger" && triggerAt) body.trigger_at = new Date(triggerAt).toISOString();
    if (type === "trigger") {
      body.trigger_kind = triggerKind;
      if (triggerCron) body.trigger_cron = triggerCron;
      if (triggerTarget) body.trigger_target = triggerTarget;
      if (triggerKey) body.trigger_key = triggerKey;
    }
    if (type === "routine") body.routine_frequency = routineFreq;
    if (type === "sequence") {
      body.sequence_mode = sequenceMode;
    }
    if (type === "condition") {
      body.condition_logic = conditionLogic || null;
      body.condition_outputs = conditionOutputs.length > 0 ? conditionOutputs : null;
    }
    if (type === "resource") {
      body.resource_url = resourceUrl || null;
      body.resource_kind = resourceKind || "link";
    }
    body.focus_on_start = focusOnStart;
    if (type === "action") {
      body.action_config = {
        type: actionKind,
        message: actionMessage,
        target_event_id: actionTarget || null,
        webhook: actionWebhook || null,
        offset_minutes: Number(actionOffsetMinutes) || 60,
        condition: actionCondition || "always",
        key: actionKeypairKey || null,
        value: actionKeypairValue || null,
      };
    }
    if (dueDate) body.due_date = dueDate;
    if (assignedTo) body.assigned_to = assignedTo;
    if (dependsOn.length) body.depends_on = dependsOn;
    if (estimatedMinutes) body.estimated_minutes = parseInt(estimatedMinutes, 10);
    // Save goal link: update linked_event_ids on the selected goal
    if (initial?.event_id && current?.workspace_id) {
      const prevGoal = goals.find(g => g.linked_event_ids?.includes(initial.event_id));
      if (prevGoal && prevGoal.goal_id !== linkedGoalId) {
        // Remove from old goal
        const updated = (prevGoal.linked_event_ids || []).filter(id => id !== initial.event_id);
        api.put(`/workspaces/${current.workspace_id}/goals/${prevGoal.goal_id}`, { linked_event_ids: updated }).catch(() => {});
      }
      if (linkedGoalId && linkedGoalId !== prevGoal?.goal_id) {
        const goal = goals.find(g => g.goal_id === linkedGoalId);
        if (goal) {
          const updated = [...new Set([...(goal.linked_event_ids || []), initial.event_id])];
          api.put(`/workspaces/${current.workspace_id}/goals/${linkedGoalId}`, { linked_event_ids: updated }).catch(() => {});
        }
      }
    }
    onSave(body);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] max-w-xl max-h-[90vh] overflow-auto"
        onInteractOutside={(e) => {
          // Prevent dialog closing when clicking inside a ThemedSelect portal
          if (e.target instanceof Element && e.target.closest("[data-themed-select-panel]")) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-display">{initial?.event_id ? "Edit" : "New"} Node</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">Configure the node, time, and behavior.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Title box — type as rectangle on the left, round color chip
              right-justified inside name box, lock icon rightmost. */}
          <div className="rounded-md border border-[#2A2A2A] bg-[#0A0A0A] overflow-visible">
            <div className="flex items-stretch relative">
              {/* Color/class chip — round circle on the LEFT (swapped per user). */}
              <div className="relative flex items-center pl-2">
                <button
                  type="button"
                  onClick={() => setColorMenuOpen((v) => !v)}
                  className="w-6 h-6 rounded-full border border-[#3A3A3A] hover:scale-110 transition-transform"
                  style={{ background: className
                    ? (classes.find((c) => c.name === className)?.color || "#3A3A3A")
                    : (customColor || NODE_TYPE_COLORS[type] || "#3A3A3A") }}
                  title="Pick a color or class"
                  data-testid="event-color-chip"
                />
                {colorMenuOpen && (
                  <div
                    className="absolute left-0 top-full mt-1 z-50 w-72 bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl p-2"
                    data-testid="event-color-menu"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="meta-label mb-1.5">Color</div>
                    <div className="grid grid-cols-8 gap-1">
                      {PRESET_COLORS.map((c) => (
                        <button key={c} type="button"
                          onClick={() => { setCustomColor(c); setClassName(""); }}
                          className={`w-6 h-6 rounded-full border-2 ${customColor === c && !className ? "border-[#EDEDED]" : "border-transparent"}`}
                          style={{ background: c }}
                          data-testid={`event-color-${c}`}
                        />
                      ))}
                      <label className="relative w-6 h-6 rounded-full cursor-pointer overflow-hidden border-2 border-dashed border-[#3A3A3A] flex items-center justify-center" title="Custom color">
                        <input type="color" value={customColor || "#0055FF"}
                          onChange={(e) => { setCustomColor(e.target.value); setClassName(""); }}
                          className="absolute inset-0 opacity-0 cursor-pointer" />
                        <Palette className="w-3 h-3 text-[#A1A1AA]" />
                      </label>
                    </div>
                    <div className="h-px bg-[#2A2A2A] my-2" />
                    <div className="meta-label mb-1.5">Class</div>
                    <div className="max-h-40 overflow-y-auto space-y-0.5">
                      <button type="button"
                        onClick={() => { setClassName(""); setColorMenuOpen(false); }}
                        className={`w-full flex items-center gap-2 px-2 py-1 rounded text-xs ${!className ? "bg-[#1a1a1a]" : "hover:bg-[#151515]"}`}
                      >
                        <span className="w-2 h-2 rounded-full border border-[#3A3A3A]" />
                        <span className="text-[#A1A1AA]">(None)</span>
                      </button>
                      {classes.map((c) => (
                        <button key={c.class_id} type="button"
                          onClick={() => { setClassName(c.name); setCustomColor(""); setColorMenuOpen(false); }}
                          className={`w-full flex items-center gap-2 px-2 py-1 rounded text-xs ${className === c.name ? "bg-[#1a1a1a]" : "hover:bg-[#151515]"}`}
                          data-testid={`event-class-opt-${c.class_id}`}
                        >
                          <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                          <span className="truncate flex-1 text-left">{c.name}</span>
                          {c.scope === "global" && <span className="text-[9px] text-[#A1A1AA]">global</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Title / note placeholder */}
              {type !== "note" ? (
                <div className="flex-1 relative min-w-0 flex items-center">
                  <input value={title} onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={e => shorthandKeyDown(e, title, setTitle)}
                    placeholder="Name this node…"
                    className="flex-1 bg-transparent border-none px-3 py-2.5 outline-none text-base min-w-0"
                    data-testid="event-title-input" autoFocus />
                  {!title.trim() && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#FF3B30] text-sm" style={{ left: `calc(${("Name this node…".length * 7.5) + 12}px)` }} title="Required">*</span>
                  )}
                </div>
              ) : (
                <div className="flex-1 px-3 py-2.5" />
              )}
              {/* Type — rectangle picker now on the RIGHT (swapped per user). */}
              <div className="flex items-center pr-2">
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  data-testid="event-type-select"
                  className="appearance-none pl-2.5 pr-6 py-1.5 text-[10px] uppercase tracking-wider rounded-md cursor-pointer border font-medium"
                  style={{
                    color: NODE_TYPE_COLORS[type] || "#EDEDED",
                    borderColor: NODE_TYPE_COLORS[type] || "#3A3A3A",
                    background: `${NODE_TYPE_COLORS[type] || "#3A3A3A"}1A`,
                    backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23A1A1AA' d='M5 6 0 0h10z'/%3E%3C/svg%3E\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 8px center",
                    backgroundSize: "7px",
                  }}
                >
                  {NODE_TYPES.map((t) => (
                    <option key={t} value={t} style={{ background: "#0A0A0A", color: "#EDEDED" }}>{t.toUpperCase()}</option>
                  ))}
                </select>
              </div>
              {/* Lock — right-justified icon-only toggle. */}
              <button
                type="button"
                onClick={() => setLocked((v) => !v)}
                className={`mr-2 my-1.5 w-8 h-8 rounded-md flex items-center justify-center border ${locked ? "text-[#5A90F5] border-[#5A90F5] bg-[#5A90F5]/15" : "text-[#A1A1AA] border-transparent hover:border-[#2A2A2A]"}`}
                title={locked ? "Locked — click to unlock" : "Unlocked — click to lock"}
                data-testid="event-lock-toggle"
              >
                {locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          {/* Unscheduled toggle — hides time fields, task goes to sidebar panel */}
          {(type === "task" || type === "routine") && (
            <label className="flex items-center gap-2 cursor-pointer text-xs text-[#A1A1AA] hover:text-[#EDEDED]">
              <input type="checkbox" checked={isUnscheduled} onChange={e => setIsUnscheduled(e.target.checked)} className="w-3.5 h-3.5" />
              <span>Unscheduled — no time slot yet</span>
            </label>
          )}
          <div className="grid grid-cols-2 gap-3" style={{ display: needsTimeRange && !isUnscheduled ? "grid" : "none" }}>
            <div>
              <label className="meta-label block mb-1">START</label>
              <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-full px-3 py-1.5 outline-none focus:border-[#555] text-xs [color-scheme:dark]"
                data-testid="event-start-input" />
            </div>
            <div>
              <label className="meta-label block mb-1">END</label>
              <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-full px-3 py-1.5 outline-none focus:border-[#555] text-xs [color-scheme:dark]"
                data-testid="event-end-input" />
            </div>
            {(type === "task" || type === "routine") && (
              <div>
                <label className="meta-label block mb-1">EST. DURATION <span className="text-[#3A3A3A] font-normal normal-case">(minutes)</span></label>
                <input type="number" min="1" max="480" value={estimatedMinutes} onChange={e => setEstimatedMinutes(e.target.value)}
                  placeholder="e.g. 30"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-full px-3 py-1.5 outline-none focus:border-[#555] text-xs"
                />
              </div>
            )}
          </div>
          {type === "reminder" && (
            <div>
              <label className="meta-label block mb-1">TIME TO OCCUR</label>
              <input type="datetime-local" value={remindAt} onChange={(e) => setRemindAt(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-full px-3 py-1.5 outline-none focus:border-[#555] text-xs [color-scheme:dark]"
                data-testid="reminder-at-input" />
            </div>
          )}
          {type === "routine" && (
            <div>
              <label className="meta-label block mb-1">FREQUENCY</label>
              <select value={routineFreq} onChange={(e) => setRoutineFreq(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
                data-testid="routine-freq-select"
              >
                <option value="daily">Daily</option>
                <option value="weekdays">Weekdays (Mon–Fri)</option>
                <option value="weekends">Weekends</option>
                <option value="weekly">Weekly (same day)</option>
                <option value="monthly">Monthly (same date)</option>
                <option value="none">No repeat</option>
              </select>
              <div className="text-[10px] text-[#A1A1AA] mt-1">Routines render at each frequency point on the calendar.</div>
            </div>
          )}
          {type === "sequence" && (
            <div>
              <label className="meta-label block mb-1">EXECUTION MODE</label>
              <div className="flex gap-2">
                {["sequential", "parallel"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setSequenceMode(m)}
                    className={`flex-1 px-3 py-2 rounded-md text-sm capitalize transition-colors ${sequenceMode === m ? "bg-[#0055FF] text-white" : "bg-[#0A0A0A] border border-[#2A2A2A] hover:border-[#3A3A3A] text-[#EDEDED]"}`}
                    data-testid={`sequence-mode-${m}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-[#A1A1AA] mt-1">
                {sequenceMode === "sequential" ? "Children run one after another." : "Children run at the same time."}
              </div>
            </div>
          )}
          {/* Class + color moved into the chip inside the name box. Keeping
              hidden legacy selects here for backward-compat test selectors. */}
          <div className="hidden">
            <select value={className} onChange={(e) => setClassName(e.target.value)}
              data-testid="event-class-select"
            >
              <option value="">(none)</option>
              {classes.map((c) => <option key={c.class_id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          {needsTimeRange && (
            <div>
              <label className="meta-label block mb-1">URGENCY</label>
              <div className="grid grid-cols-2 gap-2">
                <label className={`flex items-center gap-2 px-2.5 py-2 rounded-md border cursor-pointer transition-colors ${urgencyBeforeStartEnabled ? "border-[#5A90F5] bg-[#5A90F5]/10" : "border-[#2A2A2A] hover:border-[#3A3A3A]"}`}>
                  <input type="checkbox" checked={!!urgencyBeforeStartEnabled}
                    onChange={(e) => setUrgencyBeforeStartEnabled(e.target.checked)}
                    className="accent-[#5A90F5]"
                    data-testid="urgency-before-start-toggle"
                  />
                  <div className="flex-1">
                    <div className="text-[11px] text-[#EDEDED] leading-none mb-1">Before start</div>
                    <input type="number" value={urgencyBeforeStartMinutes || ""}
                      onChange={(e) => setUrgencyBeforeStartMinutes(e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="min"
                      disabled={!urgencyBeforeStartEnabled}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[11px] outline-none focus:border-[#5A90F5] disabled:opacity-40"
                      data-testid="urgency-before-start-minutes"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </label>
                <label className={`flex items-center gap-2 px-2.5 py-2 rounded-md border cursor-pointer transition-colors ${urgencyBeforeEndEnabled ? "border-[#FF8A00] bg-[#FF8A00]/10" : "border-[#2A2A2A] hover:border-[#3A3A3A]"}`}>
                  <input type="checkbox" checked={!!urgencyBeforeEndEnabled}
                    onChange={(e) => setUrgencyBeforeEndEnabled(e.target.checked)}
                    className="accent-[#FF8A00]"
                    data-testid="urgency-before-end-toggle"
                  />
                  <div className="flex-1">
                    <div className="text-[11px] text-[#EDEDED] leading-none mb-1">Before end</div>
                    <input type="number" value={urgencyBeforeEndMinutes || ""}
                      onChange={(e) => setUrgencyBeforeEndMinutes(e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="min"
                      disabled={!urgencyBeforeEndEnabled}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[11px] outline-none focus:border-[#FF8A00] disabled:opacity-40"
                      data-testid="urgency-before-end-minutes"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </label>
              </div>
            </div>
          )}
          {(type === "task" || type === "routine") && (
            <div>
              <label className="meta-label block mb-1">PRIORITY</label>
              <ThemedSelect
                value={priority || "none"}
                onChange={(v) => setPriority(v === "none" ? null : v)}
                options={[
                  { value: "none", label: "None" },
                  { value: "low", label: "Low" },
                  { value: "medium", label: "Medium" },
                  { value: "high", label: "High" },
                  { value: "urgent", label: "Urgent" },
                ]}
                className="w-full"
                data-testid="priority-select"
              />
            </div>
          )}
          {(type === "task" || type === "routine") && (() => {
            const DEFAULT_STATUSES = [
              { id: "not_started", label: "Not Started", color: "#71717A" },
              { id: "in_progress", label: "In Progress", color: "#5A90F5" },
              { id: "blocked",     label: "Blocked",     color: "#EF4444" },
              { id: "done",        label: "Done",         color: "#00C781" },
            ];
            const wsStatuses = currentWorkspace?.custom_statuses?.length
              ? currentWorkspace.custom_statuses
              : DEFAULT_STATUSES;
            const allStatuses = [
              ...DEFAULT_STATUSES,
              ...(currentWorkspace?.custom_statuses || []).filter(s => !DEFAULT_STATUSES.find(d => d.id === s.id)),
            ];
            const statusOptions = [
              { value: "none", label: "No Status" },
              ...allStatuses.map(s => ({ value: s.id, label: s.label })),
            ];
            const activeStatus = allStatuses.find(s => s.id === status);
            return (
              <div>
                <label className="meta-label block mb-1">STATUS</label>
                <div className="flex items-center gap-2">
                  {activeStatus && (
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: activeStatus.color }} />
                  )}
                  <ThemedSelect
                    value={status || "none"}
                    onChange={(v) => setStatus(v === "none" ? null : v)}
                    options={statusOptions}
                    className="flex-1"
                    data-testid="status-select"
                  />
                </div>
                {/* Active subtask chooser — shown when in_progress and subtasks exist */}
                {(status === "in_progress" || status === "active") && subtasks.length > 0 && (
                  <div className="mt-2">
                    <label className="meta-label block mb-1">WORKING ON</label>
                    <div className="space-y-1">
                      {subtasks.filter(st => !st.done).map((st, idx) => (
                        <label key={st.id || idx} className="flex items-center gap-2 cursor-pointer px-2 py-1 rounded hover:bg-[#0A0A0A]">
                          <input
                            type="radio"
                            name="active-subtask"
                            checked={!!st.active}
                            onChange={() => setSubtasks(subtasks.map((x, i) => ({ ...x, active: x === st })))}
                            className="accent-[#5A90F5]"
                          />
                          <span className="text-xs text-[#EDEDED]">{st.text}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {/* Goal assignment */}
          {goals.length > 0 && (type === "task" || type === "routine") && (
            <div>
              <label className="meta-label block mb-1">GOAL</label>
              <select
                value={linkedGoalId || ""}
                onChange={e => setLinkedGoalId(e.target.value || null)}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs"
              >
                <option value="">No goal</option>
                {goals.filter(g => g.status !== "completed").map(g => (
                  <option key={g.goal_id} value={g.goal_id}>{g.title}</option>
                ))}
              </select>
            </div>
          )}
          {/* Iter35 user request: tags can now be assigned to events. */}
          {tags.length > 0 && (
            <div>
              <label className="meta-label block mb-1">TAGS</label>
              <div className="flex flex-wrap gap-1.5" data-testid="event-tag-picker">
                {tags.map((t) => {
                  const on = tagIds.includes(t.tag_id);
                  return (
                    <button
                      type="button"
                      key={t.tag_id}
                      onClick={() => setTagIds((cur) => on ? cur.filter((x) => x !== t.tag_id) : [...cur, t.tag_id])}
                      className={`px-2 py-1 rounded-full text-[10px] flex items-center gap-1.5 border transition-colors ${on ? "border-[#5A90F5] bg-[#5A90F5]/15 text-[#EDEDED]" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#3A3A3A]"}`}
                      data-testid={`event-tag-${t.tag_id}`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: t.color || "#5A90F5" }} />
                      {t.name}
                      {on && <Check className="w-3 h-3" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <label className="meta-label block mb-1">{type === "reminder" || type === "note" ? "MESSAGE" : "DESCRIPTION"}</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              placeholder={type === "reminder" ? "Shown when the reminder fires" : type === "note" ? "Your note text..." : ""}
              autoFocus={type === "note"}
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-sm" />
          </div>

          {/* Dependency — tasks that must complete before this one unlocks */}
          {(type === "task" || type === "routine") && (
            <div>
              <label className="meta-label block mb-1">DEPENDS ON <span className="text-[#3A3A3A] normal-case font-normal">(blocks until complete)</span></label>
              <div className="flex flex-wrap gap-1 mb-1">
                {dependsOn.map(depId => {
                  const depEv = events.find(e => e.event_id === depId);
                  return depEv ? (
                    <span key={depId} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1a1a1a] border border-[#2A2A2A] text-[10px] text-[#A1A1AA]">
                      {depEv.completed_at ? "✓ " : ""}{depEv.title || "Untitled"}
                      <button onClick={() => setDependsOn(d => d.filter(x => x !== depId))} className="text-[#52525B] hover:text-red-400 ml-0.5">✕</button>
                    </span>
                  ) : null;
                })}
              </div>
              <select
                onChange={e => { const v = e.target.value; if (v && !dependsOn.includes(v)) setDependsOn(d => [...d, v]); e.target.value = ""; }}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#A1A1AA] outline-none focus:border-[#555]"
                defaultValue=""
              >
                <option value="">+ Add dependency…</option>
                {events.filter(e => e.type === "task" && e.event_id !== initial?.event_id && !dependsOn.includes(e.event_id)).map(e => (
                  <option key={e.event_id} value={e.event_id}>{e.title || "Untitled"}</option>
                ))}
              </select>
            </div>
          )}

          {/* Subtasks (for task/routine) */}
          {(type === "task" || type === "routine") && (
            <div>
              <label className="meta-label block mb-1">SUBTASKS</label>
              <div className="space-y-1">
                {subtasks.map((st, idx) => (
                  <div key={st.id || idx} className="rounded bg-[#0A0A0A] border border-[#2A2A2A]" data-testid={`subtask-${idx}`}>
                    <div className="flex items-center gap-2 px-2 py-1">
                      <input type="checkbox" checked={!!st.done}
                        onChange={(e) => setSubtasks(subtasks.map((x, i) => i === idx ? { ...x, done: e.target.checked } : x))}
                        className="accent-[#00C781]" />
                      <input value={st.text} onChange={(e) => setSubtasks(subtasks.map((x, i) => i === idx ? { ...x, text: e.target.value } : x))}
                        className={`flex-1 bg-transparent border-none outline-none text-sm ${st.done ? "line-through text-[#A1A1AA]" : ""}`} />
                      <button type="button" onClick={() => setSubtasks(subtasks.filter((_, i) => i !== idx))}
                        className="p-0.5 text-[#FF3B30]"><X className="w-3 h-3" /></button>
                    </div>
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 px-2 pb-1.5">
                        {tags.map(t => {
                          const stTags = st.tag_ids || [];
                          const on = stTags.includes(t.tag_id);
                          return (
                            <button key={t.tag_id} type="button"
                              onClick={() => setSubtasks(subtasks.map((x, i) => i === idx ? { ...x, tag_ids: on ? (x.tag_ids||[]).filter(id=>id!==t.tag_id) : [...(x.tag_ids||[]), t.tag_id] } : x))}
                              className={`px-1.5 py-0.5 rounded-full text-[9px] flex items-center gap-1 border transition-colors ${on ? "border-[#5A90F5] bg-[#5A90F5]/15 text-[#EDEDED]" : "border-[#1E1E1E] text-[#52525B] hover:border-[#2A2A2A]"}`}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color || "#5A90F5" }} />
                              {t.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); if (newSubtask.trim()) { setSubtasks([...subtasks, { id: `st_${Date.now()}`, text: newSubtask.trim(), done: false }]); setNewSubtask(""); } }} className="flex gap-2 mt-1">
                <input value={newSubtask} onChange={(e) => setNewSubtask(e.target.value)} placeholder="Subtask"
                  className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-1.5 outline-none focus:border-[#555] text-sm"
                  data-testid="subtask-input" />
                <button
                  type="submit"
                  aria-label="Add subtask"
                  className="flex items-center justify-center w-8 h-8 rounded-md bg-[#1a1a1a] border border-[#2A2A2A] hover:border-[#5A90F5] hover:text-[#5A90F5] text-[#A1A1AA] transition-colors"
                  data-testid="subtask-add-btn"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </form>
            </div>
          )}

          {type === "trigger" && (
            <div className="border border-[#FF8A00]/40 bg-[#FF8A00]/5 rounded-md p-3 space-y-2">
              <div className="meta-label" style={{ color: "#FF8A00" }}>TRIGGER STIMULUS</div>
              <select value={triggerKind} onChange={(e) => setTriggerKind(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs"
                data-testid="trigger-kind-select"
              >
                <option value="manual">Manual (run-now only)</option>
                <option value="time">At a specific time</option>
                <option value="recurring">Recurring schedule (cron)</option>
                <option value="webhook">Inbound webhook</option>
                <option value="on_complete">When a task is completed</option>
                <option value="keypair_change">When a key pair changes</option>
              </select>
              {triggerKind === "time" && (
                <input type="datetime-local" value={triggerAt} onChange={(e) => setTriggerAt(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] [color-scheme:dark]"
                  data-testid="trigger-at-input" />
              )}
              {triggerKind === "recurring" && (
                <input value={triggerCron} onChange={(e) => setTriggerCron(e.target.value)}
                  placeholder="0 9 * * 1-5  (9am weekdays)"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs font-mono"
                  data-testid="trigger-cron-input" />
              )}
              {triggerKind === "webhook" && initial?.event_id && (
                <div className="text-[11px] text-[#A1A1AA]">POST to this URL to fire:
                  <code className="block mt-1 px-2 py-1 rounded bg-[#0A0A0A] border border-[#2A2A2A] font-mono text-[10px] break-all">
                    {(process.env.REACT_APP_BACKEND_URL || "") + "/api/webhooks/trigger/" + initial.event_id}
                  </code>
                </div>
              )}
              {triggerKind === "on_complete" && (
                <select value={triggerTarget} onChange={(e) => setTriggerTarget(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs"
                  data-testid="trigger-target-select"
                >
                  <option value="">(select a task)</option>
                  {events.filter((ev) => ev.type === "task").map((ev) => (
                    <option key={ev.event_id} value={ev.event_id}>{ev.title || ev.event_id}</option>
                  ))}
                </select>
              )}
              {triggerKind === "keypair_change" && (
                <select value={triggerKey} onChange={(e) => setTriggerKey(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs font-mono"
                >
                  <option value="">(select a key pair)</option>
                  {(keyPairs || []).map((p) => (
                    <option key={p.kp_id} value={p.key}>{p.key}</option>
                  ))}
                </select>
              )}
              <div className="text-[11px] text-[#A1A1AA]">Connect this trigger to an action node.</div>
            </div>
          )}
          {type === "action" && (
            <div className="border border-[#0055FF]/40 bg-[#0055FF]/5 rounded-md p-3 space-y-3">
              <div className="meta-label" style={{ color: "#0055FF" }}>ACTION — what fires when the upstream trigger hits</div>
              <div>
                <label className="meta-label block mb-1">KIND</label>
                <select value={actionKind} onChange={(e) => setActionKind(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
                  data-testid="action-kind-select"
                >
                  <option value="notify">Send a notification</option>
                  <option value="complete_task">Mark task complete</option>
                  <option value="toggle_pin">Pin / unpin node</option>
                  <option value="duplicate_tomorrow">Duplicate node into tomorrow</option>
                  <option value="journal_log">Log a journal note</option>
                  <option value="slack_post">Post to Slack webhook</option>
                  <option value="reschedule">Reschedule node</option>
                  <option value="update_keypair">Update key pair</option>
                  <option value="create_node">Create a new node</option>
                  <option value="archive">Archive node</option>
                  <option value="open_url">Open a URL</option>
                  <option value="send_email">Send email (via provider)</option>
                  <option value="webhook_out">POST to external webhook</option>
                </select>
              </div>
              {(actionKind === "notify" || actionKind === "journal_log" || actionKind === "slack_post") && (
                <div>
                  <label className="meta-label block mb-1">MESSAGE</label>
                  <input value={actionMessage} onChange={(e) => setActionMessage(e.target.value)} placeholder="What this action should say / log — use {{key}} to reference key pairs"
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
                    data-testid="action-message-input" />
                  <KeyPairTagRow onInsert={(k) => setActionMessage((v) => `${v}${v && !v.endsWith(" ") ? " " : ""}{{${k}}}`)} />
                </div>
              )}
              {(actionKind === "complete_task" || actionKind === "toggle_pin" || actionKind === "duplicate_tomorrow" || actionKind === "reschedule") && (
                <div>
                  <label className="meta-label block mb-1">TARGET EVENT ID</label>
                  <input value={actionTarget} onChange={(e) => setActionTarget(e.target.value)} placeholder="evt_… (pick the node this action touches)"
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] font-mono text-xs"
                    data-testid="action-target-input" />
                  <div className="text-[10px] text-[#A1A1AA] mt-1">
                    Tip: right-click a node on the calendar → Copy ID (coming soon), or paste any event_id here.
                  </div>
                </div>
              )}
              {actionKind === "slack_post" && (
                <div>
                  <label className="meta-label block mb-1">SLACK WEBHOOK URL</label>
                  <input value={actionWebhook} onChange={(e) => setActionWebhook(e.target.value)} placeholder="https://hooks.slack.com/services/..."
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs font-mono"
                    data-testid="action-webhook-input" />
                </div>
              )}
              {actionKind === "reschedule" && (
                <div>
                  <label className="meta-label block mb-1">OFFSET (MINUTES)</label>
                  <input type="number" value={actionOffsetMinutes} onChange={(e) => setActionOffsetMinutes(parseInt(e.target.value) || 0)}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
                    data-testid="action-offset-input" />
                  <div className="text-[10px] text-[#A1A1AA] mt-1">Positive = later; negative = earlier.</div>
                </div>
              )}
              {actionKind === "update_keypair" && (
                <div className="grid grid-cols-2 gap-2" data-testid="action-keypair-fields">
                  <div>
                    <label className="meta-label block mb-1">KEY</label>
                    <input
                      list="keypair-keys-list"
                      value={actionKeypairKey}
                      onChange={(e) => setActionKeypairKey(e.target.value)}
                      placeholder="key name (new or existing)"
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs font-mono"
                      data-testid="action-keypair-key"
                    />
                    <datalist id="keypair-keys-list">
                      {(keyPairs || []).map((p) => (
                        <option key={p.kp_id} value={p.key} />
                      ))}
                    </datalist>
                    <div className="text-[10px] text-[#A1A1AA] mt-1">Type a new key or pick existing. Creates it if needed.</div>
                  </div>
                  <div>
                    <label className="meta-label block mb-1">NEW VALUE</label>
                    <input
                      value={actionKeypairValue}
                      onChange={(e) => setActionKeypairValue(e.target.value)}
                      placeholder="new value (supports {{otherKey}})"
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs font-mono"
                      data-testid="action-keypair-value"
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="meta-label block mb-1">CONDITION (OPTIONAL)</label>
                <input value={actionCondition} onChange={(e) => setActionCondition(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs font-mono" />
                <div className="text-[10px] text-[#A1A1AA] mt-1">Use "always" to always run.</div>
              </div>
            </div>
          )}
          {type === "condition" && (
            <ConditionBuilder
              logic={conditionLogic}
              setLogic={setConditionLogic}
              outputs={conditionOutputs}
              setOutputs={setConditionOutputs}
            />
          )}
          {type === "resource" && (
            <div ref={resourceEditorRef}>
            <ResourceEditor
              resourceKind={resourceKind}
              setResourceKind={setResourceKind}
              resourceUrl={resourceUrl}
              setResourceUrl={setResourceUrl}
            />
            </div>
          )}
          {/* Focus Mode toggle — per-node. When on, the app enters Focus Mode
              as soon as this node's scheduled start time arrives. */}
          {(type === "task" || type === "routine") && (
            <label className="flex items-center gap-3 px-3 py-2 rounded-md border border-[#2A2A2A] bg-[#0A0A0A] cursor-pointer hover:border-[#3A3A3A]" data-testid="focus-on-start-row">
              <input type="checkbox" checked={focusOnStart} onChange={(e) => setFocusOnStart(e.target.checked)} data-testid="focus-on-start-toggle" />
              <span className="text-xs text-[#EDEDED]">Enable Focus Mode when this task begins</span>
              <span className="ml-auto text-[10px] text-[#A1A1AA]">Dims everything else while active</span>
            </label>
          )}
        </div>
        <DialogFooter className="gap-2">
          {initial?.event_id && (
            <button
              onClick={() => {
                if (initial.locked) return toast.error("Node is locked — unlock first");
                onDelete(initial.event_id);
              }}
              className={`px-3 py-2 text-sm rounded-md ${initial?.locked ? "text-[#A1A1AA] cursor-not-allowed" : "text-[#FF3B30] hover:bg-[#FF3B30]/10"}`}
              data-testid="event-delete-btn"
            >
              {initial?.locked ? "Locked" : "Delete"}
            </button>
          )}
          <button onClick={submit} className="bg-[#EDEDED] text-[#0A0A0A] px-4 py-2 rounded-md font-medium hover:bg-white" data-testid="event-save-btn">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CalendarHistoryTabs({ snapshots, restoreSnapshot, workspaceId }) {
  const [histTab, setHistTab] = useState("versions");
  const [trash, setTrash] = useState({ docs: [], events: [], loading: true });

  useEffect(() => {
    if (histTab !== "deleted") return;
    setTrash(t => ({ ...t, loading: true }));
    Promise.all([
      api.get("/documents/trash").then(({ data }) => data || []).catch(() => []),
      workspaceId
        ? api.get(`/workspaces/${workspaceId}/events/trash`).then(({ data }) => data || []).catch(() => [])
        : Promise.resolve([]),
    ]).then(([docs, events]) => setTrash({ docs, events, loading: false }));
  }, [histTab, workspaceId]);

  return (
    <>
      <div className="flex gap-4 border-b border-[#2A2A2A] -mt-1 mb-3">
        <button onClick={() => setHistTab("versions")} className={`pb-2 text-xs font-medium border-b-2 -mb-px ${histTab === "versions" ? "border-[#5A90F5] text-[#EDEDED]" : "border-transparent text-[#A1A1AA] hover:text-[#EDEDED]"}`}>Version History</button>
        <button onClick={() => setHistTab("deleted")} className={`pb-2 text-xs font-medium border-b-2 -mb-px ${histTab === "deleted" ? "border-[#5A90F5] text-[#EDEDED]" : "border-transparent text-[#A1A1AA] hover:text-[#EDEDED]"}`}>Recently Deleted</button>
      </div>
      {histTab === "versions" && (
        <>
          <div className="text-xs text-[#A1A1AA] mb-2">{snapshots.length} snapshot{snapshots.length === 1 ? "" : "s"} · auto-saved as you work</div>
          <div className="max-h-72 overflow-auto space-y-1">
            {snapshots.length === 0 && <div className="text-xs text-[#A1A1AA] py-4 text-center">No snapshots yet.</div>}
            {snapshots.map((s) => (
              <div key={s.snapshot_id} className="flex items-center gap-2 px-3 py-2 rounded border border-[#2A2A2A] bg-[#0A0A0A]">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{s.label || format(new Date(s.created_at), "PPpp")}</div>
                  <div className="text-[10px] text-[#A1A1AA]">{format(new Date(s.created_at), "PPpp")}</div>
                </div>
                <button onClick={() => restoreSnapshot(s.snapshot_id)} className="text-[10px] px-2 py-1 rounded bg-[#1a1a1a] hover:bg-[#2a2a2a]">Restore</button>
              </div>
            ))}
          </div>
        </>
      )}
      {histTab === "deleted" && (
        <div className="max-h-72 overflow-auto space-y-2">
          {trash.loading && <div className="text-xs text-[#A1A1AA] py-4 text-center">Loading…</div>}
          {!trash.loading && !trash.events.length && !trash.docs.length && (
            <div className="text-xs text-[#A1A1AA] py-4 text-center">Recently deleted is empty.</div>
          )}
          {!trash.loading && trash.events.map(ev => (
            <div key={ev.event_id} className="flex items-center gap-3 px-3 py-2 rounded border border-[#2A2A2A] bg-[#0A0A0A]">
              <div className="w-2 h-2 rounded-full flex-shrink-0 bg-[#5A90F5]" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[#EDEDED] truncate">{ev.title || "Untitled node"}</div>
                <div className="text-[10px] text-[#52525B]">Node · {ev.deleted_at ? new Date(ev.deleted_at).toLocaleString() : ""}</div>
              </div>
              <button onClick={async () => { try { await api.put(`/workspaces/${ev.workspace_id}/events/${ev.event_id}`, { deleted: false }); setTrash(t => ({ ...t, events: t.events.filter(x => x.event_id !== ev.event_id) })); } catch {} }} className="text-[10px] px-2 py-1 rounded bg-[#1a1a1a] hover:bg-[#2a2a2a]">Restore</button>
            </div>
          ))}
          {!trash.loading && trash.docs.map(d => (
            <div key={d.doc_id} className="flex items-center gap-3 px-3 py-2 rounded border border-[#2A2A2A] bg-[#0A0A0A]">
              <div className="w-2 h-2 rounded-sm flex-shrink-0 bg-[#A1A1AA]" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[#EDEDED] truncate">{d.name || "Untitled"}</div>
                <div className="text-[10px] text-[#52525B]">Document · {d.deleted_at ? new Date(d.deleted_at).toLocaleString() : ""}</div>
              </div>
              <button onClick={async () => { try { await api.post(`/documents/${d.doc_id}/restore`); setTrash(t => ({ ...t, docs: t.docs.filter(x => x.doc_id !== d.doc_id) })); } catch {} }} className="text-[10px] px-2 py-1 rounded bg-[#1a1a1a] hover:bg-[#2a2a2a]">Restore</button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function CalendarView() {
  const routerNavigate = useRouterNavigate();
  const { current, workspaces, setCurrent, setWorkspaces } = useWorkspace();
  const { mainPanelStyle, topbarStyle, structStyle, transparentStyle } = useBg();
  const { timeFormat, snapToGrid, setSnapToGrid, snapMinutes, setSnapMinutes, weekdayTints, weekStartsOn, weekHours = 40 } = useSettings();
  const [showHeatmap, setShowHeatmap] = useState(() => { try { return localStorage.getItem("crescendo:showHeatmap") === "1"; } catch { return false; } });
  // Iter35 user request: "Paid features (especially nodes) should have
  // icons next to them to indicate that they are paid." Used in the
  // slot context menu's Add-Node submenu to render a Sparkles next to
  // gated node types.
  const ent = useEntitlements();
  // Iter37: import-from-Google/Outlook modal toggle.
  const [importOpen, setImportOpen] = useState(false);
  // Free-tier limits: triggers / actions / conditions / sequences are
  // automation primitives that gate behind Pro+. Mirrors backend gate
  // logic in `entitlements.py` (action_node, etc.).
  const isProGatedNode = (t) => {
    if (!ent || ent.is_dev) return false;
    const plan = ent.plan || "free";
    if (plan === "pro" || plan === "business") return false;
    return ["trigger", "action", "condition", "sequence"].includes(t);
  };
  // Map date → tint hex (or null) based on the user's weekday palette.
  const WEEKDAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const tintFor = (date) => {
    const day = WEEKDAY_KEYS[date.getDay()];
    const baseTint = weekdayTints?.[day] || null;
    if (!showHeatmap) return baseTint;
    // Heatmap: count tasks on this day, map to orange intensity
    const dayStart = startOfDay(date);
    const dayEnd = addDays(dayStart, 1);
    const count = events.filter(e => {
      const s = new Date(e.start);
      return s >= dayStart && s < dayEnd && (e.type === "task" || e.type === "routine");
    }).length;
    if (count === 0) return baseTint;
    const intensity = Math.min(count / 8, 1); // cap at 8 tasks = full
    const heatColor = `rgba(255,${Math.round(140 - intensity*80)},0,${(intensity * 0.18).toFixed(2)})`;
    return baseTint || heatColor;
  };
  const { classes, activeClassId, setActiveClassId } = useClasses();
  const { folders } = useFolders();
  const [wsFolderExpanded, setWsFolderExpanded] = useState(() => new Set());
  const toggleWsFolder = (id) => setWsFolderExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [wsFolderRename, setWsFolderRename] = useState(null); // { id, value }
  const [wsCtxMenu, setWsCtxMenu] = useState(null)
  // Background preset quick-switcher
  const [presetOpen, setPresetOpen] = useState(false);
  const presetRef = useRef(null);
  const calPresets = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(`crescendo:u:${current?.user_id}:bgPresets`) || "[]"); } catch { return []; }
  }, [current?.user_id, presetOpen]); // re-read when popover opens; // { x, y, ws }
  const [wsDropOpen, setWsDropOpen] = useState(false);
  const { save: saveTemplate } = useTemplates();
  const [view, setView] = useState(() => localStorage.getItem("crescendo:calView") || "week");
  // workflowMode removed — Automations is now its own top-level environment (see pages/Automations.jsx)
  // Always start at today — localStorage cursor caused "stuck on last week" bug
  const [cursor, setCursor] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [edges, setEdges] = useState([]);
  const [newEdgeId, setNewEdgeId] = useState(null); // tracks freshly created edge for animation

  // ── Notices — derived from events + edges ────────────────────────────────
  const notices = useMemo(() => {
    const items = [];
    const edgeSources = new Set(edges.map(e => e.source));
    const edgeTargets = new Set(edges.map(e => e.target));

    events.forEach(ev => {
      // Unused nodes: node types that have no incoming or outgoing edge
      const isNodeType = ZERO_DURATION_TYPES.has(ev.type);
      if (isNodeType && !edgeSources.has(ev.event_id) && !edgeTargets.has(ev.event_id)) {
        items.push({ type: "warning", id: ev.event_id, message: `"${ev.title || ev.type}" node is unused — not connected to anything`, eventId: ev.event_id });
      }
      // Trigger nodes with no outgoing edge
      if (ev.type === "trigger" && !edgeSources.has(ev.event_id)) {
        items.push({ type: "info", id: `trigger-no-out-${ev.event_id}`, message: `Trigger "${ev.title || "Untitled"}" has no action connected`, eventId: ev.event_id });
      }
      // Action nodes with no incoming edge
      if (ev.type === "action" && !edgeTargets.has(ev.event_id)) {
        items.push({ type: "info", id: `action-no-in-${ev.event_id}`, message: `Action "${ev.title || "Untitled"}" has no trigger connected`, eventId: ev.event_id });
      }
    });

    // Broken edges — source or target no longer exists
    const eventIds = new Set(events.map(e => e.event_id));
    edges.forEach(edge => {
      if (!eventIds.has(edge.source)) {
        items.push({ type: "warning", id: `broken-src-${edge.edge_id}`, message: `A connection has a missing source node`, eventId: edge.target });
      }
      if (!eventIds.has(edge.target)) {
        items.push({ type: "warning", id: `broken-tgt-${edge.edge_id}`, message: `A connection has a missing target node`, eventId: edge.source });
      }
    });

    return items;
  }, [events, edges]);
  const [eventNotes, setEventNotes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [connectSrc, setConnectSrc] = useState(null);
  const [cursorPos, setCursorPos] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [editingEdgeLabel, setEditingEdgeLabel] = useState(null); // { edgeId, value }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [convertOpen, setConvertOpen] = useState(false);
  const [noticesOpen, setNoticesOpen] = useState(false);
  const [runHistory, setRunHistory] = useState([]); // [{trigger_id, fired_at, kind, ...}]
  const [historyTab, setHistoryTab] = useState("notices"); // "notices" | "history"
  const loadRunHistory = async () => {
    if (!current?.workspace_id) return;
    try {
      const { data } = await api.get(`/workspaces/${current.workspace_id}/activity`);
      setRunHistory(data.items || []);
    } catch {}
  };
  const [snapMenuOpen, setSnapMenuOpen] = useState(false);
  const [ctxSubmenu, setCtxSubmenu] = useState(null); // 'add' | 'snap' | null

  // ── Feature state ─────────────────────────────────────────────────────────
  const [density, setDensity] = useState(
    () => localStorage.getItem("crescendo:density") || "comfortable"
  );
  const [bufferMinutes, setBufferMinutes] = useState(
    () => parseInt(localStorage.getItem("crescendo:bufferMin") || "0")
  );
  const [splitView, setSplitView] = useState(false);
  const [splitDate, setSplitDate] = useState(null);
  const [splitMode, setSplitMode] = useState("calendar"); // "calendar" | "documents"
  const [selectedDay, setSelectedDay] = useState(null); // ISO date string
  const [dayFmtKey, setDayFmtKey] = useState(0); // bumped when header format changes → re-render
  useEffect(() => {
    const handler = () => setDayFmtKey(k => k + 1);
    window.addEventListener("crescendo:day-fmt-changed", handler);
    return () => window.removeEventListener("crescendo:day-fmt-changed", handler);
  }, []);
  // Day Details panel state
  const [dayDetails, setDayDetails] = useState(null); // { date, dayEvents }
  const [timeBlockingOpen, setTimeBlockingOpen] = useState(false);
  const workHoursBarDrawnRef = useRef(false);


  // ── Node collapse/expand — persisted to localStorage for 30 days ──────────
  // Key: crescendo:nodeCollapse:{id} → {collapsed: bool, ts: number}
  const COLLAPSE_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days ms
  const getNodeCollapse = useCallback((id) => {
    try {
      const raw = localStorage.getItem(`crescendo:nodeCollapse:${id}`);
      if (!raw) return null;
      const { collapsed, ts } = JSON.parse(raw);
      if (Date.now() - ts > COLLAPSE_TTL) { localStorage.removeItem(`crescendo:nodeCollapse:${id}`); return null; }
      return collapsed;
    } catch { return null; }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setNodeCollapse = useCallback((id, collapsed) => {
    try { localStorage.setItem(`crescendo:nodeCollapse:${id}`, JSON.stringify({ collapsed, ts: Date.now() })); } catch {}
  }, []);
  // Derive effective collapsed state based on density default + per-node override
  const isNodeCollapsed = useCallback((id) => {
    const saved = getNodeCollapse(id);
    if (saved !== null) return saved; // user explicitly set it
    return density === "compact"; // compact = collapsed by default, expanded/comfortable = open
  }, [density, getNodeCollapse]);
  const toggleNodeCollapse = useCallback((id) => {
    const current = isNodeCollapsed(id);
    setNodeCollapse(id, !current);
  }, [isNodeCollapsed, setNodeCollapse]);

  // Apply density class to <html> on change
  useEffect(() => {
    document.documentElement.setAttribute("data-density", density);
    localStorage.setItem("crescendo:density", density);
  }, [density]);

  // Restore density on mount
  useEffect(() => {
    document.documentElement.setAttribute("data-density", density);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Global click-outside closer for transient menus (snap, convert, ctxMenu,
  // color menu inside dialog). User request: "dropdowns dont disappear when
  // they arent being hovered over anymore".
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!e.target.closest?.("[data-testid='snap-interval-menu'], [data-testid='snap-toggle-btn']")) setSnapMenuOpen(false);
      if (!e.target.closest?.("[data-testid='msbar-convert-menu'], [data-testid='msbar-convert']")) setConvertOpen(false);
      if (!e.target.closest?.("[data-testid='node-ctx-menu'], [data-testid='slot-ctx-menu'], [data-testid='event-color-menu']")) {
        setCtxMenu(null); setCtxSubmenu(null);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);
  const [selectionBox, setSelectionBox] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, event }
  const [clipboard, setClipboard] = useState(null); // { mode: 'copy'|'cut', events: [...] }
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  // Default savedAt to load-time so the indicator immediately shows
  // "Saved just now" rather than the ambiguous "Up To Date" with no
  // timestamp (iter35 user request: "'Up to date' needs to say when it
  // was last saved (in minutes)").
  const [savedAt, setSavedAt] = useState(() => new Date().toISOString());
  // Ticker so the 'Saved X minutes ago' label stays fresh without re-renders
  // from other sources. Re-evaluates every 30s.
  const [, setSaveTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSaveTick((t) => t + 1), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  const [isSaving, setIsSaving] = useState(false);
  const snapshotTimerRef = useRef(null);
  const [tick, setTick] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [templatePromptOpen, setTemplatePromptOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [selectedDayKey, setSelectedDayKey] = useState(null); // yyyy-MM-dd string for highlighted day column
  // Node expand/collapse — when set, that node takes the full overlap-cluster
  // width and any sibling overlappers compress to a thin ribbon (issue #1).
  const [expandedId, setExpandedId] = useState(null);
  // Force-play confirmation modal — when user picks "Force Play" on a
  // trigger, show a dialog before firing (user request).
  const [runConfirm, setRunConfirm] = useState(null);
  const pendingTemplateRef = useRef(null); // {events, edges}
  const eventRefs = useRef(new Map());
  const containerRef = useRef(null);

  const setRef = useCallback((id, el) => {
    if (el) eventRefs.current.set(id, el); else eventRefs.current.delete(id);
  }, []);

  // Overlay-all-calendars mode — when on, fetch events from EVERY workspace
  // the user has access to and render them inline with their workspace
  // colour as a translucent overlay (read-only). User request: "Overlap all
  // calendars" view.
  const [overlayAll, setOverlayAll] = useState(false);
  const [overlayEvents, setOverlayEvents] = useState([]); // [{event_id, ws_id, ws_color, ws_name, ...event}]

  // View-timezone picker — display-only. Persisted in localStorage. When
  // not equal to the user's local timezone, every event's start/end is
  // shifted in the rendered list so blocks appear under the correct day
  // column / hour for the chosen timezone (incl. push-across-midnight).
  const [displayTZ, setDisplayTZ] = useState(() => {
    try { return localStorage.getItem("crescendo:displayTZ") || LOCAL_TZ; } catch { return LOCAL_TZ; }
  });
  useEffect(() => {
    try { localStorage.setItem("crescendo:displayTZ", displayTZ); } catch {}
  }, [displayTZ]);

  // Week-view column zoom — multiplies each day-column min-width to let
  // users widen tight columns. 1.0 = default 120px. Range 0.5–3.0×, 25%
  // increments per user request iter32.
  const [daysInView, setDaysInView] = useState(() => {
    try {
      const raw = parseInt(localStorage.getItem("crescendo:daysInView") || "7", 10);
      // Clamp legacy values that may have drifted outside the new range.
      if (!isFinite(raw) || raw < 0.5) return 1;
      return Math.min(3, Math.max(0.5, raw));
    } catch { return 1; }
  });
  useEffect(() => {
    try { localStorage.setItem("crescendo:daysInView", String(daysInView)); } catch {}
  }, [daysInView]);
  useEffect(() => {
    try { localStorage.setItem("crescendo:calView", view); } catch {}
  }, [view]);
  useEffect(() => {
    try { localStorage.setItem("crescendo:calCursor", cursor.toISOString()); } catch {}
  }, [cursor]);

  // Calendar wheel handlers — Ctrl+wheel zooms the columns; Shift+wheel
  // pans horizontally. Native vertical scroll is left untouched. The
  // listener has to be `passive: false` so we can preventDefault to
  // suppress the page-level zoom.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        // 100 px deltaY ≈ one notch on most mice / trackpads; bucket
        // into 25% steps so each "scroll tick" maps cleanly to the
        // toolbar ± buttons.
        const dir = e.deltaY > 0 ? -1 : 1;
        setColZoom((z) => {
          const next = +(z + dir * 0.25).toFixed(2);
          return Math.min(3, Math.max(0.5, next));
        });
        return;
      }
      if (e.shiftKey) {
        // Shift-wheel → horizontal pan. We use the larger of deltaX /
        // deltaY (some browsers map shift+vertical-wheel to deltaX
        // already, others don't — supporting both.)
        const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        if (dx === 0) return;
        e.preventDefault();
        el.scrollLeft += dx;
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Listen for global zoom events from Dashboard Ctrl+scroll
  useEffect(() => {
    const onZoom = (e) => {
      const dir = e.detail?.dir ?? -1;
      setColZoom(z => Math.min(3, Math.max(0.5, +(z + dir * 0.25).toFixed(2))));
    };
    window.addEventListener("crescendo:col-zoom", onZoom);
    return () => window.removeEventListener("crescendo:col-zoom", onZoom);
  }, []);

  // Local container Ctrl+scroll zoom (also handled globally via Dashboard)
  useEffect(() => {
    const onGlobalWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      // Only intercept if the event didn't already go through the container handler
      if (containerRef.current && containerRef.current.contains(e.target)) return;
      e.preventDefault();
      const dir = e.deltaY > 0 ? -1 : 1;
      setColZoom((z) => {
        const next = +(z + dir * 0.25).toFixed(2);
        return Math.min(3, Math.max(0.5, next));
      });
    };
    window.addEventListener("wheel", onGlobalWheel, { passive: false });
    return () => window.removeEventListener("wheel", onGlobalWheel);
  }, []);
  useEffect(() => {
    if (!overlayAll) { setOverlayEvents([]); return; }
    const run = async () => {
      const others = workspaces.filter((w) => w.workspace_id !== current?.workspace_id);
      const results = await Promise.allSettled(
        others.map((w) => api.get(`/workspaces/${w.workspace_id}/events`).then((r) => ({ ws: w, events: r.data })))
      );
      const flat = [];
      for (const r of results) {
        if (r.status !== "fulfilled") continue;
        const { ws, events: evs } = r.value;
        for (const ev of evs) flat.push({ ...ev, _overlay: true, _ws_id: ws.workspace_id, _ws_name: ws.name, _ws_color: ws.color });
      }
      setOverlayEvents(flat);
    };
    run();
  }, [overlayAll, workspaces, current?.workspace_id]);

  const load = useCallback(async () => {
    if (!current) return;
    const [e, ed] = await Promise.all([
      api.get(`/workspaces/${current.workspace_id}/events`),
      api.get(`/workspaces/${current.workspace_id}/edges`),
    ]);
    setEvents(e.data);
    setEdges(ed.data);
    const allNotes = await Promise.all(
      e.data.map((ev) => api.get(`/workspaces/${current.workspace_id}/events/${ev.event_id}/notes`).then((r) => r.data))
    );
    setEventNotes(allNotes.flat());
  }, [current]);

  useEffect(() => { load(); }, [load]);

  // TZ-shifted view of events used purely for layout/display. We keep
  // `events` as the source-of-truth for handlers that write back to the
  // backend (drag reschedule, dialog edits) so the user is always editing
  // wall-clock time in their LOCAL timezone, not the display tz.
  const displayedEvents = useMemo(() => {
    if (!displayTZ || displayTZ === LOCAL_TZ) return events;
    return events.map((ev) => ({
      ...ev,
      start: ev.start ? shiftIsoToTZ(ev.start, displayTZ) : ev.start,
      end: ev.end ? shiftIsoToTZ(ev.end, displayTZ) : ev.end,
      trigger_at: ev.trigger_at ? shiftIsoToTZ(ev.trigger_at, displayTZ) : ev.trigger_at,
    }));
  }, [events, displayTZ]);
  const displayedOverlayEvents = useMemo(() => {
    if (!displayTZ || displayTZ === LOCAL_TZ) return overlayEvents;
    return overlayEvents.map((ev) => ({
      ...ev,
      start: ev.start ? shiftIsoToTZ(ev.start, displayTZ) : ev.start,
      end: ev.end ? shiftIsoToTZ(ev.end, displayTZ) : ev.end,
    }));
  }, [overlayEvents, displayTZ]);

  // Move a workspace up or down within its scope (root or shared
  // parent_folder_id). Mirrors the optimistic reorder in Sidebar.jsx —
  // updates local state immediately, then fire-and-forgets PUTs to
  // persist the new positions.
  const moveWorkspace = useCallback((wsId, direction) => {
    if (!setWorkspaces) return;
    const all = [...workspaces].sort((a, b) => (a.position || 0) - (b.position || 0));
    const target = all.find((w) => w.workspace_id === wsId);
    if (!target) return;
    const scope = all.filter((w) => (w.parent_folder_id || null) === (target.parent_folder_id || null));
    const idxInScope = scope.findIndex((w) => w.workspace_id === wsId);
    const swapIdx = direction === "up" ? idxInScope - 1 : idxInScope + 1;
    if (swapIdx < 0 || swapIdx >= scope.length) return;
    const other = scope[swapIdx];
    // Swap positions in the GLOBAL list.
    const a = all.findIndex((w) => w.workspace_id === target.workspace_id);
    const b = all.findIndex((w) => w.workspace_id === other.workspace_id);
    [all[a], all[b]] = [all[b], all[a]];
    const renumbered = all.map((w, i) => ({ ...w, position: i }));
    setWorkspaces(renumbered);
    renumbered.forEach((w, i) => { api.put(`/workspaces/${w.workspace_id}`, { position: i }).catch(() => {}); });
  }, [workspaces, setWorkspaces]);

  // Reload when external consumers (e.g. the sidebar's NL create) announce
  // that events have changed.
  useEffect(() => {
    const onChange = () => load();
    window.addEventListener("crescendo:events-changed", onChange);
    return () => window.removeEventListener("crescendo:events-changed", onChange);
  }, [load]);

  // Open the EventDialog at "now" when the sidebar's "Drop To Add Node" box
  // is clicked (as opposed to dragged). User request: clicking should still
  // surface the same initialization picker.
  useEffect(() => {
    const onAddBlank = () => {
      const now = new Date();
      // Round to nearest 15 minutes to match the snap default.
      now.setMinutes(Math.round(now.getMinutes() / 15) * 15, 0, 0);
      // Default to TASK (user request: blank → task on bare slot).
      setEditing({ start: now.toISOString(), end: addHours(now, 1).toISOString(), type: "task", title: "" });
      setDialogOpen(true);
    };
    window.addEventListener("crescendo:add-blank-now", onAddBlank);
    return () => window.removeEventListener("crescendo:add-blank-now", onAddBlank);
  }, []);

  useEffect(() => {
    if (!current) return;
    const run = async () => {
      try {
        const { data } = await api.post(`/workspaces/${current.workspace_id}/automations/run`);
        if (data.count > 0) {
          (data.fired || []).forEach((f) => {
            if (f.kind === "notify" || f.notify) toast(f.notify || "Notification", { className: "reminder-toast" });
            else if (f.kind === "complete_task") toast.success("Task marked complete");
            else if (f.kind === "toggle_pin") toast("Pin toggled");
            else if (f.kind === "duplicate_tomorrow") toast.success("Duplicated to tomorrow");
            else if (f.kind === "journal_log") toast("Logged to journal", { className: "reminder-toast" });
            else if (f.kind === "slack_post") toast("Posted to Slack");
            else if (f.kind === "reschedule") toast(`Rescheduled by ${f.offset}m`);
            else if (f.kind === "update_keypair") toast.success(`Key "${f.key}" → "${f.value}"`);
          });
          toast.success(`${data.count} automation${data.count > 1 ? "s" : ""} fired`);
          load();
        }
      } catch {}
    };
    run();
    const id = setInterval(run, 30000);
    return () => clearInterval(id);
  }, [current, load]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setTick((t) => t + 1);
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); ro?.disconnect(); };
  }, [events, view, cursor]);

  // Re-measure edges after events/edges change (floating node movement, layout changes).
  useEffect(() => {
    const id = requestAnimationFrame(() => setTick((t) => t + 1));
    return () => cancelAnimationFrame(id);
  }, [events, edges]);

  // Focus mode removed iter32 (user request: "Let's remove focus mode altogether").

  // Listen for the "show legend" custom event (sidebar search + ? shortcut).
  const [legendOpen, setLegendOpen] = useState(false);
  // Active drag type for ghost preview (broadcast by Sidebar on dragstart).
  const [activeDragType, setActiveDragType] = useState(null);
  useEffect(() => {
    const onDrag = (e) => setActiveDragType(e.detail?.type || null);
    window.addEventListener("crescendo:drag-type", onDrag);
    return () => window.removeEventListener("crescendo:drag-type", onDrag);
  }, []);
  useEffect(() => {
    const onLegend = () => setLegendOpen(true);
    window.addEventListener("crescendo:open-legend", onLegend);
    return () => window.removeEventListener("crescendo:open-legend", onLegend);
  }, []);
  // ? key toggles legend (unless typing in an input).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "?") return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      e.preventDefault();
      setLegendOpen((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // N key — create new event (quick add) at today/cursor
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "n" && e.key !== "N") return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.contentEditable === "true") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      e.preventDefault();
      // Open new event dialog seeded to the current cursor date at the nearest hour
      const now = new Date();
      const seed = new Date(cursor);
      seed.setHours(now.getHours(), 0, 0, 0);
      setEditing({ start: seed.toISOString(), end: new Date(seed.getTime() + 3600000).toISOString(), type: "task" });
      setDialogOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cursor]);

  // T → today, ArrowLeft/Right → navigate
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.contentEditable === "true") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "t" || e.key === "T") { e.preventDefault(); setCursor(startOfDay(new Date())); return; }
      if (e.key === "ArrowLeft")  { e.preventDefault(); setCursor(c => addDays(c, view === "week" ? -7 : -1)); }
      if (e.key === "ArrowRight") { e.preventDefault(); setCursor(c => addDays(c, view === "week" ?  7 :  1)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view]);

  // Template instantiation from sidebar (paste at now in current workspace)
  const instantiateTemplate = useCallback(async (tpl, dropDate = null) => {
    if (!current || !tpl?.config?.events?.length) return;
    const evs = tpl.config.events;
    // Shift all start/end so the earliest sits at dropDate (or now).
    const earliest = Math.min(...evs.map((e) => new Date(e.start).getTime()));
    const offset = (dropDate ? dropDate.getTime() : Date.now()) - earliest;
    // id remap so edges stay consistent.
    const idMap = new Map();
    const newEvents = [];
    for (const e of evs) {
      const body = { ...e };
      delete body.event_id; delete body.workspace_id; delete body.user_id;
      body.start = new Date(new Date(e.start).getTime() + offset).toISOString();
      body.end = new Date(new Date(e.end).getTime() + offset).toISOString();
      if (body.trigger_at) body.trigger_at = new Date(new Date(e.trigger_at).getTime() + offset).toISOString();
      if (body.remind_at) body.remind_at = new Date(new Date(e.remind_at).getTime() + offset).toISOString();
      const { data } = await api.post(`/workspaces/${current.workspace_id}/events`, body);
      idMap.set(e.event_id, data.event_id);
      newEvents.push(data);
    }
    for (const edge of (tpl.config.edges || [])) {
      const s = idMap.get(edge.source); const t = idMap.get(edge.target);
      if (!s || !t) continue;
      await api.post(`/workspaces/${current.workspace_id}/edges`, { source: s, target: t, source_handle: edge.source_handle, target_handle: edge.target_handle });
    }
    /* silent: inserted template */
    load();
    // Apply workspace accent color as CSS variable
    if (current?.color) {
      document.documentElement.style.setProperty("--ws-accent", current.color);
    } else {
      document.documentElement.style.removeProperty("--ws-accent");
    }
    // Apply workspace background/wallpaper
    if (current?.workspace_id) applyWorkspaceBgVars(current.workspace_id);
  }, [current, load]);

  useEffect(() => {
    const onInst = (e) => instantiateTemplate(e.detail);
    const onInstAt = (e) => {
      const { templateId, date } = e.detail || {};
      // Fetch the freshest template by id from sidebar context (via a custom event we dispatch).
      // Instead, find from the templates prop — but we don't have it. Use api.
      (async () => {
        try {
          const { data } = await api.get("/templates");
          const tpl = data.find((t) => t.template_id === templateId);
          if (tpl) instantiateTemplate(tpl, new Date(date));
        } catch {}
      })();
    };
    window.addEventListener("crescendo:instantiate-template", onInst);
    window.addEventListener("crescendo:instantiate-template-at", onInstAt);

    // Schedule an unscheduled task — dropped from sidebar onto a day column
    const onScheduleUnscheduled = async (e) => {
      const { eventId, start, end } = e.detail;
      if (!current) return;
      try {
        await api.put(`/workspaces/${current.workspace_id}/events/${eventId}`, {
          start, end, unscheduled: false,
        });
        // Refresh events and notify sidebar
        window.dispatchEvent(new CustomEvent("crescendo:events-changed"));
        window.dispatchEvent(new CustomEvent("crescendo:unscheduled-changed"));
      } catch (err) {
        console.error("Failed to schedule task", err);
      }
    };
    window.addEventListener("crescendo:schedule-unscheduled", onScheduleUnscheduled);

    // Open EventDialog in unscheduled mode (from sidebar + button)
    const onAddUnscheduled = (e) => {
      const nodeType = e?.detail?.type || "task";
      setEditing({ start: new Date().toISOString(), end: new Date().toISOString(), type: nodeType, title: "", unscheduled: true });
      setDialogOpen(true);
    };
    // Open EventDialog to edit an existing unscheduled task (click in sidebar)
    const onEditUnscheduled = (e) => {
      const ev = e.detail?.event;
      if (ev) { setEditing(ev); setDialogOpen(true); }
    };
    window.addEventListener("crescendo:add-unscheduled", onAddUnscheduled);
    window.addEventListener("crescendo:edit-unscheduled", onEditUnscheduled);

    return () => {
      window.removeEventListener("crescendo:instantiate-template", onInst);
      window.removeEventListener("crescendo:instantiate-template-at", onInstAt);
      window.removeEventListener("crescendo:schedule-unscheduled", onScheduleUnscheduled);
      window.removeEventListener("crescendo:add-unscheduled", onAddUnscheduled);
      window.removeEventListener("crescendo:edit-unscheduled", onEditUnscheduled);
    };
  }, [instantiateTemplate]);

  const loadSnapshots = async () => {
    if (!current) return;
    const { data } = await api.get(`/workspaces/${current.workspace_id}/snapshots`);
    setSnapshots(data);
  };
  const takeSnapshot = async () => {
    if (!current) return;
    await api.post(`/workspaces/${current.workspace_id}/snapshots`);
    loadSnapshots();
  };

  // Auto-snapshot policy (issue #8):
  //   * Keep a rolling 5-minute window.
  //   * Count each successful mutation.
  //   * If we hit >= 20 changes before the window closes → take a snapshot,
  //     reset counter + restart window.
  //   * If fewer than 20 changes at window close → reset counter + restart
  //     window (no snapshot).
  // Auto-save timer — instead of the "every 10 mutations" threshold, the
  // client schedules a snapshot at a RANDOM interval between 10 and 15
  // minutes from the last snapshot (user request). Mutations still persist
  // individually in the background; this timer governs the "version snapshot"
  // that shows up in the history list.
  const randomSaveDelay = () => (10 * 60 * 1000) + Math.floor(Math.random() * 5 * 60 * 1000);
  const autoSaveTimerRef = useRef(null);

  const scheduleNextAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      if (current && mutationCountRef.current > 0) {
        mutationCountRef.current = 0;
        api.post(`/workspaces/${current.workspace_id}/snapshots`).then(() => {
          setSavedAt(new Date());
          loadSnapshots();
          scheduleNextAutoSave();
        }).catch(() => scheduleNextAutoSave());
      } else {
        scheduleNextAutoSave();
      }
    }, randomSaveDelay());
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scheduleNextAutoSave();
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [scheduleNextAutoSave]);

  const mutationCountRef = useRef(0);
  const snapshotWindowRef = useRef(null);
  const SNAPSHOT_WINDOW_MS = 5 * 60 * 1000;
  const SNAPSHOT_MIN_CHANGES = 9999; // effectively disabled — the random timer above handles snapshots now

  const openSnapshotWindow = useCallback(() => {
    if (snapshotWindowRef.current) return;
    snapshotWindowRef.current = setTimeout(() => {
      snapshotWindowRef.current = null;
      mutationCountRef.current = 0;
    }, SNAPSHOT_WINDOW_MS);
  }, []);

  // Save lifecycle — the global axios interceptor emits 'crescendo:save-state' events.
  // Per user request, the "Saved X minutes ago" indicator reflects the last
  // *snapshot* (i.e. after every 10 mutations), NOT every single mutation.
  // Individual mutations still persist instantly in the background.
  useEffect(() => {
    const onState = (e) => {
      setIsSaving(!!e.detail?.saving);
      if (!e.detail?.saving) {
        // Each settled mutation contributes to the 5-minute change window.
        if (e.detail?.kind === "mutation") {
          mutationCountRef.current += 1;
          openSnapshotWindow();
          if (mutationCountRef.current >= SNAPSHOT_MIN_CHANGES) {
            if (snapshotWindowRef.current) { clearTimeout(snapshotWindowRef.current); snapshotWindowRef.current = null; }
            mutationCountRef.current = 0;
            if (current) {
              api.post(`/workspaces/${current.workspace_id}/snapshots`).then(() => {
                setSavedAt(new Date());   // indicator only updates on snapshot
                loadSnapshots();
              }).catch(() => {});
            }
          }
        } else if (e.detail?.kind === "snapshot") {
          setSavedAt(new Date());
        }
      }
    };
    window.addEventListener("crescendo:save-state", onState);
    return () => window.removeEventListener("crescendo:save-state", onState);
  }, [current, openSnapshotWindow]);

  const saveNow = async () => {
    if (!current) return;
    if (snapshotTimerRef.current) { clearTimeout(snapshotTimerRef.current); snapshotTimerRef.current = null; }
    await api.post(`/workspaces/${current.workspace_id}/snapshots`);
    /* silent: saved snapshot */
    loadSnapshots();
  };
  const restoreSnapshot = async (snapshot_id) => {
    if (!current) return;
    const ok = await confirmDelete({ title: "Restore Snapshot?", message: "Current workspace state will be replaced.", confirmLabel: "Restore" });
    if (!ok) return;
    await api.post(`/workspaces/${current.workspace_id}/snapshots/${snapshot_id}/restore`);
    /* silent: restored */
    load();
    setHistoryOpen(false);
  };
  useEffect(() => { if (historyOpen) loadSnapshots(); /* eslint-disable-next-line */ }, [historyOpen, current?.workspace_id]);

  // Sidebar footer fires this event to open history from the sidebar History button.
  useEffect(() => {
    const on = () => setHistoryOpen(true);
    window.addEventListener("crescendo:open-history", on);
    return () => window.removeEventListener("crescendo:open-history", on);
  }, []);

  // Unified keyboard shortcuts: Delete/Ctrl+D/C/V/X
  useEffect(() => {
    const onKey = async (e) => {
      const target = e.target;
      if (target && ["INPUT","TEXTAREA"].includes(target.tagName)) return;
      if (target && target.isContentEditable) return;
      if (!current) return;

      // Delete / Backspace → delete selected edge or nodes
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedEdgeId) {
          e.preventDefault();
          await api.delete(`/workspaces/${current.workspace_id}/edges/${selectedEdgeId}`);
          setSelectedEdgeId(null);
          load();
          return;
        }
        if (selectedIds.size > 0) {
          e.preventDefault();
          // Honor the lock flag — never delete locked nodes from keyboard.
          const victims = events.filter((ev) => selectedIds.has(ev.event_id) && !ev.locked);
          const lockedCount = [...selectedIds].length - victims.length;
          for (const ev of victims) {
            await api.delete(`/workspaces/${current.workspace_id}/events/${ev.event_id}`);
          }
          setSelectedIds(new Set());
          load();
          if (lockedCount > 0) {
            toast(`${victims.length} deleted. ${lockedCount} locked — skipped.`);
          }
        }
        return;
      }

      // Ctrl/Cmd shortcuts
      if (e.ctrlKey || e.metaKey) {
        const selectedEvents = events.filter((ev) => selectedIds.has(ev.event_id));
        if (e.key.toLowerCase() === "c" && selectedEvents.length > 0) {
          e.preventDefault();
          setClipboard({ mode: "copy", events: selectedEvents });
          /* silent: copied */
        } else if (e.key.toLowerCase() === "x" && selectedEvents.length > 0) {
          e.preventDefault();
          setClipboard({ mode: "cut", events: selectedEvents });
          /* silent: cut */
        } else if (e.key.toLowerCase() === "v" && clipboard?.events?.length) {
          e.preventDefault();
          const ids = await pasteClipboard();
          if (clipboard.mode === "cut") {
            for (const ev of clipboard.events) {
              await api.delete(`/workspaces/${current.workspace_id}/events/${ev.event_id}`);
            }
            setClipboard(null);
          }
          setSelectedIds(new Set(ids));
          load();
        } else if (e.key.toLowerCase() === "d" && selectedEvents.length > 0) {
          e.preventDefault();
          const ids = [];
          for (const ev of selectedEvents) {
            const body = duplicateBody(ev, 30);
            const { data } = await api.post(`/workspaces/${current.workspace_id}/events`, body);
            ids.push(data.event_id);
          }
          setSelectedIds(new Set(ids));
          load();
          /* silent: duplicated */
        } else if (e.key.toLowerCase() === "g" && selectedEvents.length > 1) {
          // Ctrl/Cmd+G → Task Group. Creates a sequence node that wraps the
          // selected nodes into a single "big node" that acts as a container
          // (moves / deletes its children together).
          //
          // Auto-include any events that visually overlap a selected event
          // — recurring user bug: marquee selection over a cluster missed
          // some elements that were stacked behind others, so grouping
          // "left elements out". Expanding to the full overlap cluster
          // makes the operation predictable.
          e.preventDefault();
          const expanded = new Set(selectedEvents.map((x) => x.event_id));
          const overlaps = (a, b) => {
            const as = Date.parse(a.start), ae = Math.max(Date.parse(a.end) || as, as + 25 * 60000);
            const bs = Date.parse(b.start), be = Math.max(Date.parse(b.end) || bs, bs + 25 * 60000);
            // Only events on the same calendar day can be visually grouped.
            const sameDay = new Date(as).toDateString() === new Date(bs).toDateString();
            return sameDay && as < be && bs < ae;
          };
          let changed = true;
          while (changed) {
            changed = false;
            for (const ev of events) {
              if (expanded.has(ev.event_id)) continue;
              if (nestedChildIds.has(ev.event_id)) continue; // already in a sequence
              if (ev.type === "sequence") continue; // don't pull in other groups
              for (const sid of expanded) {
                const seed = events.find((x) => x.event_id === sid);
                if (seed && overlaps(seed, ev)) { expanded.add(ev.event_id); changed = true; break; }
              }
            }
          }
          const groupSet = events.filter((x) => expanded.has(x.event_id));
          const startMs = Math.min(...groupSet.map((x) => Date.parse(x.start)));
          const endMs = Math.max(...groupSet.map((x) => Date.parse(x.end) || Date.parse(x.start)));
          const body = {
            title: "Task Group",
            type: "sequence",
            start: new Date(startMs).toISOString(),
            end: new Date(Math.max(endMs, startMs + 30 * 60000)).toISOString(),
            sequence_mode: "parallel",
            children_event_ids: groupSet.map((x) => x.event_id),
          };
          const { data } = await api.post(`/workspaces/${current.workspace_id}/events`, body);
          setSelectedIds(new Set([data.event_id]));
          load();
          /* silent: grouped */
        }
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedEdgeId, selectedIds, current, load, events, clipboard]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicateBody = (ev, offsetMinutes = 0) => ({
    title: ev.title, description: ev.description, type: ev.type,
    start: addMinutes(new Date(ev.start), offsetMinutes).toISOString(),
    end: addMinutes(new Date(ev.end), offsetMinutes).toISOString(),
    class_name: ev.class_name, custom_color: ev.custom_color,
    trigger_at: ev.trigger_at, action_config: ev.action_config,
  });

  const pasteClipboard = async () => {
    if (!clipboard?.events?.length || !current) return [];
    const ids = [];
    for (const ev of clipboard.events) {
      const body = duplicateBody(ev, 30);
      const { data } = await api.post(`/workspaces/${current.workspace_id}/events`, body);
      ids.push(data.event_id);
    }
    /* silent: pasted */
    return ids;
  };

  // OS detection for displaying correct modifier keys (⌘ on Mac, Ctrl on Win/Linux)
  const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const modKey = isMac ? "⌘" : "Ctrl";

  // Reminder fire check — with dedupe via in-memory set so it never double-fires
  const firedReminderIds = useRef(new Set());
  useEffect(() => {
    if (!current) return;
    const run = async () => {
      const now = Date.now();
      for (const ev of events) {
        if (ev.type !== "reminder") continue;
        if (ev.reminded_at) { firedReminderIds.current.add(ev.event_id); continue; }
        if (firedReminderIds.current.has(ev.event_id)) continue;
        const t = new Date(ev.start).getTime();
        // Iter35 user request: "When opening the app after a task has
        // started, a toast should ONLY show if the task is still
        // occurring." For reminders (zero-duration), apply a 5-minute
        // grace window — past that we mark them fired silently so the
        // user doesn't get spammed with stale notifications when they
        // open the app hours later.
        const STALE_MS = 5 * 60 * 1000;
        if (t <= now - STALE_MS) {
          // Stale — silently mark fired and persist.
          firedReminderIds.current.add(ev.event_id);
          try { await api.put(`/workspaces/${current.workspace_id}/events/${ev.event_id}`, { reminded_at: new Date().toISOString() }); } catch {}
          continue;
        }
        if (t <= now) {
          firedReminderIds.current.add(ev.event_id); // mark immediately to prevent re-fire
          try {
            // Bubbly ding via WebAudio API
            const AC = window.AudioContext || window.webkitAudioContext;
            if (AC) {
              const ctx = new AC();
              const now2 = ctx.currentTime;
              [
                [880, 0], [1175, 0.08], [1568, 0.16],
              ].forEach(([freq, delay]) => {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.type = "sine"; o.frequency.value = freq;
                g.gain.setValueAtTime(0, now2 + delay);
                g.gain.linearRampToValueAtTime(0.15, now2 + delay + 0.02);
                g.gain.exponentialRampToValueAtTime(0.0001, now2 + delay + 0.35);
                o.connect(g); g.connect(ctx.destination);
                o.start(now2 + delay); o.stop(now2 + delay + 0.4);
              });
            }
          } catch {}
          toast(ev.description || ev.title, { duration: 5000, className: "reminder-toast" });
          // Also fire a native browser notification if permission granted
          if (window.Notification?.permission === "granted") {
            try {
              new window.Notification(ev.title || "Reminder", {
                body: ev.description || "",
                icon: "/icons/icon-192x192.png",
                tag: ev.event_id,
                silent: true, // audio already played above
              });
            } catch {}
          } else if (window.Notification?.permission === "default") {
            window.Notification.requestPermission().catch(() => {});
          }
          try {
            await api.put(`/workspaces/${current.workspace_id}/events/${ev.event_id}`, { reminded_at: new Date().toISOString() });
          } catch {}
        }
      }
    };
    run();
    const id = setInterval(run, 15000);
    return () => clearInterval(id);
  }, [current, events]);

  // Undo/Redo
  const pushUndo = useCallback((cmd) => {
    setUndoStack((s) => [...s.slice(-50), cmd]);
    setRedoStack([]);
  }, []);
  const undo = useCallback(async () => {
    if (!current || undoStack.length === 0) return;
    const cmd = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    setRedoStack((s) => [...s, cmd]);
    await cmd.undo?.();
    load();
    toast("Undone");
  }, [undoStack, current, load]);
  const redo = useCallback(async () => {
    if (!current || redoStack.length === 0) return;
    const cmd = redoStack[redoStack.length - 1];
    setRedoStack((s) => s.slice(0, -1));
    setUndoStack((s) => [...s, cmd]);
    await cmd.redo?.();
    load();
    toast("Redone");
  }, [redoStack, current, load]);

  // Keyboard shortcuts - add undo/redo + Esc (close ctx menus / clear expand) to existing handler
  useEffect(() => {
    const onKey = (e) => {
      const target = e.target;
      if (target && ["INPUT","TEXTAREA"].includes(target.tagName)) return;
      if (target && target.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
      else if (((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y")) || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "z")) { e.preventDefault(); redo(); }
      else if (e.key === "Escape") {
        // Esc clears expand + closes any stuck context menu / connection drag.
        setExpandedId(null);
        setCtxMenu(null);
        setCtxSubmenu(null);
        if (connectSrc) { setConnectSrc(null); setCursorPos(null); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, connectSrc]);

  const notesByEvent = useMemo(() => {
    const m = new Map();
    eventNotes.forEach((n) => { if (!m.has(n.event_id)) m.set(n.event_id, []); m.get(n.event_id).push(n); });
    return m;
  }, [eventNotes]);

  const classByName = useMemo(() => {
    const m = new Map();
    classes.forEach((c) => m.set(c.name, c));
    return m;
  }, [classes]);

  // Nodes attached to calendar events — used by EventNodeRow
  const attachedByEvent = useMemo(() => {
    const m = new Map();
    events.filter(e => isFloatingType(e.type) && e.attached_to).forEach(n => {
      if (!m.has(n.attached_to)) m.set(n.attached_to, []);
      m.get(n.attached_to).push(n);
    });
    return m;
  }, [events]);

  // color applied to top bar when class assigned (fallback to class color)
  const classColorMap = useMemo(() => {
    const m = new Map();
    events.forEach((e) => {
      const cls = e.class_name ? classByName.get(e.class_name) : null;
      if (cls) m.set(e.event_id, cls.color);
    });
    return m;
  }, [events, classByName]);

  // background tint when class is active (click in sidebar)
  const classBgMap = useMemo(() => {
    const m = new Map();
    if (!activeClassId) return m;
    const activeCls = classes.find((c) => c.class_id === activeClassId);
    if (!activeCls) return m;
    events.forEach((e) => { if (e.class_name === activeCls.name) m.set(e.event_id, activeCls.color); });
    return m;
  }, [activeClassId, classes, events]);

  const dimmedMap = useMemo(() => {
    const m = new Map();
    if (!activeClassId) return m;
    const activeCls = classes.find((c) => c.class_id === activeClassId);
    if (!activeCls) return m;
    events.forEach((e) => { if (e.class_name !== activeCls.name) m.set(e.event_id, true); });
    return m;
  }, [activeClassId, classes, events]);

  const onSlotDoubleClick = (dt) => {
    setEditing({ start: dt.toISOString(), end: addHours(dt, 1).toISOString(), type: "task", title: "" });
    setDialogOpen(true);
  };
  const onNodeDrop = async (type, dt, pos) => {
    const realType = type === "blank" ? "task" : type;
    const base = { type: realType, title: "", description: "", start: dt.toISOString(), end: isZeroDurationType(realType) ? dt.toISOString() : addHours(dt, 1).toISOString() };
    setEditing(base);
    setDialogOpen(true);
  };

  // Attach a node to a calendar event — creates it at the same start time
  // so it appears below the event in the floating nodes layer.
  const onAttachNode = async (parentEvent, nodeType, _posOrDate) => {
    if (!current) return;
    const start = parentEvent.start || new Date().toISOString();
    const doc = {
      type: nodeType,
      title: nodeType.charAt(0).toUpperCase() + nodeType.slice(1),
      description: "",
      start,
      end: start,
      workspace_id: current.workspace_id,
      attached_to: parentEvent.event_id,
    };
    try {
      await api.post(`/workspaces/${current.workspace_id}/events`, doc);
      load();
    } catch {
      toast.error("Failed to attach node");
    }
  };

  const onEventOpen = (ev) => { setEditing(ev); setDialogOpen(true); };

  const onReschedule = async (event_id, deltaMin, absoluteStart) => {
    if (!current) return;
    // Cross-day absolute move (from pointer system)
    if (absoluteStart) {
      const ev = events.find((e) => e.event_id === event_id);
      if (!ev) return;
      const dur = Date.parse(ev.end || ev.start) - Date.parse(ev.start);
      const newStart = absoluteStart.toISOString();
      const newEnd = new Date(absoluteStart.getTime() + dur).toISOString();
      await api.put(`/workspaces/${current.workspace_id}/events/${event_id}`, { start: newStart, end: newEnd, unscheduled: false });
      load();
      return;
    }
    const ids = selectedIds.has(event_id) && selectedIds.size > 1 ? [...selectedIds] : [event_id];
    const expandedIds = new Set(ids);
    for (const id of ids) {
      const ev = events.find((e) => e.event_id === id);
      if (ev && ev.type === "sequence" && Array.isArray(ev.children_event_ids)) {
        for (const c of ev.children_event_ids) expandedIds.add(c);
      }
    }
    const targets = [...expandedIds].map((id) => events.find((e) => e.event_id === id)).filter((ev) => ev && !ev.locked);
    if (targets.length === 0) return;
    const apply = async (delta) => {
      await Promise.all(targets.map((ev) => {
        const newStart = addMinutes(new Date(ev.start), delta).toISOString();
        const newEnd = addMinutes(new Date(ev.end), delta).toISOString();
        return api.put(`/workspaces/${current.workspace_id}/events/${ev.event_id}`, { start: newStart, end: newEnd });
      }));
    };
    await apply(deltaMin);
    // Register undo/redo entry for this movement.
    pushUndo({
      undo: async () => { await apply(-deltaMin); load(); },
      redo: async () => { await apply(deltaMin); load(); },
    });
    load();
  };

  const onResize = async (event_id, newEnd) => {
    if (!current) return;
    await api.put(`/workspaces/${current.workspace_id}/events/${event_id}`, { end: newEnd });
    load();
  };

  const onMoveCommit = async (event_id, position_x, position_y) => {
    if (!current) return;
    // Optimistic update to keep the node in the new position during reload
    setEvents((prev) => prev.map((e) => e.event_id === event_id ? { ...e, position_x, position_y } : e));
    try {
      await api.put(`/workspaces/${current.workspace_id}/events/${event_id}`, { position_x, position_y });
    } catch {
      load();
    }
  };

  // Bump tick while a floating node is dragging so edges redraw live.
  const onDragFrame = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  const onSave = async (body) => {
    if (!current) return;
    if (editing?.event_id) {
      const prev = events.find((e) => e.event_id === editing.event_id);
      await api.put(`/workspaces/${current.workspace_id}/events/${editing.event_id}`, body);
      if (prev) {
        pushUndo({
          undo: async () => { await api.put(`/workspaces/${current.workspace_id}/events/${editing.event_id}`, prev); },
          redo: async () => { await api.put(`/workspaces/${current.workspace_id}/events/${editing.event_id}`, body); },
        });
      }
      /* silent: updated */
    } else {
      // Carry over position_x/y from the drop target if present (free-flow placement)
      if (editing?.position_x != null) body.position_x = editing.position_x;
      if (editing?.position_y != null) body.position_y = editing.position_y;
      const { data } = await api.post(`/workspaces/${current.workspace_id}/events`, body);
      pushUndo({
        undo: async () => { await api.delete(`/workspaces/${current.workspace_id}/events/${data.event_id}`); },
        redo: async () => { await api.post(`/workspaces/${current.workspace_id}/events`, body); },
      });
      /* silent: created */
    }
    setDialogOpen(false); load();
    if (body?.unscheduled) window.dispatchEvent(new CustomEvent("crescendo:unscheduled-changed"));
  };

  const onDelete = async (id) => {
    if (!current) return;
    const ev = events.find((e) => e.event_id === id);
    // Sequence binds its children (user request: "same thing with deleting").
    const toDelete = [id];
    if (ev && ev.type === "sequence" && Array.isArray(ev.children_event_ids)) {
      toDelete.push(...ev.children_event_ids.filter((c) => c !== id));
    }
    await Promise.all(toDelete.map((x) => api.delete(`/workspaces/${current.workspace_id}/events/${x}`)));
    if (ev) {
      pushUndo({
        undo: async () => { await api.post(`/workspaces/${current.workspace_id}/events`, { ...ev }); },
        redo: async () => { await Promise.all(toDelete.map((x) => api.delete(`/workspaces/${current.workspace_id}/events/${x}`))); },
      });
    }
    setDialogOpen(false); load();
  };

  const onStartConnect = (event, side, e) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setConnectSrc({ event, side });
    setCursorPos({ x: e.clientX - rect.left + container.scrollLeft, y: e.clientY - rect.top + container.scrollTop });
    const onMove = (ev) => {
      setCursorPos({ x: ev.clientX - rect.left + container.scrollLeft, y: ev.clientY - rect.top + container.scrollTop });
    };
    const onUp = async (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const eventEl = el?.closest('[data-event-id]');
      if (eventEl) {
        const tgtId = eventEl.getAttribute("data-event-id");
        if (tgtId && tgtId !== event.event_id) {
          // Dedupe — don't create a duplicate edge if one already exists
          // between the same source/target (matching handle too).
          const sourceHandle = (side === "true" || side === "false") ? side : null;
          const existing = edges.find((ed) =>
            ed.source === event.event_id && ed.target === tgtId &&
            (ed.source_handle || null) === (sourceHandle || null)
          );
          if (existing) {
            toast("Already connected");
            setConnectSrc(null); setCursorPos(null); return;
          }
          // Exclusive paths — for condition true/false branches, each branch
          // can attach to ONLY ONE downstream node. If a prior edge exists
          // from this branch, replace it (delete-then-create) so the user
          // gets a clean re-route. (User request: "True and False must only
          // be allowed to attach to one distinct node each.")
          if (sourceHandle === "true" || sourceHandle === "false") {
            const prior = edges.find((ed) =>
              ed.source === event.event_id && (ed.source_handle || null) === sourceHandle
            );
            if (prior) {
              try { await api.delete(`/workspaces/${current.workspace_id}/edges/${prior.edge_id}`); }
              catch { /* ignore — server will accept the new edge or reject */ }
            }
          }
          try {
            const body = { source: event.event_id, target: tgtId };
            if (sourceHandle) body.source_handle = sourceHandle;
            const { data: newEdge } = await api.post(`/workspaces/${current.workspace_id}/edges`, body);
            // Flash the new edge with a draw-on animation
            if (newEdge?.edge_id) {
              setNewEdgeId(newEdge.edge_id);
              setTimeout(() => setNewEdgeId(null), 900);
            }
            // Auto-trigger: if the target is an action node and the source isn't
            // already a trigger/action/condition, promote it to trigger automatically.
            const targetEvent = events.find(ev => ev.event_id === tgtId);
            if (targetEvent?.type === "action" && event.type !== "trigger" && event.type !== "action" && event.type !== "condition") {
              try {
                await api.put(`/workspaces/${current.workspace_id}/events/${event.event_id}`, { type: "trigger" });
              } catch { /* non-fatal */ }
            }
            load();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Cannot connect");
          }
        }
      }
      setConnectSrc(null); setCursorPos(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const onAddNote = async (event_id, content) => {
    await api.post(`/workspaces/${current.workspace_id}/events/${event_id}/notes`, { content });
    load();
  };
  const onDeleteNote = async (event_id, note_id) => {
    await api.delete(`/workspaces/${current.workspace_id}/events/${event_id}/notes/${note_id}`);
    load();
  };
  // Add an existing event as a child of a sequence container (new node type).
  const onAddToSequence = async (seq_event_id, child_event_id) => {
    if (!current) return;
    const seq = events.find((e) => e.event_id === seq_event_id);
    const child = events.find((e) => e.event_id === child_event_id);
    if (!seq || !child) return;
    // Sequences only accept ACTION children (user requirement).
    if (child.type !== "action") {
      toast.error("Only action nodes can be nested into a sequence");
      return;
    }
    const kids = Array.isArray(seq.children_event_ids) ? seq.children_event_ids : [];
    if (kids.includes(child_event_id)) return;
    await api.put(`/workspaces/${current.workspace_id}/events/${seq_event_id}`, {
      children_event_ids: [...kids, child_event_id],
    });
    /* silent: added to sequence */
    load();
  };

  // Remove a child action from a sequence container (inverse of onAddToSequence).
  const onRemoveFromSequence = async (seq_event_id, child_event_id) => {
    if (!current) return;
    const seq = events.find((e) => e.event_id === seq_event_id);
    if (!seq) return;
    const kids = Array.isArray(seq.children_event_ids) ? seq.children_event_ids : [];
    const next = kids.filter((id) => id !== child_event_id);
    await api.put(`/workspaces/${current.workspace_id}/events/${seq_event_id}`, {
      children_event_ids: next,
    });
    load();
  };

  // Toggle a task's completed_at timestamp from the inline checkbox in the
  // event block header. Tasks only. Optimistic update for snappy UX.
  const onToggleComplete = async (ev) => {
    if (!current) return;
    const next = ev.completed_at ? null : new Date().toISOString();
    setEvents((prev) => prev.map((e) => e.event_id === ev.event_id ? { ...e, completed_at: next } : e));
    try {
      await api.put(`/workspaces/${current.workspace_id}/events/${ev.event_id}`, { completed_at: next });
      // Push status to connected integrations (ClickUp, Slack)
      if (next) {
        api.post(`/workspaces/${current.workspace_id}/events/${ev.event_id}/sync-status`).catch(() => {});
        // Dependency unlock: find tasks that depend on this one and unblock them
        const dependents = events.filter(e =>
          Array.isArray(e.depends_on) && e.depends_on.includes(ev.event_id) && e.status === "blocked"
        );
        for (const dep of dependents) {
          // Check if ALL dependencies of this task are now complete
          const allDone = dep.depends_on.every(depId => {
            if (depId === ev.event_id) return true; // just completed
            const depEv = events.find(e => e.event_id === depId);
            return depEv?.completed_at;
          });
          if (allDone) {
            setEvents(prev => prev.map(e => e.event_id === dep.event_id ? { ...e, status: "todo" } : e));
            api.put(`/workspaces/${current.workspace_id}/events/${dep.event_id}`, { status: "todo" }).catch(() => {});
            toast(`"${dep.title}" unlocked`, { icon: "🔓" });
          }
        }
      }
    } catch {
      load();
    }
  };

  // Create a NEW action node directly inside a sequence — no need to place
  // it on the calendar first and then drag it in (user request). Opens the
  // edit dialog so the user can name the action and pick its kind.
  const onCreateChildInSequence = async (seq_event_id) => {
    if (!current) return;
    const seq = events.find((e) => e.event_id === seq_event_id);
    if (!seq) return;
    const body = {
      title: "",
      type: "action",
      start: seq.start,
      end: seq.start,
      action_config: { type: "notify", message: "" },
    };
    let created = null;
    try {
      const { data } = await api.post(`/workspaces/${current.workspace_id}/events`, body);
      created = data;
      const kids = Array.isArray(seq.children_event_ids) ? seq.children_event_ids : [];
      await api.put(`/workspaces/${current.workspace_id}/events/${seq_event_id}`, {
        children_event_ids: [...kids, data.event_id],
      });
      setEditing(data);
      setDialogOpen(true);
    } catch (err) {
      // Roll back the orphan action so it doesn't dangle on the calendar.
      if (created?.event_id) {
        try { await api.delete(`/workspaces/${current.workspace_id}/events/${created.event_id}`); } catch {}
      }
      toast.error("Couldn't add to sequence");
    }
    load();
  };

  // Drag a sequence child out of its parent onto a day slot — removes the
  // child from the sequence and re-times it to the drop slot (user request:
  // "grab and drop outside to remove it from sequence").
  const onDetachFromSequence = async (seq_event_id, child_event_id, dropDate) => {
    if (!current) return;
    const seq = events.find((e) => e.event_id === seq_event_id);
    if (!seq) return;
    const kids = (seq.children_event_ids || []).filter((id) => id !== child_event_id);
    const newStart = dropDate.toISOString();
    const newEnd = dropDate.toISOString();
    try {
      await api.put(`/workspaces/${current.workspace_id}/events/${seq_event_id}`, { children_event_ids: kids });
      await api.put(`/workspaces/${current.workspace_id}/events/${child_event_id}`, { start: newStart, end: newEnd });
    } catch { /* fallthrough */ }
    load();
  };

  // Reorder a child within its parent sequence via drag-drop (user request:
  // "i should be able to reorder the nodes inside"). `before` = drop on top
  // half of `targetCid` → insert before; false = bottom half → insert after.
  const onReorderSequenceChild = async (seq_event_id, movedCid, targetCid, before) => {
    if (!current) return;
    const seq = events.find((e) => e.event_id === seq_event_id);
    if (!seq) return;
    const kids = [...(seq.children_event_ids || [])];
    const fromIdx = kids.indexOf(movedCid);
    if (fromIdx === -1) return;
    kids.splice(fromIdx, 1);
    let toIdx = kids.indexOf(targetCid);
    if (toIdx === -1) return;
    if (!before) toIdx += 1;
    kids.splice(toIdx, 0, movedCid);
    // Optimistic update.
    setEvents((prev) => prev.map((e) => e.event_id === seq_event_id ? { ...e, children_event_ids: kids } : e));
    try {
      await api.put(`/workspaces/${current.workspace_id}/events/${seq_event_id}`, { children_event_ids: kids });
    } catch {
      load();
    }
  };


  const onSelect = useCallback((id, { additive } = {}) => {
    setSelectedIds((prev) => {
      const next = new Set(additive ? prev : []);
      if (additive && prev.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Rubber-band selection on empty space
  const boxStartRef = useRef(null);
  const onContainerPointerDown = (e) => {
    if (e.target.closest("[data-event-id]")) return;
    if (e.target.closest("[data-testid^='slot-']")) {
      // allow slot click only if minimal movement - handled in onSlotClick via onClick; here set up rubber-band
    }
    // Close any open context menu or connection in progress, deselect edges.
    setCtxMenu(null);
    if (connectSrc) setConnectSrc(null);
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + containerRef.current.scrollLeft;
    const y = e.clientY - rect.top + containerRef.current.scrollTop;
    boxStartRef.current = { x, y, moved: false, additive: e.shiftKey };
    setSelectedEdgeId(null);
    if (!e.shiftKey) setSelectedIds(new Set());
    // Deselect the active class too — clicking empty calendar space should
    // drop any class filter the user had applied.
    if (activeClassId) setActiveClassId?.(null);
  };
  const onContainerPointerMove = (e) => {
    if (!boxStartRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + containerRef.current.scrollLeft;
    const y = e.clientY - rect.top + containerRef.current.scrollTop;
    const dx = x - boxStartRef.current.x; const dy = y - boxStartRef.current.y;
    if (!boxStartRef.current.moved && Math.hypot(dx, dy) > 6) boxStartRef.current.moved = true;
    if (boxStartRef.current.moved) {
      const box = {
        x: Math.min(x, boxStartRef.current.x), y: Math.min(y, boxStartRef.current.y),
        w: Math.abs(dx), h: Math.abs(dy),
      };
      setSelectionBox(box);
      // compute hits
      const cRect = containerRef.current.getBoundingClientRect();
      const hits = new Set(boxStartRef.current.additive ? selectedIds : []);
      eventRefs.current.forEach((el, id) => {
        const r = el.getBoundingClientRect();
        const ex = r.left - cRect.left + containerRef.current.scrollLeft;
        const ey = r.top - cRect.top + containerRef.current.scrollTop;
        if (ex + r.width >= box.x && ex <= box.x + box.w && ey + r.height >= box.y && ey <= box.y + box.h) {
          hits.add(id);
        }
      });
      setSelectedIds(hits);
    }
  };
  const onContainerPointerUp = () => {
    boxStartRef.current = null;
    setSelectionBox(null);
  };

  // Nodes nested inside any sequence's `children_event_ids`. We hide these
  // from the standalone calendar render so they only appear inside their
  // parent sequence's mini-block (user request).
  const nestedChildIds = useMemo(() => {
    const s = new Set();
    events.forEach((e) => {
      if (e.type === "sequence" && Array.isArray(e.children_event_ids)) {
        e.children_event_ids.forEach((cid) => s.add(cid));
      }
    });
    return s;
  }, [events]);

  // Context menu handlers
  const outgoingMap = useMemo(() => {
    const m = new Map();
    edges.forEach((ed) => m.set(ed.source, true));
    return m;
  }, [edges]);

  const unusedNodeSet = useMemo(() => {
    const edgeSources = new Set(edges.map(e => e.source));
    const edgeTargets = new Set(edges.map(e => e.target));
    const s = new Set();
    events.forEach(ev => {
      if (ZERO_DURATION_TYPES.has(ev.type) && !edgeSources.has(ev.event_id) && !edgeTargets.has(ev.event_id)) {
        s.add(ev.event_id);
      }
    });
    return s;
  }, [events, edges]);

  const onContextMenu = (e, event) => {
    e.preventDefault();
    e.stopPropagation();
    // Clamp so the menu never opens off-screen. Menu is ~220w × ~420h in the worst case.
    const MENU_W = 220, MENU_H = 440;
    const x = Math.min(e.clientX, window.innerWidth - MENU_W - 8);
    const y = Math.min(e.clientY, window.innerHeight - MENU_H - 8);
    setCtxMenu({ x: Math.max(8, x), y: Math.max(8, y), event });
    if (!selectedIds.has(event.event_id)) {
      setSelectedIds(new Set([event.event_id]));
    }
  };

  // Right-click on empty slot → quick "add node" menu seeded at that time.
  const onSlotContextMenu = (e, dt) => {
    e.preventDefault(); e.stopPropagation();
    const MENU_W = 180, MENU_H = 240;
    const x = Math.min(e.clientX, window.innerWidth - MENU_W - 8);
    const y = Math.min(e.clientY, window.innerHeight - MENU_H - 8);
    setCtxMenu({ x: Math.max(8, x), y: Math.max(8, y), slot: dt });
  };
  useEffect(() => {
    const close = () => { setCtxMenu(null); setCtxSubmenu(null); };
    window.addEventListener("click", close);
    // Note: we intentionally don't close on scroll so users can scroll through
    // long right-click menus without the menu collapsing (issue #2).
    return () => { window.removeEventListener("click", close); };
  }, []);

  // Tick once per minute (aligned to the minute mark) so the "Saved Xs ago"
  // indicator updates predictably rather than only on interaction.
  useEffect(() => {
    let intervalId = null;
    const msToNextMinute = 60000 - (Date.now() % 60000);
    const firstId = setTimeout(() => {
      setTick((t) => t + 1);
      intervalId = setInterval(() => setTick((t) => t + 1), 60000);
    }, msToNextMinute);
    return () => { clearTimeout(firstId); if (intervalId) clearInterval(intervalId); };
  }, []);

  const convertType = async (newType) => {
    if (!ctxMenu || !current) return;
    const ids = selectedIds.size > 0 ? [...selectedIds] : [ctxMenu.event.event_id];
    await Promise.all(ids.map((id) => api.put(`/workspaces/${current.workspace_id}/events/${id}`, { type: newType })));
    /* silent: converted */
    load();
  };
  const copyText = (text) => {
    navigator.clipboard?.writeText(text || "");
    /* silent: copied */
  };

  const navigate = (dir) => {
    if (view === "day") setCursor(addDays(cursor, dir));
    else if (view === "week") setCursor(dir > 0 ? addWeeks(cursor, 1) : subWeeks(cursor, 1));
    else setCursor(dir > 0 ? addMonths(cursor, 1) : subMonths(cursor, 1));
  };

  const title = useMemo(() => {
    if (view === "day") return format(cursor, "EEEE, MMMM d, yyyy");
    if (view === "week") {
      const s = startOfWeek(cursor, { weekStartsOn });
      return `${format(s, "MMMM d")} – ${format(addDays(s, 6), "MMMM d, yyyy")}`;
    }
    return format(cursor, "MMMM yyyy");
  }, [cursor, view, weekStartsOn]);

  // Number of days shown scales inversely with zoom — zooming out reveals
  // more days rather than just making columns narrower and leaving empty space.
  const visibleDaysCount = daysInView;

  const weekDays = useMemo(() => {
    const s = startOfWeek(cursor, { weekStartsOn });
    return Array.from({ length: visibleDaysCount }, (_, i) => addDays(s, i));
  }, [cursor, weekStartsOn, visibleDaysCount]);

  const edgeShapes = useMemo(() => {
    void tick;
    const container = containerRef.current;
    if (!container) return [];
    const cRect = container.getBoundingClientRect();
    return edges.map((edge) => {
      const s = eventRefs.current.get(edge.source);
      const t = eventRefs.current.get(edge.target);
      if (!s || !t) return null;
      const sr = s.getBoundingClientRect();
      const tr = t.getBoundingClientRect();

      const sx = sr.right - cRect.left + container.scrollLeft;
      let sy = sr.top + sr.height / 2 - cRect.top + container.scrollTop;
      if (edge.source_handle === "true")  sy = sr.top + 9  - cRect.top + container.scrollTop;
      else if (edge.source_handle === "false") sy = sr.top + 23 - cRect.top + container.scrollTop;
      const tx = tr.right - cRect.left + container.scrollLeft;
      const ty = tr.top + tr.height / 2 - cRect.top + container.scrollTop;

      // Edge shape — matches the reference screenshots:
      // CLOSE (|dy| < 80px): tight bracket — exits source right, makes one
      //   right-angle turn with a small rounded corner, enters target right.
      // FAR (|dy| >= 80px): smooth S-curve — exits source right, enters
      //   target left side, creating a natural arc between the two nodes.
      const dx = tx - sx;
      const dy = ty - sy;
      const absDy = Math.abs(dy);
      const dirY = dy >= 0 ? 1 : -1;
      let d;
      if (absDy < 80) {
        // CLOSE — bracket: source.right → 28px right → down/up → target.right
        // midX sits just past the rightmost of the two nodes
        const midX = Math.max(sx, tx) + 28;
        const r = Math.min(10, absDy / 2); // shrink radius when very close
        d = [
          `M ${sx} ${sy}`,
          `H ${midX - r}`,
          `Q ${midX} ${sy} ${midX} ${sy + dirY * r}`,
          `V ${ty - dirY * r}`,
          `Q ${midX} ${ty} ${midX - r} ${ty}`,
          `H ${tx}`,
        ].join(" ");
      } else {
        // FAR — S-curve: exit right from source, enter from LEFT of target
        // Using cubic bezier with horizontal tangents at both endpoints.
        // Source exits rightward (+offset), target is approached from the left (-offset).
        const offset = Math.max(50, absDy * 0.4, Math.abs(dx) * 0.5);
        d = `M ${sx} ${sy} C ${sx + offset} ${sy}, ${tx - offset} ${ty}, ${tx} ${ty}`;
      }

      const BRAND = "#5A90F5";
      const src  = events.find((e) => e.event_id === edge.source);
      const tgt  = events.find((e) => e.event_id === edge.target);
      // Use node type color → custom color → class color → brand blue
      let colorA = src?.custom_color || classColorMap.get(src?.event_id) || NODE_TYPE_COLORS[src?.type] || BRAND;
      let colorB = tgt?.custom_color || classColorMap.get(tgt?.event_id) || NODE_TYPE_COLORS[tgt?.type] || BRAND;
      if (edge.source_handle === "true")  { colorA = "#6EE7B7"; colorB = "#6EE7B7"; }
      else if (edge.source_handle === "false") { colorA = "#FCA5A5"; colorB = "#FCA5A5"; }

      const gradId = `eg-${edge.edge_id}`;
      // Midpoint for inline label
      const mx = (sx + tx) / 2;
      const my = (sy + ty) / 2;
      return { id: edge.edge_id, d, colorA, colorB, gradId, sx, sy, tx, ty, mx, my, branch: edge.source_handle, triggerLabel: edge.trigger_label, conditionLabel: edge.condition_label };
    }).filter(Boolean);
  }, [edges, events, tick, classColorMap]);

  const liveConnect = useMemo(() => {
    if (!connectSrc || !cursorPos) return null;
    const s = eventRefs.current.get(connectSrc.event.event_id);
    if (!s) return null;
    const container = containerRef.current;
    if (!container) return null;
    const cRect = container.getBoundingClientRect();
    const sr = s.getBoundingClientRect();
    // All connections originate from the RIGHT side of the source node
    const sx = sr.right - cRect.left + container.scrollLeft;
    let sy = sr.top + sr.height / 2 - cRect.top + container.scrollTop;
    if (connectSrc.side === "true") sy = sr.top + 8 - cRect.top + container.scrollTop;
    else if (connectSrc.side === "false") sy = sr.bottom - 8 - cRect.top + container.scrollTop;
    const color = connectSrc.event.custom_color || classColorMap.get(connectSrc.event.event_id) || "#5A90F5";
    const tx = cursorPos.x, ty = cursorPos.y;
    const baseOffset = Math.max(60, Math.abs(tx - sx) * 0.5);
    const d = `M ${sx} ${sy} C ${sx + baseOffset} ${sy}, ${tx + baseOffset} ${ty}, ${tx} ${ty}`;
    return { d, color };
  }, [connectSrc, cursorPos]);

  return (
    <div className="h-full flex flex-col overflow-hidden" data-cresc-cal-canvas data-cresc-cal-root
      style={{ background: mainPanelStyle.background || "#0A0A0A" }}>
      {/* Workspace header */}
      {current && (
        <div className="flex items-center gap-3 px-6 py-4 border-b border-[#2A2A2A] bg-[#0A0A0A]" data-testid="workspace-header" data-cresc-topbar style={topbarStyle}>
          {/* Left: workspace switcher + description — takes all available space, truncates before right buttons */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
          <Popover open={!!wsDropOpen} onOpenChange={setWsDropOpen}>
            <PopoverTrigger asChild>
              <button
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#1a1a1a] text-left flex-shrink-0"
                data-testid="ws-header-switcher"
                title="Switch workspace"
                onClick={() => setWsDropOpen(v => !v)}
              >
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: current.color }} />
                <h1 className="font-display text-sm font-semibold tracking-tight text-[#EDEDED]">{current.name}</h1>
                <ChevronDown className="w-3.5 h-3.5 text-[#A1A1AA]" />
              </button>
            </PopoverTrigger>
            {current.description && (
              <span
                className="text-[11px] text-[#52525B] truncate min-w-0 flex-1 hidden sm:block"
                title={current.description}
                data-testid="ws-inline-description"
              >{current.description}</span>
            )}
            <PopoverContent
              align="start"
              className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] p-0 w-80"
              data-testid="ws-header-popover"
            >
              {current.description && (
                <div
                  className="px-3 py-2 text-[11px] text-[#A1A1AA] border-b border-[#2A2A2A] line-clamp-2"
                  title={current.description}
                  data-testid="ws-header-description"
                >
                  {current.description}
                </div>
              )}
              <div className="max-h-[60vh] overflow-y-auto p-2 space-y-0.5" data-testid="ws-header-list">
                {/* Workspace folders + their nested workspaces. */}
                {folders.filter((f) => f.kind === "workspace").map((f) => {
                  const expanded = wsFolderExpanded.has(f.folder_id);
                  const kids = workspaces.filter((w) => w.parent_folder_id === f.folder_id);
                  const isRenaming = wsFolderRename?.id === f.folder_id;
                  return (
                    <div key={f.folder_id} data-testid={`ws-header-folder-${f.folder_id}`}
                      onDragOver={(e) => {
                        if (e.dataTransfer.types.includes("application/x-crescendo-ws-id")) {
                          e.preventDefault(); e.dataTransfer.dropEffect = "move";
                          e.currentTarget.classList.add("drop-target");
                        }
                      }}
                      onDragLeave={(e) => e.currentTarget.classList.remove("drop-target")}
                      onDrop={async (e) => {
                        e.currentTarget.classList.remove("drop-target");
                        const wsId = e.dataTransfer.getData("application/x-crescendo-ws-id");
                        if (!wsId) return;
                        try { await api.put(`/workspaces/${wsId}`, { parent_folder_id: f.folder_id }); setWorkspaces(prev => prev.map(w => w.workspace_id === wsId ? { ...w, parent_folder_id: f.folder_id } : w)); } catch {}
                      }}
                    >
                      <div className="group w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-[#A1A1AA] hover:bg-[#151515]">
                        <button onClick={() => toggleWsFolder(f.folder_id)} className="flex items-center gap-2 flex-1 min-w-0">
                          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "" : "-rotate-90"}`} />
                          <FolderIcon className="w-3 h-3" style={{ color: f.color || undefined }} />
                          {isRenaming ? (
                            <input
                              autoFocus
                              value={wsFolderRename.value}
                              onChange={(ev) => setWsFolderRename({ ...wsFolderRename, value: ev.target.value })}
                              onClick={(ev) => ev.stopPropagation()}
                              onBlur={async () => {
                                const v = wsFolderRename.value.trim();
                                if (v && v !== f.name) {
                                  try { await api.put(`/folders/${f.folder_id}`, { name: v }); window.dispatchEvent(new CustomEvent("crescendo:folders-changed")); } catch {}
                                }
                                setWsFolderRename(null);
                              }}
                              onKeyDown={(ev) => { if (ev.key === "Enter") ev.currentTarget.blur(); if (ev.key === "Escape") setWsFolderRename(null); }}
                              className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1 text-xs outline-none focus:border-[#5A90F5]"
                              data-testid={`ws-header-folder-rename-${f.folder_id}`}
                            />
                          ) : (
                            <span className="flex-1 truncate text-xs text-left">{f.name}</span>
                          )}
                          <span className="text-[10px] text-[#52525B]">{kids.length}</span>
                        </button>
                        <button
                          onClick={(ev) => { ev.stopPropagation(); setWsFolderRename({ id: f.folder_id, value: f.name }); }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]"
                          title="Rename folder"
                          data-testid={`ws-header-folder-rename-btn-${f.folder_id}`}
                        >
                          <FileEdit className="w-3 h-3" />
                        </button>
                        <button
                          onClick={async (ev) => {
                            ev.stopPropagation();
                            if (kids.length > 0) return; // safety: don't let popover delete folders with children
                            try { await api.delete(`/folders/${f.folder_id}`); window.dispatchEvent(new CustomEvent("crescendo:folders-changed")); } catch {}
                          }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]"
                          title={kids.length > 0 ? "Empty the folder first" : "Delete empty folder"}
                          data-testid={`ws-header-folder-delete-${f.folder_id}`}
                        >
                          <Trash2 className={`w-3 h-3 ${kids.length === 0 ? "text-[#FF3B30]" : "text-[#52525B]"}`} />
                        </button>
                      </div>
                      {expanded && kids.map((w, idx) => (
                        <div
                          key={w.workspace_id}
                          className={`group w-full flex items-center gap-1 px-2 py-1.5 rounded ml-3 ${current.workspace_id === w.workspace_id ? "bg-[#1a1a1a]" : "hover:bg-[#151515]"}`}
                          data-testid={`ws-header-row-${w.workspace_id}`}
                        >
                          <button
                            draggable
                            onDragStart={(e) => { e.dataTransfer.setData("application/x-crescendo-ws-id", w.workspace_id); e.dataTransfer.effectAllowed = "move"; }}
                            onClick={() => setCurrent(w.workspace_id)}
                            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setWsDropOpen(false); setWsCtxMenu({ x: e.clientX, y: e.clientY, ws: w }); }}
                            className="flex-1 min-w-0 flex items-center gap-2 text-left"
                            data-testid={`ws-header-item-${w.workspace_id}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: w.color }} />
                            <span className="flex-1 truncate text-xs">{w.name}</span>
                            {current.workspace_id === w.workspace_id && <Check className="w-3 h-3 text-[#5A90F5]" />}
                          </button>
                          <button
                            type="button"
                            disabled={idx === 0}
                            onClick={(e) => { e.stopPropagation(); moveWorkspace(w.workspace_id, "up"); }}
                            className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a] disabled:opacity-20 disabled:hover:bg-transparent"
                            title="Move up"
                            data-testid={`ws-header-up-${w.workspace_id}`}
                          ><ChevronUp className="w-3 h-3 text-[#A1A1AA]" /></button>
                          <button
                            type="button"
                            disabled={idx === kids.length - 1}
                            onClick={(e) => { e.stopPropagation(); moveWorkspace(w.workspace_id, "down"); }}
                            className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a] disabled:opacity-20 disabled:hover:bg-transparent"
                            title="Move down"
                            data-testid={`ws-header-down-${w.workspace_id}`}
                          ><ChevronDown className="w-3 h-3 text-[#A1A1AA]" /></button>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {/* Top-level (un-foldered) workspaces. */}
                <div
                  onDragOver={(e) => {
                    if (e.dataTransfer.types.includes("application/x-crescendo-ws-id")) {
                      e.preventDefault(); e.dataTransfer.dropEffect = "move";
                    }
                  }}
                  onDrop={async (e) => {
                    const wsId = e.dataTransfer.getData("application/x-crescendo-ws-id");
                    if (!wsId) return;
                    try { await api.put(`/workspaces/${wsId}`, { parent_folder_id: null }); setWorkspaces(prev => prev.map(w => w.workspace_id === wsId ? { ...w, parent_folder_id: null } : w)); } catch {}
                  }}
                >
                  {(() => {
                    const rootWs = workspaces.filter((w) => !w.parent_folder_id);
                    return rootWs.map((w, idx) => (
                      <div
                        key={w.workspace_id}
                        className={`group w-full flex items-center gap-1 px-2 py-1.5 rounded ${current.workspace_id === w.workspace_id ? "bg-[#1a1a1a]" : "hover:bg-[#151515]"}`}
                        data-testid={`ws-header-row-${w.workspace_id}`}
                      >
                        <button
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData("application/x-crescendo-ws-id", w.workspace_id); e.dataTransfer.effectAllowed = "move"; }}
                          onClick={() => setCurrent(w.workspace_id)}
                          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setWsDropOpen(false); setWsCtxMenu({ x: e.clientX, y: e.clientY, ws: w }); }}
                          className="flex-1 min-w-0 flex items-center gap-2 text-left"
                          data-testid={`ws-header-item-${w.workspace_id}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: w.color }} />
                          <span className="flex-1 truncate text-xs">{w.name}</span>
                          {current.workspace_id === w.workspace_id && <Check className="w-3 h-3 text-[#5A90F5]" />}
                        </button>
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={(e) => { e.stopPropagation(); moveWorkspace(w.workspace_id, "up"); }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a] disabled:opacity-20 disabled:hover:bg-transparent"
                          title="Move up"
                          data-testid={`ws-header-up-${w.workspace_id}`}
                        ><ChevronUp className="w-3 h-3 text-[#A1A1AA]" /></button>
                        <button
                          type="button"
                          disabled={idx === rootWs.length - 1}
                          onClick={(e) => { e.stopPropagation(); moveWorkspace(w.workspace_id, "down"); }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#2a2a2a] disabled:opacity-20 disabled:hover:bg-transparent"
                          title="Move down"
                          data-testid={`ws-header-down-${w.workspace_id}`}
                        ><ChevronDown className="w-3 h-3 text-[#A1A1AA]" /></button>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              <div className="border-t border-[#2A2A2A] p-1 bg-[#141414] flex flex-col">
                {/* Overlay all calendars (multi-workspace conflict view).
                    User request: "Overlap all calendars" P2. */}
                <button
                  onClick={() => setOverlayAll((v) => !v)}
                  className={`px-2 py-1.5 rounded text-xs flex items-center gap-2 ${overlayAll ? "bg-[#5A90F5]/15 text-[#5A90F5]" : "text-[#EDEDED] hover:bg-[#1a1a1a]"}`}
                  data-testid="ws-header-overlay-all"
                >
                  <Globe className="w-3.5 h-3.5" /> Overlay
                </button>
                <button
                  onClick={() => { window.dispatchEvent(new CustomEvent("crescendo:open-new-workspace")); }}
                  className="px-2 py-1.5 rounded text-xs hover:bg-[#1a1a1a] flex items-center gap-2 text-[#EDEDED]"
                  data-testid="ws-header-new"
                >
                  <Plus className="w-3.5 h-3.5" /> New workspace
                </button>
                <button
                  onClick={async () => {
                    // Create a workspace folder directly so the popover gets
                    // immediate feedback (the previous implementation
                    // dispatched a sidebar event that was a no-op now that
                    // the sidebar's workspaces section was removed).
                    try {
                      const { data } = await api.post(`/folders`, { kind: "workspace", name: "New folder" });
                      setWsFolderExpanded((s) => { const n = new Set(s); n.add(data.folder_id); return n; });
                      setWsFolderRename({ id: data.folder_id, value: data.name });
                      window.dispatchEvent(new CustomEvent("crescendo:folders-changed"));
                    } catch { /* silent */ }
                  }}
                  className="px-2 py-1.5 rounded text-xs hover:bg-[#1a1a1a] flex items-center gap-2 text-[#EDEDED]"
                  data-testid="ws-header-new-folder"
                >
                  <FolderPlus className="w-3.5 h-3.5" /> New folder
                </button>
              </div>
            </PopoverContent>
          </Popover>
          </div>
          {/* Right-side: save indicator + history + settings */}
          {/* Save indicator */}
          <div className="flex items-center gap-1.5 text-[11px] text-[#A1A1AA]" data-testid="save-indicator">
            {isSaving ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-[#A1A1AA]" />
                <span>Saving…</span>
              </>
            ) : savedAt ? (
              <>
                <Cloud className="w-3.5 h-3.5 text-[#A1A1AA]" />
                <span>Saved {formatRelativeSave(savedAt)}</span>
              </>
            ) : (
              <>
                <Cloud className="w-3.5 h-3.5 text-[#A1A1AA]" />
                <span>Up To Date</span>
              </>
            )}
          </div>
          {/* Share — opens the share popover for the CURRENT workspace.
              User request (iter26): swap with Save and use the outlined
              style; Save now carries the white-fill (matching the
              Documents header pattern). */}
          <button
            onClick={() => {
                if (!current) { toast.error("No workspace loaded"); return; }
                window.dispatchEvent(new CustomEvent("crescendo:open-share", { detail: { workspace: current } }));
              }}
            className="text-[11px] px-2.5 py-1 rounded border border-[#2A2A2A] hover:bg-[#1a1a1a] text-[#EDEDED] flex items-center gap-1"
            data-testid="ws-header-share"
            title="Share workspace"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
          <button
            onClick={saveNow}
            className="text-[11px] px-2.5 py-1 rounded bg-white text-[#0A0A0A] font-semibold hover:bg-[#E7ECFA] flex items-center gap-1"
            data-testid="save-now-btn"
            title="Save snapshot now"
          >
            Save
          </button>
          {/* Notices — next to save per design spec */}
          <div className="relative">
            <button
              onClick={() => { setNoticesOpen(v => !v); if (!noticesOpen) { setHistoryTab("notices"); loadRunHistory(); } }}
              className={`px-2 py-1 rounded border text-[10px] transition-colors flex items-center gap-1 ${noticesOpen ? "border-[#5A90F5] text-[#5A90F5] bg-[#5A90F5]/10" : notices.length > 0 ? "border-[#FF9500] text-[#FF9500] bg-[#FF9500]/10 hover:bg-[#FF9500]/20" : "border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA] hover:bg-[#1a1a1a]"}`}
              title="Notices & Run History"
            >
              <AlertTriangle className="w-3 h-3" />
              {notices.length > 0 && <span className="font-mono">{notices.length}</span>}
            </button>
            {noticesOpen && (
              <>
                <div className="fixed inset-0 z-[9980]" onClick={() => setNoticesOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-[9981] bg-[#141414] border border-[#2A2A2A] rounded-md shadow-xl w-80 overflow-hidden">
                  <div className="flex border-b border-[#2A2A2A]">
                    {["notices","history"].map(tab => (
                      <button key={tab} onClick={() => { setHistoryTab(tab); if (tab==="history") loadRunHistory(); }}
                        className={`flex-1 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider transition-colors ${historyTab===tab ? "text-[#EDEDED] border-b-2 border-[#5A90F5]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>
                        {tab === "notices" ? `Notices${notices.length > 0 ? ` (${notices.length})` : ""}` : "Run History"}
                      </button>
                    ))}
                  </div>
                  {historyTab === "notices" ? (
                    notices.length === 0 ? (
                      <div className="px-3 py-4 text-[11px] text-[#52525B] text-center">No issues found</div>
                    ) : (
                      <div className="max-h-64 overflow-y-auto">
                        {notices.map(n => (
                          <button key={n.id} onClick={() => {
                            setNoticesOpen(false);
                            if (!n.eventId) return;
                            const el = document.querySelector(`[data-event-id="${n.eventId}"]`) || document.querySelector(`[data-testid*="${n.eventId}"]`);
                            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                          }} className="w-full flex items-start gap-2 px-3 py-2 hover:bg-[#1a1a1a] text-left border-b border-[#1a1a1a] last:border-0 transition-colors">
                            {n.type === "warning" ? <AlertTriangle className="w-3 h-3 text-[#FF9500] flex-shrink-0 mt-0.5" /> : <Info className="w-3 h-3 text-[#5A90F5] flex-shrink-0 mt-0.5" />}
                            <span className="text-[11px] text-[#A1A1AA] leading-snug">{n.message}</span>
                          </button>
                        ))}
                      </div>
                    )
                  ) : (
                    <div className="max-h-64 overflow-y-auto">
                      {runHistory.length === 0 ? (
                        <div className="px-3 py-4 text-[11px] text-[#52525B] text-center">No automation runs yet</div>
                      ) : runHistory.map((h, i) => {
                        const ts = h.fired_at ? new Date(h.fired_at) : null;
                        const rel = ts ? (Date.now() - ts < 60000 ? "just now" : ts.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})) : "";
                        const kindLabel = { complete_task:"✓ Completed", toggle_pin:"📌 Pinned", reschedule:"⏩ Rescheduled", update_keypair:"🔑 Key updated", create_node:"➕ Created node", journal_log:"📓 Logged", notify:"🔔 Notified", slack_post:"💬 Slack post", duplicate_tomorrow:"📋 Duplicated", archive:"📦 Archived" }[h.kind] || (h.kind || "fired");
                        const triggerEv = events.find(e => e.event_id === h.trigger_id);
                        return (
                          <div key={i} className="flex items-start gap-2 px-3 py-2 border-b border-[#1a1a1a] last:border-0">
                            <Zap className="w-3 h-3 text-[#FF8A00] flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] text-[#EDEDED] truncate">{kindLabel}</div>
                              {triggerEv && <div className="text-[10px] text-[#52525B] truncate">via "{triggerEv.title || "trigger"}"</div>}
                            </div>
                            <span className="text-[10px] text-[#52525B] flex-shrink-0">{rel}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <button
            onClick={() => setLegendOpen(true)}
            className="w-6 h-6 flex items-center justify-center rounded border border-[#2A2A2A] hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED] text-[11px] font-semibold"
            title="Keyboard Shortcuts (press ?)"
            data-testid="legend-btn"
          >
            ?
          </button>
          {/* Settings cogwheel — moved to upper navbar next to ? */}
          <WorkspaceSettingsButton onOpenImport={() => setImportOpen(true)} />
        </div>
      )}
      {/* Toolbar — reference style: pill view toggle left, date nav right */}
      <div className="flex items-center justify-between px-6 py-3 flex-shrink-0" style={{ borderBottom: "1px solid var(--cresc-border-sub, #1E1E22)" }}>
        {/* Left: Week / Day / Month pill toggle */}
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: "var(--cresc-surface-2, #1C1C1F)" }}>
          {["week", "day", "month"].map(v => {
            const label = v.charAt(0).toUpperCase() + v.slice(1);
            const active = (v === "day" ? daysInView === 1 : view === v && daysInView !== 1);
            return (
              <button key={v}
                onClick={(e) => { e.stopPropagation(); if (v === "day") { setDaysInView(1); setView("week"); } else { setView(v); setDaysInView(v === "week" ? 7 : daysInView); } }}
                className="px-4 py-1.5 rounded-lg text-[13px] font-medium transition-all"
                style={{ background: active ? "var(--cresc-surface-3, #222226)" : "transparent", color: active ? "var(--cresc-text, #F0F0F0)" : "var(--cresc-text-3, #56565E)" }}
                data-testid={`view-${v}`}>
                {label}
              </button>
            );
          })}
        </div>

        {/* Right: date range + prev/next */}
        <div className="flex items-center gap-3">
          <button onClick={(e) => { e.stopPropagation(); const now = new Date(); setCursor(startOfDay(now)); requestAnimationFrame(() => { const minutes = now.getHours() * 60 + now.getMinutes(); const y = (minutes / 60) * HOUR_HEIGHT; containerRef.current?.scrollTo({ top: Math.max(0, y - 120), behavior: "smooth" }); }); }} className="text-[12px] font-medium px-3 py-1.5 rounded-lg transition-colors" style={{ border: "1px solid var(--cresc-border, #2A2A2E)", color: "var(--cresc-text-3, #56565E)" }} data-testid="calendar-today-btn">Today</button>
          <div className="flex items-center gap-1">
            <button onClick={(e) => { e.stopPropagation(); navigate(-1); }} className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors" style={{ background: "var(--cresc-surface-2, #1C1C1F)", color: "var(--cresc-text-3, #56565E)" }} data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></button>
            <h2 className="text-[14px] font-semibold px-2 min-w-[140px] text-center" style={{ color: "var(--cresc-text, #F0F0F0)" }} data-testid="calendar-title">{title}</h2>
            <button onClick={(e) => { e.stopPropagation(); navigate(1); }} className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors" style={{ background: "var(--cresc-surface-2, #1C1C1F)", color: "var(--cresc-text-3, #56565E)" }} data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

      </div>
      {!workHoursBarDrawnRef.current && (() => { workHoursBarDrawnRef.current = true; return null; })()}
      {(() => {
        const weekStart = startOfWeek(cursor, { weekStartsOn: weekStartsOn ?? 0 });
        const weekEnd = addDays(weekStart, 7);
        const weekEvents = events.filter(e =>
          !e.unscheduled && !e.completed_at &&
          new Date(e.start) >= weekStart && new Date(e.start) < weekEnd
        );
        const totalMinutes = weekEvents.reduce((sum, e) => {
          const dur = (Date.parse(e.end || e.start) - Date.parse(e.start)) / 60000;
          return sum + Math.max(0, Math.min(dur, 480)); // cap each event at 8h
        }, 0);
        const capacityHours = weekHours || 40;
        const usedHours = Math.round(totalMinutes / 60 * 10) / 10;
        const pct = Math.min(100, (usedHours / capacityHours) * 100);
        const color = pct > 90 ? "#FF3B30" : pct > 70 ? "#FF9500" : "#5A90F5";
        return (
          <div className="flex items-center gap-3 px-6 py-1.5 border-b border-[#1a1a1a] bg-[#0A0A0A]/60" data-testid="capacity-bar" style={structStyle}>
            <span className="text-[10px] text-[#52525B] flex-shrink-0">Week</span>
            <div className="flex-1 h-1 bg-[#1a1a1a] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="text-[10px] flex-shrink-0" style={{ color }}>{usedHours}h / {capacityHours}h</span>
          </div>
        );
      })()}
      {wsCtxMenu && createPortal(
        <>
          <div className="fixed inset-0 z-[9990]" onClick={() => setWsCtxMenu(null)} />
          <div className="sidebar-ctx" style={{ left: wsCtxMenu.x, top: wsCtxMenu.y, zIndex: 9991 }} onClick={(e) => e.stopPropagation()}>
            <div className="ctx-sub">{wsCtxMenu.ws.name}</div>
            <div className="ctx-item" onClick={() => { setCurrent(wsCtxMenu.ws.workspace_id); setWsCtxMenu(null); window.dispatchEvent(new CustomEvent("crescendo:open-workspace-settings")); }}>
              <Settings className="w-3.5 h-3.5" /> Edit Workspace
            </div>
            <div className="ctx-item" onClick={async () => {
              try {
                const { data } = await api.post(`/workspaces/${wsCtxMenu.ws.workspace_id}/guest-link`);
                const url = `${window.location.origin}/guest/${data.token}`;
                await navigator.clipboard.writeText(url);
                toast.success("Guest link copied to clipboard");
              } catch { toast.error("Failed to create guest link"); }
              setWsCtxMenu(null);
            }}>
              <Share2 className="w-3.5 h-3.5" /> Copy Guest Link
            </div>
            <div className="ctx-item" onClick={() => { moveWorkspace(wsCtxMenu.ws.workspace_id, "up"); setWsCtxMenu(null); }}>
              <ChevronUp className="w-3.5 h-3.5" /> Move Up
            </div>
            <div className="ctx-item" onClick={() => { moveWorkspace(wsCtxMenu.ws.workspace_id, "down"); setWsCtxMenu(null); }}>
              <ChevronDown className="w-3.5 h-3.5" /> Move Down
            </div>
            <div className="ctx-sep" />
            <div className="ctx-item text-[#FF3B30] hover:!bg-[#FF3B30]/15" onClick={async () => {
              const ok = await confirmDelete({ title: "Delete Workspace?", message: `"${wsCtxMenu.ws.name}" and all its nodes will be permanently removed.`, confirmLabel: "Delete Workspace", checkboxLabel: "I understand all nodes will be deleted" });
              if (!ok) { setWsCtxMenu(null); return; }
              try { await api.delete(`/workspaces/${wsCtxMenu.ws.workspace_id}`); window.dispatchEvent(new CustomEvent("crescendo:reload-workspaces")); window.dispatchEvent(new CustomEvent("crescendo:folders-changed")); } catch { toast.error("Delete failed"); }
              setWsCtxMenu(null);
            }}>
              <Trash2 className="w-3.5 h-3.5" /> Delete Workspace
            </div>
          </div>
        </>, document.body
      )}
      {importOpen && (
        <CalendarImportModal open={importOpen} onClose={() => { setImportOpen(false); load(); }} />
      )}

      {/* Pinned bar — pinned events render as minimized chips; click to open. */}
      {events.filter((e) => e.pinned && !e.completed_at && e.status !== "done" && new Date(e.end || e.start) >= new Date(Date.now() - 60000)).length > 0 && (
        <div className="border-b border-[#2A2A2A] bg-[#0A0A0A] px-4 py-2 flex items-center gap-2 overflow-x-auto" data-testid="pinned-bar" style={structStyle}>
          <Pin className="w-3 h-3 text-[#A1A1AA] flex-shrink-0" />
          {displayedEvents.filter((e) => e.pinned && !e.completed_at && e.status !== "done" && new Date(e.end || e.start) >= new Date(Date.now() - 60000)).map((ev) => {
            const c = classColorMap.get(ev.event_id) || ev.custom_color || "#5A90F5";
            const nowMs = Date.now();
            const isHappeningPin = Date.parse(ev.start) <= nowMs && Date.parse(ev.end || ev.start) >= nowMs;
            return (
              <button key={ev.event_id}
                onClick={() => onEventOpen(ev)}
                className="flex items-center gap-2 px-2 py-1 rounded border flex-shrink-0 transition-colors"
                style={{
                  borderColor: isHappeningPin ? c + "66" : "#2A2A2A",
                  background: isHappeningPin ? `color-mix(in srgb, ${c} 18%, #141414)` : "#141414",
                }}
                data-testid={`pinned-chip-${ev.event_id}`}
                title={`${ev.title} — click to open`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
                <span className="text-xs text-[#EDEDED] truncate max-w-[180px]">{ev.title}</span>
                <span className="text-[10px] text-[#A1A1AA]">{ev.type === "reminder" || ev.type === "trigger" || ev.type === "action" || ev.type === "condition" || ev.type === "note" ? format(new Date(ev.start), timeFormat) : format(new Date(ev.start), timeFormat)}</span>
                <span role="button" aria-label="Unpin" tabIndex={0}
                  onClick={async (e) => { e.stopPropagation(); await api.put(`/workspaces/${current.workspace_id}/events/${ev.event_id}`, { pinned: false }); load(); }}
                  className="text-[#FF3B30] inline-flex items-center"
                >
                  <PinOff className="w-3 h-3" />
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex-1 overflow-auto relative" ref={containerRef} data-testid="calendar-scroll"
        onPointerDown={onContainerPointerDown}
        onPointerMove={onContainerPointerMove}
        onPointerUp={onContainerPointerUp}
      >
        {view === "month" ? (
          <MonthView date={cursor} events={displayedEvents} onDayClick={(d) => { setCursor(d); setView("day"); }} classHighlightMap={classColorMap} weekStartsOn={weekStartsOn} />
        ) : (
          <div className="flex min-w-full relative">
            <TimeGutter timeFormat={timeFormat} />
            <div className="flex flex-1 relative">
              {view === "day" ? (
                <DayColumn date={cursor} events={displayedEvents.filter((e) => {
                    if (nestedChildIds.has(e.event_id)) return false;
                    if (e.unscheduled) return false;
                    const evStart = parseISO(e.start);
                    const evEnd   = parseISO(e.end || e.start);
                    const dayStart = startOfDay(cursor);
                    return isSameDay(evStart, cursor) || (!isSameDay(evStart, cursor) && evEnd > dayStart && evStart < dayStart);
                  }).map((e) => {
                    const evStart = parseISO(e.start);
                    if (!isSameDay(evStart, cursor)) {
                      return { ...e, _continuation: true, _originalStart: e.start, start: startOfDay(cursor).toISOString() };
                    }
                    return e;
                  })}
                  isToday={isSameDay(cursor, new Date())} showLabel
                  overlayEvents={displayedOverlayEvents}
                  colZoom={Math.max(1, 7 / daysInView)}
                  weekdayTint={tintFor(cursor)}
                  _dragType={activeDragType}
                  dayFmtKey={dayFmtKey}
                  isSelectedDay={selectedDayKey === format(cursor, "yyyy-MM-dd")}
                  onDayHeaderDoubleClick={(d) => setDayDetails({ date: d, dayEvents: events.filter(ev => isSameDay(new Date(ev.start), d)) })}
                  onSelectDay={(d) => setSelectedDayKey((prev) => prev === format(d, "yyyy-MM-dd") ? null : format(d, "yyyy-MM-dd"))}
                  onEventOpen={onEventOpen} onSlotDoubleClick={onSlotDoubleClick}
                  onSlotContextMenu={onSlotContextMenu}
                  onContextMenu={onContextMenu}
                  onStartConnect={onStartConnect}
                  onAddNote={onAddNote} onDeleteNote={onDeleteNote}
                  onAddToSequence={onAddToSequence}
                  onRemoveFromSequence={onRemoveFromSequence}
                  onCreateChildInSequence={onCreateChildInSequence}
                  onReorderSequenceChild={onReorderSequenceChild}
                  onToggleComplete={onToggleComplete}
                  notesByEvent={notesByEvent}
                  setRef={setRef} timeFormat={timeFormat}
                  onReschedule={onReschedule} onResize={onResize}
                  selectedIds={selectedIds} onSelect={onSelect}
                  classColorMap={classColorMap} classBgMap={classBgMap} dimmedMap={dimmedMap}
                  onNodeDrop={onNodeDrop}
                  onAttachNode={onAttachNode}
                  isConnecting={connectSrc !== null}
                  attachedByEvent={attachedByEvent}
                  onCrossDayDrop={async (eventId, newStart) => {
                    if (!current) return;
                    try {
                      const ev = events.find(e => e.event_id === eventId);
                      const dur = ev ? (Date.parse(ev.end || ev.start) - Date.parse(ev.start)) : 3600000;
                      const newEnd = new Date(newStart.getTime() + dur);
                      await api.put(`/workspaces/${current.workspace_id}/events/${eventId}`, {
                        start: newStart.toISOString(), end: newEnd.toISOString(), unscheduled: false,
                      });
                      load();
                    } catch { toast.error("Move failed"); }
                  }}
                  onDetachFromSequence={onDetachFromSequence}
                  onMoveCommit={onMoveCommit}
                  onDragFrame={onDragFrame}
                  snapToGrid={snapToGrid}
                  snapMinutes={snapMinutes}
                  resolveChild={(cid) => events.find((e) => e.event_id === cid)}
                  outgoingMap={outgoingMap}
                  unusedNodeSet={unusedNodeSet}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                  isNodeCollapsed={isNodeCollapsed}
                  toggleNodeCollapse={toggleNodeCollapse}
                />
              ) : (
                weekDays.map((d) => (
                  <DayColumn key={d.toISOString()} date={d}
                    events={displayedEvents.filter((e) => {
                    if (nestedChildIds.has(e.event_id)) return false;
                    if (e.unscheduled) return false;
                    const evStart = parseISO(e.start);
                    const evEnd   = parseISO(e.end || e.start);
                    const dayStart = startOfDay(d);
                    // Show if starts on this day, OR started before but ends after midnight into this day
                    return isSameDay(evStart, d) || (!isSameDay(evStart, d) && evEnd > dayStart && evStart < dayStart);
                  }).map((e) => {
                    // Mark continuation events (started on a previous day) so DayColumn can render them from top
                    const evStart = parseISO(e.start);
                    if (!isSameDay(evStart, d)) {
                      return { ...e, _continuation: true, _originalStart: e.start, start: startOfDay(d).toISOString() };
                    }
                    return e;
                  })}
                    isToday={isSameDay(d, new Date())} showLabel
                    overlayEvents={displayedOverlayEvents}
                    colZoom={Math.max(1, 7 / daysInView)}
                    weekdayTint={tintFor(d)}
                    _dragType={activeDragType}
                    dayFmtKey={dayFmtKey}
                    isSelectedDay={selectedDayKey === format(d, "yyyy-MM-dd")}
                    onDayHeaderDoubleClick={(dd) => setDayDetails({ date: dd, dayEvents: events.filter(ev => isSameDay(new Date(ev.start), dd)) })}
                    onSelectDay={(dd) => setSelectedDayKey((prev) => prev === format(dd, "yyyy-MM-dd") ? null : format(dd, "yyyy-MM-dd"))}
                    onEventOpen={onEventOpen} onSlotDoubleClick={onSlotDoubleClick}
                    onSlotContextMenu={onSlotContextMenu}
                    onContextMenu={onContextMenu}
                    onStartConnect={onStartConnect}
                    onAddNote={onAddNote} onDeleteNote={onDeleteNote}
                    onAddToSequence={onAddToSequence}
                    onRemoveFromSequence={onRemoveFromSequence}
                    onCreateChildInSequence={onCreateChildInSequence}
                    onReorderSequenceChild={onReorderSequenceChild}
                    onToggleComplete={onToggleComplete}
                    notesByEvent={notesByEvent}
                    setRef={setRef} timeFormat={timeFormat}
                    onReschedule={onReschedule} onResize={onResize}
                    selectedIds={selectedIds} onSelect={onSelect}
                    classColorMap={classColorMap} classBgMap={classBgMap} dimmedMap={dimmedMap}
                    onNodeDrop={onNodeDrop}
                  onAttachNode={onAttachNode}
                  isConnecting={connectSrc !== null}
                  attachedByEvent={attachedByEvent}
                    onCrossDayDrop={async (eventId, newStart) => {
                      if (!current) return;
                      try {
                        const ev = events.find(e => e.event_id === eventId);
                        const dur = ev ? (Date.parse(ev.end || ev.start) - Date.parse(ev.start)) : 3600000;
                        const newEnd = new Date(newStart.getTime() + dur);
                        await api.put(`/workspaces/${current.workspace_id}/events/${eventId}`, {
                          start: newStart.toISOString(), end: newEnd.toISOString(), unscheduled: false,
                        });
                        load();
                      } catch { toast.error("Move failed"); }
                    }}
                    onDetachFromSequence={onDetachFromSequence}
                    onMoveCommit={onMoveCommit}
                    onDragFrame={onDragFrame}
                    snapToGrid={snapToGrid}
                    snapMinutes={snapMinutes}
                    resolveChild={(cid) => events.find((e) => e.event_id === cid)}
                    outgoingMap={outgoingMap}
                  unusedNodeSet={unusedNodeSet}
                    expandedId={expandedId}
                    setExpandedId={setExpandedId}
                    isNodeCollapsed={isNodeCollapsed}
                    toggleNodeCollapse={toggleNodeCollapse}
                  />
                ))
              )}
            </div>
            {/* ── Split view: second independent calendar panel ── */}
          {splitView && view !== "month" && (
            <div className="flex flex-col border-l-2 border-[#5A90F5]/40 flex-1 min-w-0 relative">
              <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[#2A2A2A] bg-[#0A0A0A] sticky top-0 z-30">
                {splitMode === "calendar" && (
                  <button onClick={() => setSplitDate(d => subWeeks(d, 1))} className="p-1 hover:bg-[#1a1a1a] rounded"><ChevronLeft className="w-3 h-3" /></button>
                )}
                <span className="text-[11px] text-[#A1A1AA] flex-1 text-center">
                  {splitMode === "calendar" ? (splitDate ? format(splitDate, "MMM d, yyyy") : "") : "Documents"}
                </span>
                {splitMode === "calendar" && (
                  <button onClick={() => setSplitDate(d => addWeeks(d, 1))} className="p-1 hover:bg-[#1a1a1a] rounded"><ChevronRight className="w-3 h-3" /></button>
                )}
                <div className="flex gap-1 ml-1">
                  <button onClick={() => setSplitMode("calendar")}
                    className={`text-[9px] px-1.5 py-0.5 rounded ${splitMode === "calendar" ? "bg-[#5A90F5]/20 text-[#5A90F5]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>Cal</button>
                  <button onClick={() => setSplitMode("documents")}
                    className={`text-[9px] px-1.5 py-0.5 rounded ${splitMode === "documents" ? "bg-[#5A90F5]/20 text-[#5A90F5]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>Docs</button>
                </div>
              </div>
              {splitMode === "documents" ? (
                <div className="flex-1 overflow-hidden">
                  <SplitDocumentsView />
                </div>
              ) : (
              <div className="flex flex-1 relative">
                <TimeGutter timeFormat={timeFormat} />
                <div className="flex flex-1 relative">
                  {splitDate && (view === "day" ? [splitDate] : Array.from({ length: visibleDaysCount }, (_, i) => addDays(startOfWeek(splitDate, { weekStartsOn }), i))).map((d) => (
                    <DayColumn key={"split-" + d.toISOString()} date={d}
                      events={displayedEvents.filter((e) => {
                        if (nestedChildIds.has(e.event_id)) return false;
                        if (e.unscheduled) return false;
                        const evStart = parseISO(e.start);
                        const evEnd = parseISO(e.end || e.start);
                        const dayStart = startOfDay(d);
                        return isSameDay(evStart, d) || (!isSameDay(evStart, d) && evEnd > dayStart && evStart < dayStart);
                      }).map((e) => {
                        const evStart = parseISO(e.start);
                        if (!isSameDay(evStart, d)) return { ...e, _continuation: true, _originalStart: e.start, start: startOfDay(d).toISOString() };
                        return e;
                      })}
                      isToday={isSameDay(d, new Date())} showLabel
                      overlayEvents={[]}
                      colZoom={Math.max(1, 7 / daysInView)}
                      weekdayTint={tintFor(d)}
                      _dragType={activeDragType}
                      isSelectedDay={false}
                      onSelectDay={(d) => setSelectedDay(d?.toISOString?.()?.slice(0,10) || null)}
                      onEventOpen={onEventOpen} onSlotDoubleClick={onSlotDoubleClick}
                      onSlotContextMenu={onSlotContextMenu}
                      onContextMenu={onContextMenu}
                      onStartConnect={onStartConnect}
                      onAddNote={onAddNote} onDeleteNote={onDeleteNote}
                      onAddToSequence={onAddToSequence}
                      onRemoveFromSequence={onRemoveFromSequence}
                      onCreateChildInSequence={onCreateChildInSequence}
                      onReorderSequenceChild={onReorderSequenceChild}
                      onToggleComplete={onToggleComplete}
                      notesByEvent={notesByEvent}
                      setRef={setRef} timeFormat={timeFormat}
                      onReschedule={onReschedule} onResize={onResize}
                      selectedIds={selectedIds} onSelect={onSelect}
                      classColorMap={classColorMap} classBgMap={classBgMap} dimmedMap={dimmedMap}
                      onNodeDrop={onNodeDrop}
                  onAttachNode={onAttachNode}
                  isConnecting={connectSrc !== null}
                  attachedByEvent={attachedByEvent}
                      onCrossDayDrop={async (eventId, newStart) => {
                        if (!current) return;
                        try {
                          const ev = events.find(e => e.event_id === eventId);
                          const dur = ev ? (Date.parse(ev.end || ev.start) - Date.parse(ev.start)) : 3600000;
                          const newEnd = new Date(newStart.getTime() + dur);
                          await api.put(`/workspaces/${current.workspace_id}/events/${eventId}`, { start: newStart.toISOString(), end: newEnd.toISOString(), unscheduled: false });
                          load();
                        } catch { toast.error("Move failed"); }
                      }}
                      onDetachFromSequence={onDetachFromSequence}
                      onMoveCommit={onMoveCommit}
                      onDragFrame={onDragFrame}
                      snapToGrid={snapToGrid}
                      snapMinutes={snapMinutes}
                      resolveChild={(cid) => events.find((e) => e.event_id === cid)}
                      outgoingMap={outgoingMap}
                  unusedNodeSet={unusedNodeSet}
                      expandedId={expandedId}
                      setExpandedId={setExpandedId}
                    />
                  ))}
                </div>
              </div>
              )}
            </div>
          )}
          <svg className="connection-overlay absolute" width="100%" height={24 * HOUR_HEIGHT + 48} style={{ pointerEvents: "none" }}>
              <defs>
                {edgeShapes.map((p) => (
                  <linearGradient key={p.gradId} id={p.gradId} gradientUnits="userSpaceOnUse"
                    x1={p.sx} y1={p.sy} x2={p.tx} y2={p.ty}>
                    <stop offset="0%" stopColor={p.colorA} />
                    <stop offset="100%" stopColor={p.colorB} />
                  </linearGradient>
                ))}
              </defs>
              {edgeShapes.map((p) => {
                const selected = selectedEdgeId === p.id;
                const hasLabel = p.triggerLabel || p.conditionLabel;
                const labelText = p.triggerLabel || p.conditionLabel || "";
                const isNew = p.id === newEdgeId;
                return (
                  <g key={p.id}
                    onClick={(e) => { e.stopPropagation(); setSelectedEdgeId(p.id); }}
                    style={{ cursor: "pointer", pointerEvents: "stroke" }}
                    data-testid={`edge-${p.id}`}
                  >
                    {/* Invisible wide hit target */}
                    <path d={p.d} fill="none" stroke="transparent" strokeWidth="12" style={{ pointerEvents: "stroke" }} />
                    {/* Visible gradient line — with draw-on animation for new edges */}
                    <path d={p.d} fill="none"
                      stroke={`url(#${p.gradId})`}
                      strokeWidth={selected ? 2.5 : 1.5}
                      strokeLinejoin="round" strokeLinecap="round"
                      opacity={selected ? 1 : 0.8}
                      style={isNew ? {
                        strokeDasharray: 300,
                        strokeDashoffset: 300,
                        animation: "edge-draw-on 0.6s cubic-bezier(0.4,0,0.2,1) forwards",
                      } : undefined}
                    />
                    {/* Dot at source */}
                    <circle cx={p.sx} cy={p.sy} r={selected ? 3.5 : 2.5} fill={p.colorA} opacity={selected ? 1 : 0.85} />
                    {/* Dot at target */}
                    <circle cx={p.tx} cy={p.ty} r={selected ? 3.5 : 2.5} fill={p.colorB} opacity={selected ? 1 : 0.85} />
                    {/* Inline trigger label at midpoint */}
                    {(hasLabel || selected) && (
                      <g
                        style={{ pointerEvents: "all", cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingEdgeLabel({ edgeId: p.id, value: labelText });
                        }}
                        data-testid={`edge-label-${p.id}`}
                      >
                        <rect
                          x={p.mx - (labelText ? labelText.length * 3.2 + 10 : 24)}
                          y={p.my - 9}
                          width={labelText ? labelText.length * 6.4 + 20 : 48}
                          height={18}
                          rx={4}
                          fill="#141414"
                          stroke={selected ? p.colorA : "#2A2A2A"}
                          strokeWidth={selected ? 1.5 : 1}
                        />
                        <text
                          x={p.mx}
                          y={p.my + 4}
                          textAnchor="middle"
                          fill={labelText ? "#A1A1AA" : "#3A3A3A"}
                          fontSize={9}
                          fontFamily="'DM Sans', sans-serif"
                          style={{ userSelect: "none" }}
                        >
                          {labelText || "if…"}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
              {liveConnect && (
                <path d={liveConnect.d} fill="none" stroke={liveConnect.color} strokeWidth="1.5"
                  strokeDasharray="4 3" strokeLinejoin="round" strokeLinecap="round" opacity="0.85" />
              )}
            </svg>
            {selectionBox && (
              <div className="selection-box" style={{ left: selectionBox.x, top: selectionBox.y, width: selectionBox.w, height: selectionBox.h }} />
            )}
          </div>
        )}
        </div>

      {/* Edge condition / trigger label editor */}
      {editingEdgeLabel && (
        <>
          <div className="fixed inset-0 z-[170]" onClick={() => setEditingEdgeLabel(null)} />
          <div className="fixed z-[171] bg-[#141414] border border-[#2A2A2A] rounded-lg shadow-xl p-3 w-64"
            style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)" }}
            onClick={e => e.stopPropagation()}
          >
            <p className="text-[10px] text-[#52525B] mb-1">If condition <span className="text-[#3A3A3A]">— runs when this is true</span></p>
            <div className="flex flex-wrap gap-1 mb-2">
              {["always", "task complete", "task started", "deadline reached", "condition met", "manually"].map(opt => (
                <button key={opt} onClick={() => setEditingEdgeLabel(prev => ({ ...prev, value: opt }))}
                  className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${editingEdgeLabel.value === opt ? "border-[#5A90F5] text-[#5A90F5] bg-[#5A90F5]/10" : "border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA] hover:bg-[#1a1a1a]"}`}>
                  {opt}
                </button>
              ))}
            </div>
            <input
              autoFocus
              value={editingEdgeLabel.value}
              onChange={e => setEditingEdgeLabel(prev => ({ ...prev, value: e.target.value }))}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  await api.patch(`/workspaces/${current.workspace_id}/edges/${editingEdgeLabel.edgeId}`, { trigger_label: editingEdgeLabel.value });
                  setEdges(prev => prev.map(ed => ed.edge_id === editingEdgeLabel.edgeId ? { ...ed, trigger_label: editingEdgeLabel.value } : ed));
                  setEditingEdgeLabel(null);
                }
                if (e.key === "Escape") setEditingEdgeLabel(null);
              }}
              placeholder="Custom trigger label…"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none focus:border-[#5A90F5] mb-2"
            />
            <div className="flex gap-1">
              <button onClick={() => setEditingEdgeLabel(null)}
                className="flex-1 px-2 py-1.5 rounded border border-[#2A2A2A] text-[11px] text-[#52525B] hover:bg-[#1a1a1a]">Cancel</button>
              <button onClick={async () => {
                await api.patch(`/workspaces/${current.workspace_id}/edges/${editingEdgeLabel.edgeId}`, { trigger_label: editingEdgeLabel.value });
                setEdges(prev => prev.map(ed => ed.edge_id === editingEdgeLabel.edgeId ? { ...ed, trigger_label: editingEdgeLabel.value } : ed));
                setEditingEdgeLabel(null);
              }}
                className="flex-1 px-2 py-1.5 rounded bg-[#5A90F5] text-white text-[11px] font-medium hover:bg-[#4a80e5]">Save</button>
            </div>
          </div>
        </>
      )}

      <EventDialog open={dialogOpen} onClose={() => setDialogOpen(false)} initial={editing}
        onSave={onSave} onDelete={onDelete} classes={classes} events={events} />

      {/* Multi-select contextual action bar — replaces the old right-click menu for bulk ops. */}
      {selectedIds.size > 0 && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 px-2 py-1.5 rounded-xl border border-[#2A2A2A] bg-[#141414]/95 shadow-2xl backdrop-blur-md"
          style={{ bottom: 28 }}
          data-testid="multi-select-bar"
        >
          <span className="text-[11px] text-[#A1A1AA] px-2 font-mono">
            {selectedIds.size} selected
          </span>
          <div className="w-px h-5 bg-[#2A2A2A]" />
          {/* Complete — only for selections containing task nodes. */}
          {[...selectedIds].some((id) => (events.find((e) => e.event_id === id)?.type) === "task") && (
            <button
              className="msbar-btn"
              title="Mark tasks complete"
              onClick={async () => {
                const ids = [...selectedIds].filter((id) => (events.find((e) => e.event_id === id)?.type) === "task");
                for (const id of ids) await api.put(`/workspaces/${current.workspace_id}/events/${id}`, { completed_at: new Date().toISOString() });
                setSelectedIds(new Set()); load(); /* silent: marked complete */
              }}
              data-testid="msbar-complete"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          )}
          {/* Convert — opens a mini type picker. */}
          <div className="relative">
            <button
              className="msbar-btn"
              title="Convert type"
              onClick={() => setConvertOpen((v) => !v)}
              data-testid="msbar-convert"
            >
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            {convertOpen && (
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-[#141414] border border-[#2A2A2A] rounded-md shadow-xl p-1 w-36 z-50"
                onMouseLeave={() => setConvertOpen(false)}
                data-testid="msbar-convert-menu">
                {NODE_TYPES.map((t) => (
                  <button key={t}
                    className="w-full text-left text-xs px-2 py-1 rounded hover:bg-[#1a1a1a] capitalize flex items-center gap-2"
                    onClick={async () => {
                      for (const id of selectedIds) await api.put(`/workspaces/${current.workspace_id}/events/${id}`, { type: t });
                      setConvertOpen(false); load(); /* silent: converted */
                    }}
                    data-testid={`msbar-convert-${t}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: NODE_TYPE_COLORS[t] }} />
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            className="msbar-btn"
            title="Pin"
            onClick={async () => {
              for (const id of selectedIds) {
                const ev = events.find((e) => e.event_id === id);
                if (ev) await api.put(`/workspaces/${current.workspace_id}/events/${id}`, { pinned: !ev.pinned });
              }
              load(); /* silent: toggled pin */
            }}
            data-testid="msbar-pin"
          >
            <Pin className="w-3.5 h-3.5" />
          </button>
          <button
            className="msbar-btn"
            title="Lock / Unlock (prevents accidental delete)"
            onClick={async () => {
              for (const id of selectedIds) {
                const ev = events.find((e) => e.event_id === id);
                if (ev) await api.put(`/workspaces/${current.workspace_id}/events/${id}`, { locked: !ev.locked });
              }
              load(); /* silent: toggled lock */
            }}
            data-testid="msbar-lock"
          >
            <Lock className="w-3.5 h-3.5" />
          </button>
          <div className="w-px h-5 bg-[#2A2A2A]" />
          <button
            className="msbar-btn"
            title="Reschedule to tomorrow"
            onClick={async () => {
              for (const id of selectedIds) {
                const ev = events.find(e => e.event_id === id);
                if (!ev || ev.unscheduled) continue;
                const s = addDays(parseISO(ev.start), 1).toISOString();
                const e2 = addDays(parseISO(ev.end || ev.start), 1).toISOString();
                await api.put(`/workspaces/${current.workspace_id}/events/${id}`, { start: s, end: e2 });
              }
              setSelectedIds(new Set()); load();
            }}
          >
            <ChevronRight className="w-3.5 h-3.5" /><span className="text-[10px]">+1d</span>
          </button>
          {tags.length > 0 && (
            <div className="relative group">
              <button className="msbar-btn" title="Add tag to all selected">
                <Tag className="w-3.5 h-3.5" />
              </button>
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:flex flex-col bg-[#141414] border border-[#2A2A2A] rounded-md shadow-xl p-1 min-w-[120px] z-50">
                {tags.map(t => (
                  <button key={t.tag_id} className="text-left text-xs px-2 py-1 rounded hover:bg-[#1a1a1a] flex items-center gap-2"
                    onClick={async () => {
                      for (const id of selectedIds) {
                        const ev = events.find(e => e.event_id === id);
                        if (!ev) continue;
                        const newTags = [...new Set([...(ev.tag_ids || []), t.tag_id])];
                        await api.put(`/workspaces/${current.workspace_id}/events/${id}`, { tag_ids: newTags });
                      }
                      load();
                    }}>
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.color || "#5A90F5" }} />{t.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="w-px h-5 bg-[#2A2A2A]" />
          <button
            className="msbar-btn msbar-danger"
            title="Delete (Del)"
            onClick={async () => {
              const victims = events.filter((e) => selectedIds.has(e.event_id) && !e.locked);
              const locked = [...selectedIds].length - victims.length;
              for (const ev of victims) await api.delete(`/workspaces/${current.workspace_id}/events/${ev.event_id}`);
              setSelectedIds(new Set()); load();
              if (locked > 0) toast(`${victims.length} deleted. ${locked} locked — skipped.`);
            }}
            data-testid="msbar-delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button
            className="msbar-btn"
            title="Clear selection (Esc)"
            onClick={() => setSelectedIds(new Set())}
            data-testid="msbar-clear"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Node right-click context menu. */}
      {/* Node & slot right-click context menu. */}
      {ctxMenu && !ctxMenu.event && ctxMenu.slot && (
        <div
          className="fixed z-[200] bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl p-1 w-48 text-xs"
          style={{
            left: Math.min(ctxMenu.x, window.innerWidth - 200),
            top: Math.min(ctxMenu.y, window.innerHeight - 260),
          }}
          data-testid="slot-ctx-menu"
        >
          {/* Add Node */}
          <div
            className="ctx-item justify-between"
            onMouseEnter={() => setCtxSubmenu("add")}
            data-testid="sctx-add-node"
          >
            <span className="flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add Node</span>
            <ChevronRight className="w-3 h-3" />
            {ctxSubmenu === "add" && (
              <div
                className="absolute top-0 bg-[#141414] border border-[#2A2A2A] rounded-md shadow-xl p-1 w-36"
                style={{ left: "100%" }}
                onMouseEnter={() => setCtxSubmenu("add")}
              >
                {NODE_TYPES.map((t) => {
                  const gated = isProGatedNode(t);
                  return (
                    <button key={t}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (gated) {
                          window.dispatchEvent(new CustomEvent("crescendo:paywall", {
                            detail: { error: "feature_locked", feature: `${t}_node`, plan: "pro", message: `${t.charAt(0).toUpperCase()+t.slice(1)} nodes require Pro.` },
                          }));
                          setCtxMenu(null); setCtxSubmenu(null);
                          return;
                        }
                        const body = { type: t, title: "", start: ctxMenu.slot, end: isZeroDurationType(t) ? ctxMenu.slot : addHours(new Date(ctxMenu.slot), 1).toISOString() };
                        const { data } = await api.post(`/workspaces/${current.workspace_id}/events`, body);
                        setEditing(data); setDialogOpen(true);
                        setCtxMenu(null); setCtxSubmenu(null); load();
                      }}
                      className={`w-full text-left text-xs px-2 py-1 rounded hover:bg-[#5A90F5]/15 hover:text-[#EDEDED] capitalize flex items-center gap-2 ${gated ? "opacity-80" : ""}`}
                      data-testid={`sctx-add-${t}`}
                    >
                      <span className="w-2 h-2 rounded-full bg-[#5A90F5]" />
                      <span className="flex-1">{t}</span>
                      {gated && <Sparkles className="w-3 h-3 text-[#E0B355]" data-testid={`sctx-add-${t}-pro`} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {/* Snap-to-grid */}
          <div
            className="ctx-item justify-between"
            onMouseEnter={() => setCtxSubmenu("snap")}
            data-testid="sctx-snap"
          >
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${snapToGrid ? "bg-[#5A90F5]" : "bg-[#555]"}`} />
              Snap to Grid {snapToGrid ? `(${snapMinutes}m)` : "(off)"}
            </span>
            <ChevronRight className="w-3 h-3" />
            {ctxSubmenu === "snap" && (
              <div
                className="absolute top-0 bg-[#141414] border border-[#2A2A2A] rounded-md shadow-xl p-1 w-32"
                style={{ left: "100%" }}
                onMouseEnter={() => setCtxSubmenu("snap")}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); setSnapToGrid?.(!snapToGrid); setCtxMenu(null); setCtxSubmenu(null); }}
                  className="w-full text-left text-xs px-2 py-1 rounded hover:bg-[#1a1a1a]"
                >
                  {snapToGrid ? "Turn off snap" : "Turn on snap"}
                </button>
                <div className="h-px bg-[#2A2A2A] my-1" />
                {[5, 10, 15, 30, 60].map((n) => (
                  <button key={n}
                    onClick={(e) => { e.stopPropagation(); setSnapMinutes?.(n); if (!snapToGrid) setSnapToGrid?.(true); setCtxMenu(null); setCtxSubmenu(null); }}
                    className={`w-full text-left text-xs px-2 py-1 rounded ${snapMinutes === n ? "bg-[#0055FF]/20 text-white" : "hover:bg-[#1a1a1a]"}`}
                    data-testid={`sctx-snap-${n}`}
                  >
                    {n} minutes
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="ctx-sep" />
          <div className="ctx-item" onClick={() => { window.dispatchEvent(new CustomEvent("crescendo:open-documents")); setCtxMenu(null); }}>
            <FileText className="w-3.5 h-3.5" /> Open Documents
          </div>
        </div>
      )}
      {ctxMenu?.event && (
        <div
          className="fixed z-[200] bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl p-1 w-52 text-xs"
          style={{
            left: Math.min(ctxMenu.x, window.innerWidth - 220),
            top: Math.min(ctxMenu.y, window.innerHeight - 400),
          }}
          data-testid="node-ctx-menu"
        >
          {ctxMenu.event.type === "condition" && (
            <>
              {/* Add Output Branch — FIRST option for condition events
                  (user request) with a divider underneath. */}
              <div
                className="ctx-item justify-between"
                onMouseEnter={() => setCtxSubmenu("nctx-output")}
                onMouseLeave={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  if (e.clientX < r.right - 4) setCtxSubmenu(null);
                }}
                data-testid="nctx-add-output"
              >
                <span className="flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add Output Branch</span>
                <ChevronRight className="w-3 h-3" />
                {ctxSubmenu === "nctx-output" && (
                  <div
                    className="absolute top-0 bg-[#141414] border border-[#2A2A2A] rounded-md shadow-xl p-1 w-36"
                    style={{ left: "100%" }}
                    onMouseEnter={() => setCtxSubmenu("nctx-output")}
                  >
                    {[
                      { key: "true", label: "True", color: "#6EE7B7" },
                      { key: "false", label: "False", color: "#FCA5A5" },
                      { key: "custom", label: "Custom…", color: "#5A90F5" },
                    ].map((opt) => (
                      <button key={opt.key}
                        onClick={async (e) => {
                          e.stopPropagation();
                          let label = opt.label;
                          if (opt.key === "custom") {
                            label = window.prompt("Branch label:", "branch")?.trim();
                            if (!label) { setCtxMenu(null); setCtxSubmenu(null); return; }
                          }
                          const existing = Array.isArray(ctxMenu.event.condition_outputs) ? ctxMenu.event.condition_outputs : [];
                          if (existing.find((b) => b.label === label)) {
                            toast.message("Branch already exists");
                            setCtxMenu(null); setCtxSubmenu(null); return;
                          }
                          const next = [...existing, { label, value: "" }];
                          try {
                            await api.put(`/workspaces/${current.workspace_id}/events/${ctxMenu.event.event_id}`, { condition_outputs: next });
                            /* silent: branch added */
                          } catch { toast.error("Couldn't add branch"); }
                          setCtxMenu(null); setCtxSubmenu(null); load();
                        }}
                        className="w-full text-left px-2 py-1 rounded hover:bg-[#5A90F5]/15 text-xs flex items-center gap-2"
                      >
                        <span className="w-2 h-2 rounded-full" style={{ background: opt.color }} />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="ctx-sep" />
            </>
          )}
          {/* Quick color picker */}
          <div className="px-2 py-1.5 flex flex-wrap gap-1.5" onClick={e => e.stopPropagation()}>
            {["#EF4444","#FF8A00","#FFCC00","#00C781","#5A90F5","#A855F7","#14B8A6","#F97316","#A1A1AA","#FFFFFF"].map(c => (
              <button key={c} onClick={async () => {
                await api.put(`/workspaces/${current.workspace_id}/events/${ctxMenu.event.event_id}`, { custom_color: c });
                setCtxMenu(null); load();
              }} className="w-5 h-5 rounded-full border-2 hover:scale-125 transition-transform"
                style={{ background: c, borderColor: ctxMenu.event.custom_color === c ? "#fff" : "transparent" }}
                title={c}
              />
            ))}
            <button onClick={async () => {
              await api.put(`/workspaces/${current.workspace_id}/events/${ctxMenu.event.event_id}`, { custom_color: null });
              setCtxMenu(null); load();
            }} className="w-5 h-5 rounded-full border border-[#2A2A2A] hover:scale-125 transition-transform flex items-center justify-center" title="Default color">
              <X className="w-2.5 h-2.5 text-[#52525B]" />
            </button>
          </div>
          <div className="ctx-sep" />
          <div className="ctx-item" onClick={() => { setEditing(ctxMenu.event); setDialogOpen(true); setCtxMenu(null); }} data-testid="nctx-edit">
            <FileEdit className="w-3.5 h-3.5" /> Edit…
          </div>
          <div className="ctx-item" onClick={async () => {
            const ev = ctxMenu.event;
            // Warn if the event has a due date that's already passed
            if (ev.due_date && new Date(ev.due_date) < new Date()) {
              const ok = window.confirm(`"${ev.title}" has a past deadline (${new Date(ev.due_date).toLocaleDateString()}). Duplicate anyway?`);
              if (!ok) { setCtxMenu(null); return; }
            }
            const body = duplicateBody(ev, 30);
            await api.post(`/workspaces/${current.workspace_id}/events`, body);
            setCtxMenu(null); load();
          }} data-testid="nctx-duplicate">
            <CopyIcon className="w-3.5 h-3.5" /> Duplicate
          </div>
          <div className="ctx-item" onClick={() => { copyText(ctxMenu.event.event_id); setCtxMenu(null); }} data-testid="nctx-copy-id">
            <Clipboard className="w-3.5 h-3.5" /> Copy ID
          </div>
          {/* Move to first available block — works on selection OR the right-clicked node */}
          {(() => {
            const ids = selectedIds.size > 0 ? [...selectedIds] : [ctxMenu.event.event_id];
            const selEvts = displayedEvents
              .filter(e => ids.includes(e.event_id))
              .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
            return selEvts.length > 0 ? (
              <div className="ctx-item" data-testid="nctx-move-first-available" onClick={async () => {
                if (!current) { setCtxMenu(null); return; }
                // Find the earliest gap that fits all selected events packed together
                const otherEvts = displayedEvents.filter(e => !selectedIds.has(e.event_id) && !e.unscheduled);
                const totalDuration = selEvts.reduce((sum, e) => sum + (Date.parse(e.end || e.start) - Date.parse(e.start)), 0);
                // Try slots starting from day start, in 15-min increments
                const dayStart = new Date(selEvts[0].start);
                dayStart.setHours(0, 0, 0, 0);
                let candidate = new Date(dayStart);
                const maxTry = 48 * 4; // 2 days in 15-min blocks
                let placed = false;
                for (let i = 0; i < maxTry; i++) {
                  const slotEnd = new Date(candidate.getTime() + totalDuration);
                  const overlaps = otherEvts.some(o => {
                    const os = Date.parse(o.start), oe = Date.parse(o.end || o.start);
                    return !(slotEnd.getTime() <= os || candidate.getTime() >= oe);
                  });
                  if (!overlaps) {
                    // Place events packed from candidate
                    let offset = candidate.getTime();
                    await Promise.all(selEvts.map(ev => {
                      const dur = Date.parse(ev.end || ev.start) - Date.parse(ev.start);
                      const newStart = new Date(offset);
                      const newEnd = new Date(offset + dur);
                      offset += dur;
                      return api.put(`/workspaces/${current.workspace_id}/events/${ev.event_id}`, {
                        start: newStart.toISOString(), end: newEnd.toISOString(),
                      });
                    }));
                    placed = true;
                    break;
                  }
                  candidate = new Date(candidate.getTime() + 15 * 60 * 1000);
                }
                if (!placed) toast.error("No available slot found in the next 2 days");
                setCtxMenu(null); load();
              }}>
                <Zap className="w-3.5 h-3.5" /> Move to First Available Block
              </div>
            ) : null;
          })()}
          {/* Add breaks between selected nodes */}
          {selectedIds.size > 1 && (() => {
            const selEvts = displayedEvents
              .filter(e => selectedIds.has(e.event_id))
              .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
            return (
              <div className="ctx-item" data-testid="nctx-add-breaks" onClick={async () => {
                if (!current) { setCtxMenu(null); return; }
                const BREAK = 15 * 60 * 1000; // 15 min default
                let cursor = Date.parse(selEvts[0].start);
                await Promise.all(selEvts.map(ev => {
                  const dur = Date.parse(ev.end || ev.start) - Date.parse(ev.start);
                  const newStart = new Date(cursor);
                  const newEnd = new Date(cursor + dur);
                  cursor = cursor + dur + BREAK;
                  return api.put(`/workspaces/${current.workspace_id}/events/${ev.event_id}`, {
                    start: newStart.toISOString(), end: newEnd.toISOString(),
                  });
                }));
                setCtxMenu(null); load();
                toast.success("15-minute breaks added between nodes");
              }}>
                <Plus className="w-3.5 h-3.5" /> Add Breaks (15 min)
              </div>
            );
          })()}
          {/* Pin / Unpin */}
          <div className="ctx-item" onClick={async () => {
            await api.put(`/workspaces/${current.workspace_id}/events/${ctxMenu.event.event_id}`, { pinned: !ctxMenu.event.pinned });
            setCtxMenu(null); load();
          }}>
            <Pin className="w-3.5 h-3.5" /> {ctxMenu.event.pinned ? "Unpin" : "Pin"}
          </div>
          {ctxMenu.event.type === "task" && (
            <div className="ctx-item" onClick={async () => {
              await api.put(`/workspaces/${current.workspace_id}/events/${ctxMenu.event.event_id}`, { completed_at: ctxMenu.event.completed_at ? null : new Date().toISOString() });
              setCtxMenu(null); load();
            }}>
              <Check className="w-3.5 h-3.5" /> {ctxMenu.event.completed_at ? "Mark Incomplete" : "Mark Complete"}
            </div>
          )}
          {ctxMenu.event.type === "trigger" && (
            <div className="ctx-item" data-testid="ctx-force-play" onClick={() => {
              setRunConfirm(ctxMenu.event);
              setCtxMenu(null);
            }}>
              <PlayCircle className="w-3.5 h-3.5" /> Force Play
            </div>
          )}
          {/* Focus toggle on task/routine */}
          {(ctxMenu.event.type === "task" || ctxMenu.event.type === "routine") && (
            <div className="ctx-item" onClick={async () => {
              await api.put(`/workspaces/${current.workspace_id}/events/${ctxMenu.event.event_id}`, { focus_on_start: !ctxMenu.event.focus_on_start });
              setCtxMenu(null); load();
            }}>
              <Focus className="w-3.5 h-3.5" /> {ctxMenu.event.focus_on_start ? "Disable Focus On Start" : "Enable Focus On Start"}
            </div>
          )}
          <div className="ctx-sep" />
          {selectedIds.size > 1 && (
            <div className="ctx-item" onClick={async () => {
              const selEvts = events.filter((e) => selectedIds.has(e.event_id));
              const startMs = Math.min(...selEvts.map((x) => Date.parse(x.start)));
              const endMs = Math.max(...selEvts.map((x) => Date.parse(x.end) || Date.parse(x.start)));
              const body = {
                title: "Task Group", type: "sequence",
                start: new Date(startMs).toISOString(),
                end: new Date(Math.max(endMs, startMs + 30 * 60000)).toISOString(),
                sequence_mode: "parallel",
                children_event_ids: selEvts.map((x) => x.event_id),
              };
              await api.post(`/workspaces/${current.workspace_id}/events`, body);
              setCtxMenu(null); load();
            }} data-testid="nctx-group">
              <FolderIcon className="w-3.5 h-3.5" /> Group into Task Group
            </div>
          )}
          <div className="ctx-item" onClick={() => {
            const name = window.prompt("Template name:", ctxMenu.event.title || "Template");
            if (!name) return;
            const selEvts = selectedIds.size > 0 ? events.filter((e) => selectedIds.has(e.event_id)) : [ctxMenu.event];
            const cfg = { events: selEvts.map((ev) => ({ ...ev, event_id: undefined })) };
            saveTemplate({ name: name.trim(), description: "", config: cfg });
            setCtxMenu(null);
          }} data-testid="nctx-save-template">
            <FilePlus className="w-3.5 h-3.5" /> Save as template…
          </div>
          <div className="ctx-item" onClick={async () => {
            const ev = ctxMenu.event;
            setCtxMenu(null);
            try {
              await api.post("/integrations/google/push-event", {
                title: ev.title || "Untitled",
                start: ev.start,
                end: ev.end || ev.start,
                description: ev.description || "",
                gcal_event_id: ev.gcal_event_id || undefined,
              });
              toast.success("Pushed to Google Calendar");
            } catch (err) {
              const det = err?.response?.data?.detail;
              if (err?.response?.status === 404 || String(det).includes("not found")) toast.error("Connect Google Calendar first in Library");
              else toast.error("Push failed: " + (det || "check Google connection"));
            }
          }}>
            <Globe className="w-3.5 h-3.5" /> Push to Google Calendar
          </div>
          <div className="ctx-sep" />
          {/* Convert To → vertical submenu (like the slot menu). */}
          <div className="ctx-item justify-between"
            onMouseEnter={() => setCtxSubmenu("nctx-convert")}
            data-testid="nctx-convert">
            <span className="flex items-center gap-1.5"><ArrowRight className="w-3.5 h-3.5" /> Convert To</span>
            <ChevronRight className="w-3 h-3" />
            {ctxSubmenu === "nctx-convert" && (
              <div className="absolute top-0 bg-[#141414] border border-[#2A2A2A] rounded-md shadow-xl p-1 w-36" style={{ left: "100%" }} onMouseEnter={() => setCtxSubmenu("add")}>
                {NODE_TYPES.filter((t) => t !== ctxMenu.event.type).map((t) => (
                  <button key={t}
                    onClick={async (e) => { e.stopPropagation(); await convertType(t); setCtxMenu(null); setCtxSubmenu(null); }}
                    className="w-full text-left text-xs px-2 py-1 rounded hover:bg-[#5A90F5]/15 hover:text-[#EDEDED] flex items-center gap-2 capitalize"
                    data-testid={`nctx-convert-${t}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-[#5A90F5]" />
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="ctx-sep" />
          <div className="ctx-item" onClick={async () => {
            await api.put(`/workspaces/${current.workspace_id}/events/${ctxMenu.event.event_id}`, { locked: !ctxMenu.event.locked });
            setCtxMenu(null); load();
          }} data-testid="nctx-lock">
            {ctxMenu.event.locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
            {ctxMenu.event.locked ? "Unlock" : "Lock"}
          </div>
          <div className="ctx-item text-[#FF3B30]" onClick={async () => {
            if (ctxMenu.event.locked) { toast.error("Locked — unlock first"); return; }
            await onDelete(ctxMenu.event.event_id);
            setCtxMenu(null);
          }} data-testid="nctx-delete">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </div>
        </div>
      )}

      {/* Keyboard shortcut legend (toggle with ?). */}
      <Dialog open={legendOpen} onOpenChange={setLegendOpen}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] max-w-lg" data-testid="legend-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Keyboard Shortcuts</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Tap <kbd className="bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1 py-0.5 text-[10px]">?</kbd> anytime to reopen.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
            {[
              ["Delete", "Delete selected node(s)"],
              [`${MOD_KEY} + D`, "Duplicate selection"],
              [`${MOD_KEY} + C / X / V`, "Copy / Cut / Paste"],
              [`${MOD_KEY} + Z / ⇧Z`, "Undo / Redo"],
              [`${MOD_KEY} + G`, "Group into a folder"],
              [`${MOD_KEY} + Enter`, "Create node from AI prompt"],
              ["?", "Toggle this legend"],
              ["Esc", "Close dialogs / cancel drag"],
              ["Double-click empty slot", "New node at that time"],
              ["Double-click day header", "New event for that day"],
              ["Right-click sidebar item", "Context menu"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3 py-1 border-b border-[#1F1F1F]">
                <kbd className="bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[10px] text-[#EDEDED] whitespace-nowrap">{k}</kbd>
                <span className="text-[#A1A1AA] text-right">{v}</span>
              </div>
            ))}
          </div>

        </DialogContent>
      </Dialog>

      {/* Force-play confirmation (user request: confirmation, not a silent
          checkbox). Fires the workspace's automation pass when confirmed. */}
      <Dialog open={!!runConfirm} onOpenChange={(v) => !v && setRunConfirm(null)}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] max-w-sm" data-testid="force-play-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Force play this trigger?</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              This will immediately run the automation pass — every action wired to
              <span className="text-[#EDEDED]"> {runConfirm?.title || "this trigger"}</span> will fire now.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              onClick={() => setRunConfirm(null)}
              className="px-3 py-1.5 rounded text-xs border border-[#2A2A2A] hover:bg-[#1a1a1a]"
              data-testid="force-play-cancel"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!current) return;
                try {
                  await api.post(`/workspaces/${current.workspace_id}/automations/run`);
                  toast.success("Triggered");
                } catch {
                  toast.error("Run failed");
                }
                setRunConfirm(null);
                load();
              }}
              className="px-3 py-1.5 rounded text-xs bg-[#5A90F5] text-[#0A0A0A] font-semibold hover:bg-[#7AA5F8]"
              data-testid="force-play-confirm"
            >
              Force Play
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* History / snapshots dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] max-w-md" data-testid="history-dialog"
          onInteractOutside={(e) => { if (e.target instanceof Element && e.target.closest("[data-themed-select-panel]")) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="font-display">History</DialogTitle>
          </DialogHeader>
          <CalendarHistoryTabs snapshots={snapshots} restoreSnapshot={restoreSnapshot} workspaceId={current?.workspace_id} />
        </DialogContent>
      </Dialog>
      {/* Save-as-Template dialog */}
      <Dialog open={templatePromptOpen} onOpenChange={setTemplatePromptOpen}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] max-w-sm" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Save as Template</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Reusable across workspaces. Will capture {pendingTemplateRef.current?.events?.length || 0} node(s).</DialogDescription>
          </DialogHeader>
          <input value={templateName} onChange={(e) => setTemplateName(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
            placeholder="Template name" data-testid="template-name-input" autoFocus
          />
          <DialogFooter>
            <button onClick={() => { setTemplatePromptOpen(false); pendingTemplateRef.current = null; }} className="px-3 py-2 text-sm text-[#A1A1AA] hover:bg-[#1a1a1a] rounded-md">Cancel</button>
            <button onClick={async () => {
              const tpl = pendingTemplateRef.current;
              if (!tpl) return setTemplatePromptOpen(false);
              await saveTemplate({ name: templateName.trim() || "Untitled template", description: "", config: tpl });
              /* silent: template saved */
              setTemplatePromptOpen(false);
              pendingTemplateRef.current = null;
            }} className="bg-[#EDEDED] text-[#0A0A0A] px-4 py-2 rounded-md font-medium hover:bg-white" data-testid="save-template-submit">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ── Day Details Panel ── */}
      {dayDetails && (() => {
        const { date, dayEvents } = dayDetails;
        const WORK_DAY_MIN = 8 * 60, WORK_WEEK_MIN = 40 * 60;
        const dur = ev => (!ev.end || ev.end === ev.start) ? 0 : Math.max(0,(new Date(ev.end)-new Date(ev.start))/60000);
        const totalMin = dayEvents.reduce((s,ev) => s+dur(ev), 0);
        const unusedMin = Math.max(0, WORK_DAY_MIN - totalMin);
        const pctWeek = Math.min(100, (totalMin/WORK_WEEK_MIN)*100);
        const byType = {};
        dayEvents.forEach(ev => { const t=ev.type||"task"; if(!byType[t])byType[t]={count:0,min:0}; byType[t].count++; byType[t].min+=dur(ev); });
        const fmtTime = m => m<60?`${Math.round(m)}m`:`${Math.floor(m/60)}h ${Math.round(m%60)}m`;
        const TYPE_COLOR = {task:"#5A90F5",reminder:"#FF9500",note:"#52525B",focus:"#A78BFA",buffer:"#22C55E",event:"#F59E0B"};
        return (
          <div className="fixed inset-0 z-[200]" onClick={()=>setDayDetails(null)}>
            <div className="absolute inset-0 bg-black/40"/>
            <div onClick={e=>e.stopPropagation()}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 bg-[#111] border border-[#2A2A2A] rounded-xl shadow-2xl p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] text-[#52525B] uppercase tracking-wider">{format(date,"EEEE")}</div>
                  <div className="text-xl font-semibold text-[#EDEDED]">{format(date,"MMMM d, yyyy")}</div>
                </div>
                <button onClick={()=>setDayDetails(null)} className="text-[#3F3F46] hover:text-[#A1A1AA] text-xl leading-none">×</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[["Nodes",dayEvents.length,"total"],["Scheduled",fmtTime(totalMin),"in nodes"],["Unused",fmtTime(unusedMin),"of 8h day"],["Workweek",`${pctWeek.toFixed(1)}%`,"of 40h"]].map(([l,v,s])=>(
                  <div key={l} className="bg-[#0A0A0A] border border-[#1E1E1E] rounded-lg p-3">
                    <div className="text-[9px] text-[#52525B] uppercase tracking-wider mb-0.5">{l}</div>
                    <div className="text-base font-semibold text-[#EDEDED]">{v}</div>
                    <div className="text-[9px] text-[#3F3F46]">{s}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="flex justify-between text-[9px] text-[#52525B] mb-1.5"><span>Workweek usage</span><span>{pctWeek.toFixed(1)}%</span></div>
                <div className="h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
                  <div className="h-full bg-[#5A90F5] rounded-full" style={{width:`${pctWeek}%`}}/>
                </div>
              </div>
              {Object.keys(byType).length>0&&(
                <div className="space-y-1.5">
                  <div className="text-[9px] text-[#52525B] uppercase tracking-wider mb-1">By type</div>
                  {Object.entries(byType).map(([type,{count,min:m}])=>(
                    <div key={type} className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{background:TYPE_COLOR[type]||"#52525B"}}/>
                      <span className="text-xs text-[#A1A1AA] capitalize flex-1">{type}</span>
                      <span className="text-[10px] text-[#52525B]">{count} node{count!==1?"s":""}</span>
                      {m>0&&<span className="text-[10px] text-[#3F3F46] ml-1">{fmtTime(m)}</span>}
                    </div>
                  ))}
                </div>
              )}
              {/* Time audit — expected vs actual for tasks with estimates */}
              {(() => {
                const auditTasks = dayEvents.filter(e => e.estimated_minutes && e.completed_at);
                if (!auditTasks.length) return null;
                const totalEst = auditTasks.reduce((s,e) => s + (e.estimated_minutes||0), 0);
                const totalAct = auditTasks.reduce((s,e) => s + dur(e), 0);
                const diff = totalAct - totalEst;
                const pct = totalEst > 0 ? Math.round((totalAct/totalEst)*100) : 100;
                return (
                  <div className="border-t border-[#1E1E1E] pt-3">
                    <div className="text-[9px] text-[#52525B] uppercase tracking-wider mb-2">Time Audit</div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-[#0A0A0A] border border-[#1E1E1E] rounded-lg p-2">
                        <div className="text-[9px] text-[#52525B] mb-0.5">Expected</div>
                        <div className="text-sm font-semibold text-[#EDEDED]">{fmtTime(totalEst)}</div>
                      </div>
                      <div className="bg-[#0A0A0A] border border-[#1E1E1E] rounded-lg p-2">
                        <div className="text-[9px] text-[#52525B] mb-0.5">Actual</div>
                        <div className="text-sm font-semibold text-[#EDEDED]">{fmtTime(totalAct)}</div>
                      </div>
                      <div className={`rounded-lg p-2 ${diff > 0 ? "bg-red-500/10 border border-red-500/20" : "bg-green-500/10 border border-green-500/20"}`}>
                        <div className="text-[9px] text-[#52525B] mb-0.5">Variance</div>
                        <div className={`text-sm font-semibold ${diff > 0 ? "text-red-400" : "text-green-400"}`}>{diff > 0 ? "+" : ""}{fmtTime(Math.abs(diff))}</div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="flex justify-between text-[9px] text-[#52525B] mb-1"><span>Actual vs estimate</span><span>{pct}%</span></div>
                      <div className="h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${pct > 110 ? "bg-red-400" : pct > 90 ? "bg-green-400" : "bg-[#5A90F5]"}`} style={{width:`${Math.min(pct,150)}%`}}/>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {dayEvents.length===0&&<p className="text-[11px] text-[#3F3F46] text-center py-1">No nodes scheduled</p>}
            </div>
          </div>
        );
      })()}

      {/* ── Today's Agenda — reference-style bottom section ── */}
      {(() => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const todayEvs = (events || [])
          .filter(e => !e.unscheduled && e.start?.slice(0, 10) === todayStr && !e.completed_at)
          .sort((a, b) => a.start.localeCompare(b.start));
        if (todayEvs.length === 0) return null;
        return (
          <div className="flex-shrink-0" style={{ borderTop: "1px solid var(--cresc-border-sub, #1E1E22)" }}>
            <div className="flex items-center gap-3 px-6 py-2.5" style={{ borderBottom: "1px solid var(--cresc-border-sub, #1E1E22)" }}>
              <span className="text-[11px] font-medium" style={{ color: "var(--cresc-text-3, #56565E)" }}>Filters</span>
              <div className="flex items-center gap-2">
                {["#F5C842","#7C6CF8","#5EAA8A","#EF4444"].map(c => (
                  <button key={c} className="w-4 h-4 rounded-full border-2 border-transparent hover:border-white/40 transition-all" style={{ background: c }} />
                ))}
              </div>
              <button className="ml-auto px-3 py-1 rounded-lg text-[11px] font-semibold" style={{ background: "var(--cresc-accent, #7C6CF8)", color: "#000" }}>+ New</button>
            </div>
            <div className="flex items-center gap-2 px-6 pt-3 pb-2">
              <span className="text-[15px] font-bold" style={{ color: "var(--cresc-text, #F0F0F0)" }}>Today's agenda</span>
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold" style={{ background: "var(--cresc-surface-3, #222226)", color: "var(--cresc-text-2, #888892)" }}>{todayEvs.length}</span>
            </div>
            <div className="flex gap-3 px-6 pb-4 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
              {todayEvs.map(ev => {
                const evColor = ev.custom_color || "#7C6CF8";
                return (
                  <div key={ev.event_id}
                    className="flex-shrink-0 flex flex-col gap-2 p-4 rounded-2xl cursor-pointer transition-all"
                    style={{ background: "var(--cresc-surface-2, #1C1C1F)", border: "1px solid var(--cresc-border, #2A2A2E)", minWidth: 200, maxWidth: 280 }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = evColor + "60"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "var(--cresc-border, #2A2A2E)"}>
                    <div className="flex items-center gap-1.5 text-[11px]" style={{ color: "var(--cresc-text-3, #56565E)" }}>
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: evColor }} />
                      {format(new Date(ev.start), "h:mm a")}
                    </div>
                    <p className="text-[13px] font-semibold" style={{ color: "var(--cresc-text, #F0F0F0)" }}>{ev.title}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}


    </div>
  );
}
